import * as React from 'react';
import React__default, { useRef, useEffect, createRef, isValidElement, useContext, useState, useMemo, useImperativeHandle, forwardRef, cloneElement, useCallback, Children } from 'react';
import CBAccordion from '@cb/apricot/CBAccordion';
import { usePrevious, useViewport } from '@cb/react-hooks';
import cn from 'classnames';
import { isUndefined, isString, isNumber, isBoolean, isNil, flatMap, startCase, max, map, unzip, each, set, compact, orderBy } from 'lodash-es';
import * as ReactDOMServer from 'react-dom/server';
import { useUID, useUIDSeed, uid } from 'react-uid';
import CBAnchorMenu from '@cb/apricot/CBAnchorMenu';
import CBBackToTop from '@cb/apricot/CBBackToTop';
import '@cb/apricot/CBButton';
import '@cb/apricot/CBIcon';
import CBBand from '@cb/apricot/CBBand';
import '@cb/apricot/CBBreadcrumb';
import '@cb/apricot/CBCard';
import '@cb/apricot/CBIllustration';
import CBModal from '@cb/apricot/CBModal';
import CBNotification from '@cb/apricot/CBNotification';
import { CSSTransition as CSSTransition$2 } from 'react-transition-group';
import CBSpinner from '@cb/apricot/CBSpinner';
import CBDatePicker from '@cb/apricot/CBDatePicker';
import CBForm from '@cb/apricot/CBForm';
import CBInputMask from '@cb/apricot/CBInputMask';
import { isElement, isReactComponent } from '@cb/react-utils';
import '@cb/apricot/CBBase';
import CBDropdown from '@cb/apricot/CBDropdown';
import CBInternationalPhone from '@cb/apricot/CBInternationalPhone';
import CBUtils from '@cb/apricot/CBUtils';
import CBEventTimeline from '@cb/apricot/CBEventTimeline';
import CBFilter from '@cb/apricot/CBFilter';
import CBHorizontalCarousel from '@cb/apricot/CBHorizontalCarousel';
import '@cb/apricot/CBLoader';
import '@cb/apricot/CBPagination';
import CBPhotoGallery from '@cb/apricot/CBPhotoGallery';
import flattenChildren from 'react-flatten-children';
import CBPopover from '@cb/apricot/CBPopover';
import CBSlider from '@cb/apricot/CBSlider';
import '@cb/apricot/CBStepper';
import CBSticky from '@cb/apricot/CBSticky';
import '@cb/apricot/CBTable';
import { useResizeDetector } from 'react-resize-detector';
import CBTab from '@cb/apricot/CBTab';
import CBToast from '@cb/apricot/CBToast';
import CBTooltip from '@cb/apricot/CBTooltip';
import '@cb/apricot/CBTreeView';

const AccordionControls = ({ title, controls }) => !(title || controls) ? null : (React__default.createElement("div", { className: "cb-accordion-controls" },
    title && (React__default.createElement("div", { className: "cb-accordion-title" },
        React__default.createElement("h3", null, title))),
    controls && (React__default.createElement("div", { className: "cb-accordion-buttons" },
        React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-greyscale cb-accordion-expand" }, "Expand All"),
        React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-greyscale cb-accordion-collapse" }, "Collapse All")))));

/* eslint-disable max-lines-per-function */
/**
 * Previous value reference
 * @param value - value to keep reference of the previous
 */
/**
 * AccordionList component
 */
const AccordionList = ({ children, title, controls, extendFocusableElements, single = false, onOpenChange, onChange, resizeOnOpen, autoUpdate = true, animation = true, heightAdjustment = 0, className, cbRef, }) => {
    const ref = useRef(null);
    const [childRefs, setChildRefs] = React__default.useState([]);
    const prevChildren = usePrevious(children); // save the previous children
    const accordion = useRef(null);
    const onPanelOpenChangeCallBack = panelEl => {
        const isOpen = panelEl.classList.contains('cb-in');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const childrenArray = React__default.Children.toArray(children);
        // if open and should be resized - force accordion resize
        if (isOpen && resizeOnOpen && accordion.current) {
            accordion.current.adjustHeight();
        }
        const index = childRefs.findIndex(child => {
            return child && child.querySelector('.cb-accordion-panel') === panelEl;
        });
        if (children && isValidElement(childrenArray[index])) {
            const childProps = childrenArray[index].props;
            if (childProps.onOpenChange) {
                childProps.onOpenChange(isOpen);
            }
            if (onOpenChange) {
                onOpenChange(index, isOpen);
            }
        }
    };
    const onChangeCallBack = panelEl => {
        const isOpen = panelEl.classList.contains('cb-in');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const childrenArray = React__default.Children.toArray(children);
        const index = childRefs.findIndex(child => {
            return child && child.querySelector('.cb-accordion-panel') === panelEl;
        });
        if (children && isValidElement(childrenArray[index])) {
            const childProps = childrenArray[index].props;
            if (childProps.onChange) {
                childProps.onChange(isOpen);
            }
            if (onChange) {
                onChange(index, isOpen);
            }
        }
    };
    /**
     * Check if the major child props changed
     *  - check the children length first
     *  - then all child props that are strings and numbers (shallow comparison)
     *    to avoid complex comparisons
     */
    const majorChildPropsChanged = () => {
        const currentChildren = React__default.Children.toArray(children);
        const previousChildren = React__default.Children.toArray(prevChildren);
        if (currentChildren.length !== previousChildren.length) {
            return true;
        }
        let isChanged = false;
        currentChildren.forEach((current, index) => {
            const previous = previousChildren[index];
            if (current && previous && isValidElement(current) && isValidElement(previous)) {
                isChanged =
                    isChanged ||
                        Object.keys(current.props).some(key => 
                        // compare only simple types here
                        (isString(current.props[key]) ||
                            isNumber(current.props[key]) ||
                            isBoolean(current.props[key])) &&
                            current.props[key] !== previous.props[key]);
            }
        });
        return isChanged;
    };
    useEffect(() => {
        if (isUndefined(prevChildren) || autoUpdate || majorChildPropsChanged()) {
            if (accordion.current) {
                accordion.current.destroy();
            }
            accordion.current = CBAccordion({
                elem: ref.current,
                multiOpen: !single,
                animation,
                heightAdjustment,
                extendFocusableElements,
                callBack: onPanelOpenChangeCallBack,
                onChange: onChangeCallBack,
            });
            // if we have an external ref object - update it
            if (cbRef) {
                // eslint-disable-next-line no-param-reassign
                cbRef.current = accordion.current;
            }
        }
    }, [children, autoUpdate]);
    /**
     * When we have children - create an array of refs
     */
    React__default.useEffect(() => {
        // add or remove refs
        setChildRefs(new Array(React__default.Children.toArray(children).length)
            .fill(undefined)
            .map((_, i) => childRefs[i] || createRef()));
    }, [children]);
    /**
     * Fill the refs array with child references
     */
    const extendedChildren = () => {
        return React__default.Children.map(children, (child, index) => {
            if (isValidElement(child)) {
                return React__default.cloneElement(child, {
                    ref: r => {
                        if (r) {
                            childRefs[index] = r;
                        }
                    },
                });
            }
            return child;
        });
    };
    return (React__default.createElement("div", { className: cn('cb-accordion', className), ref: ref, "data-cbtrack-comp": "apricot-react:accordion" },
        React__default.createElement(AccordionControls, { title: title, controls: controls }),
        React__default.createElement("div", { className: "cb-accordion-panels" }, extendedChildren())));
};

const AccordionHeader = ({ title, subtitle, headerId, classNameHeader }) => {
    const mainHeader = cn(subtitle && 'cb-font-weight-medium');
    return (React__default.createElement("a", { href: "/#", className: cn('cb-accordion-heading', classNameHeader), id: headerId, role: "button" },
        React__default.createElement("div", { className: "cb-accordion-heading-title" },
            title && typeof title === 'string' ? React__default.createElement("span", { className: mainHeader }, title) : title,
            React__default.createElement("div", { className: "cb-accordion-icon" },
                React__default.createElement("span", { className: "line top" }),
                React__default.createElement("span", { className: "line bottom" }))),
        subtitle && React__default.createElement("p", { className: "cb-accordion-heading-content" }, subtitle)));
};

const AccordionPanel = ({ children, open, panelId, classNamePanel, classNameContent, }) => (React__default.createElement("div", { className: cn('cb-accordion-panel transition', classNamePanel, open && 'cb-in'), role: "region", id: panelId },
    React__default.createElement("div", { className: cn('cb-accordion-panel-content', classNameContent) }, children)));

const AccordionItem = React__default.forwardRef(({ title, subtitle, children, open, headerId, panelId, panelOverlay, className, classNameHeader, classNamePanel, classNameContent, }, ref) => (React__default.createElement("div", { className: cn('cb-accordion-container', panelOverlay && 'cb-accordion-panel-overflow', className), ref: ref },
    React__default.createElement(AccordionHeader, { title: title, subtitle: subtitle, headerId: headerId, classNameHeader: classNameHeader }),
    React__default.createElement(AccordionPanel, { open: open, panelId: panelId, classNamePanel: classNamePanel, classNameContent: classNameContent }, children))));

const Expandable = ({ children, defaultOpen = false, extendFocusableElements, className, classNameHeader, classNamePanel, classNameContent, offScreen, ariaLabelledbyID, closedLabel = 'See More', openLabel = 'See Less', linkPosition: linkPos = 'bottom', title, triggerId, panelId, panelOverlay, heightAdjustment, onChange, }) => {
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const uid = useUID();
    const elementId = panelId || `pnl_${uid}`;
    /**
     * On Expandable state change handler
     * @param panelEl
     */
    const onChangeCallBack = panelEl => {
        if (onChange)
            onChange(panelEl.classList.contains('cb-in'));
    };
    useEffect(() => {
        CBAccordion({
            elem: triggerRef.current,
            targetElem: panelRef.current,
            triggerLabel: ReactDOMServer.renderToString(closedLabel),
            triggerLabelActive: ReactDOMServer.renderToString(openLabel),
            onChange: onChangeCallBack,
            animation: false,
            heightAdjustment,
            extendFocusableElements,
        });
    }, [linkPos]);
    const getPosition = (position) => {
        switch (position) {
            case 'top':
                return 'top';
            case 'bottom':
            default:
                return 'bottom';
        }
    };
    const triggerLink = () => {
        return (React__default.createElement("a", { href: "/#", className: cn('cb-link-blue cb-accordion-trigger', className), ref: triggerRef, id: triggerId, role: "button" },
            typeof openLabel === 'string' && typeof closedLabel === 'string' && (React__default.createElement("span", null, defaultOpen ? openLabel : closedLabel)),
            (typeof openLabel !== 'string' || typeof closedLabel !== 'string') &&
                (defaultOpen ? openLabel : closedLabel),
            React__default.createElement("div", { className: "cb-accordion-icon cb-margin-left-8" },
                React__default.createElement("span", { className: "line top" }),
                React__default.createElement("span", { className: "line bottom" }))));
    };
    return (React__default.createElement(React__default.Fragment, null,
        getPosition(linkPos) === 'top' && triggerLink(),
        React__default.createElement("div", { className: cn('cb-accordion-trigger-panel transition', panelOverlay && 'cb-accordion-panel-overflow', defaultOpen && 'cb-in', classNamePanel), "aria-label": offScreen || undefined, "aria-labelledby": ariaLabelledbyID || undefined, role: "region", ref: panelRef, id: elementId, "data-cbtrack-comp": "apricot-react:accordion" },
            title && React__default.createElement("div", { className: cn('cb-accordion-heading-title', classNameHeader) }, title),
            React__default.createElement("div", { className: cn('cb-accordion-panel-content', classNameContent) }, children)),
        getPosition(linkPos) === 'bottom' && triggerLink()));
};

const linkPosition = ['top', 'bottom'];

const ICON_RIGHT = 'right'; // most right position
const ICON_LEFT = 'left'; // most left position
const ICON_NONE = 'none'; // no icon
const iconVariants = [ICON_RIGHT, ICON_LEFT, ICON_NONE];

const defaultAccordionContext = {
    forceReindex: 0,
    accordionId: '',
    expandable: false,
    panelCount: 0,
    registerIndex: () => 0,
    isSelected: () => false,
    selectPanel: () => { },
};
const SimpleAccordionContext = React.createContext(defaultAccordionContext);
const defaultSimpleAccordionItemContext = {
    panelId: '',
    buttonId: '',
    selected: false,
    expandable: false,
    onSelect: () => { },
};
const SimpleAccordionItemContext = React.createContext(defaultSimpleAccordionItemContext);

const useAccordion = () => useContext(SimpleAccordionContext);
const useAccordionItem = () => useContext(SimpleAccordionItemContext);

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const SimpleAccordionControls = ({ title, controls }) => {
    const { panelCount, selectPanel } = useContext(SimpleAccordionContext);
    const updateAll = (open) => Array(panelCount)
        .fill(null)
        .forEach((_, index) => selectPanel(index, open));
    const onExpand = () => updateAll(true);
    const onCollapse = () => updateAll(false);
    return !(title || controls) ? null : (React__default.createElement("div", { className: "cb-accordion-controls" },
        title && (React__default.createElement("div", { className: "cb-accordion-title" },
            React__default.createElement("h3", null, title))),
        controls && (React__default.createElement("div", { className: "cb-accordion-buttons" },
            React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-naked cb-accordion-expand", onClick: onExpand }, "Expand All"),
            React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-naked cb-accordion-collapse", onClick: onCollapse }, "Collapse All")))));
};

function extendEvent(handler, localHandler) {
    return (event) => {
        if (handler)
            handler(event);
        if (!event.defaultPrevented) {
            return localHandler(event);
        }
        return null;
    };
}
/**
 * Simple Accordion
 */
const SimpleAccordion = React.forwardRef((_a, ref) => {
    var { children, onItemChange, className, index, id, single = true, expandable = false, shouldReindexChildren, title, controls, onSelectPanel } = _a, props = __rest(_a, ["children", "onItemChange", "className", "index", "id", "single", "expandable", "shouldReindexChildren", "title", "controls", "onSelectPanel"]);
    const [selectedIndex, setSelectedIndex] = React.useState({});
    const [forceReindex, setForceReindex] = useState(0);
    const uid = useUID();
    const accordionId = id || `sa_${uid}`;
    const currentIndex = useRef(-1);
    const previousChildren = usePrevious(children) || [];
    useEffect(() => {
        if (shouldReindexChildren &&
            shouldReindexChildren(React.Children.toArray(previousChildren), React.Children.toArray(children))) {
            currentIndex.current = -1;
            setForceReindex(new Date().getTime());
        }
    }, [children]);
    // Revert forceReindex once handled
    useEffect(() => {
        if (forceReindex) {
            setForceReindex(0);
        }
    }, [forceReindex]);
    const selectPanel = (ind, open) => {
        setSelectedIndex(previous => {
            // No index passed, return the current state
            if (ind === undefined || ind < 0) {
                return single ? {} : previous;
            }
            const openFlag = open === undefined ? !previous[ind] : open;
            // If onSelectPanel is provided, let it control whether to proceed with default behavior
            if (onSelectPanel) {
                const shouldProceed = onSelectPanel(ind, openFlag, previous);
                if (!shouldProceed) {
                    return previous; // Return previous state without changes
                }
            }
            // Call the onItemChange callback if provided
            if (onItemChange)
                onItemChange(ind, openFlag);
            // index was given - extend the selection object
            return Object.assign(Object.assign({}, (single && openFlag ? {} : previous)), { [ind]: openFlag });
        });
    };
    const registerIndex = (refObject) => {
        // Panel default ref value is -1
        if (refObject.current === -1) {
            // eslint-disable-next-line no-plusplus,no-param-reassign
            refObject.current = ++currentIndex.current;
        }
        return refObject.current;
    };
    const isSelected = (ind) => {
        return selectedIndex[ind];
    };
    const context = useMemo(() => ({
        accordionId,
        expandable,
        registerIndex,
        isSelected,
        selectPanel,
        forceReindex,
        panelCount: currentIndex.current + 1,
    }), [accordionId, selectedIndex, isSelected]);
    useEffect(() => {
        selectPanel(index, true);
    }, [index, single]);
    return (React.createElement(SimpleAccordionContext.Provider, { value: context },
        React.createElement("div", Object.assign({ className: cn('cb-accordion cb-accordion-simple', expandable && 'cb-accordion-expandable', className), ref: ref, "data-cbtrack-comp": "apricot-react:accordion" }, props),
            (title || controls) && React.createElement(SimpleAccordionControls, { title: title, controls: !single && controls }),
            React.createElement("div", { className: "cb-accordion-panels" }, children))));
});
SimpleAccordion.displayName = 'SimpleAccordion';
/**
 * Accordion Item
 */
const SimpleAccordionItem = React.forwardRef((_a, ref) => {
    var { children, className, panelOverlay, open } = _a, props = __rest(_a, ["children", "className", "panelOverlay", "open"]);
    const { selectPanel, isSelected, accordionId, expandable, registerIndex, forceReindex } = React.useContext(SimpleAccordionContext);
    const indexRef = React.useRef(-1);
    const index = registerIndex(indexRef);
    const selected = isSelected(index);
    const context = useMemo(() => ({
        panelId: `accordion-${accordionId}-panel-${index}`,
        buttonId: `accordion-${accordionId}-button-${index}`,
        selected,
        expandable,
        onSelect: () => {
            selectPanel(index);
        },
    }), [accordionId, index, selected]);
    useEffect(() => {
        if (forceReindex) {
            indexRef.current = -1;
        }
    }, [forceReindex]);
    useEffect(() => {
        selectPanel(index, !!open);
    }, [index, open]);
    return (React.createElement(SimpleAccordionItemContext.Provider, { value: context },
        React.createElement("div", Object.assign({ className: cn('cb-accordion-container', panelOverlay && 'cb-accordion-panel-overflow', className) }, props, { ref: ref }), children)));
});
SimpleAccordionItem.displayName = 'SimpleAccordionItem';
/**
 * Accordion Icon
 */
const SimpleAccordionIcon = ({ disabled, disabledLabel, iconPosition }) => {
    return (React.createElement("div", { role: disabled ? 'img' : undefined, "aria-label": disabled && disabledLabel ? disabledLabel : undefined, className: cn('cb-accordion-icon', iconPosition === ICON_LEFT && 'cb-margin-right-8', iconPosition === ICON_RIGHT && 'cb-margin-left-8') },
        React.createElement("span", { className: "line top" }),
        React.createElement("span", { className: "line bottom" })));
};
/**
 * Accordion Button
 */
const SimpleAccordionHeader = React.forwardRef((_a, ref) => {
    var { children, onClick, className, disabled = false, inTabOrder = false, disabledLabel = 'locked', iconPosition = ICON_RIGHT } = _a, props = __rest(_a, ["children", "onClick", "className", "disabled", "inTabOrder", "disabledLabel", "iconPosition"]);
    const { panelId, buttonId, selected, onSelect } = React.useContext(SimpleAccordionItemContext);
    // accordion should open with enter and space
    const a11YKeydown = (e) => {
        const k = e.key;
        // space or enter
        if (k === ' ' || k === 'Enter') {
            e.preventDefault();
            onSelect();
        }
    };
    return (React.createElement("a", Object.assign({ tabIndex: disabled && !inTabOrder ? -1 : 0, role: "button", className: cn('cb-accordion-heading', selected && 'cb-active', disabled && 'cb-disabled', iconPosition === ICON_LEFT && 'cb-accordion-left-icon', className) }, props, { onClick: !disabled ? extendEvent(onClick, onSelect) : undefined, onKeyDown: !disabled ? a11YKeydown : undefined, "aria-expanded": selected || false, "aria-controls": panelId, "aria-disabled": disabled ? true : undefined, id: buttonId, ref: ref }),
        React.createElement("div", { className: "cb-accordion-heading-title" },
            iconPosition === ICON_LEFT && (React.createElement(SimpleAccordionIcon, { disabled: disabled, disabledLabel: disabledLabel })),
            children,
            iconPosition === ICON_RIGHT && (React.createElement(SimpleAccordionIcon, { disabled: disabled, disabledLabel: disabledLabel })))));
});
SimpleAccordionHeader.displayName = 'SimpleAccordionHeader';
/**
 * Accordion Trigger - Expandable
 */
const SimpleAccordionTrigger = React.forwardRef((_a, ref) => {
    var { defaultOpen = false, onClick, className, disabled = false, inTabOrder = false, disabledLabel = 'locked', closedLabel = 'See More', openLabel = 'See Less', iconPosition = ICON_RIGHT } = _a, props = __rest(_a, ["defaultOpen", "onClick", "className", "disabled", "inTabOrder", "disabledLabel", "closedLabel", "openLabel", "iconPosition"]);
    const { panelId, buttonId, selected, onSelect } = React.useContext(SimpleAccordionItemContext);
    const [label, setLabel] = useState(defaultOpen ? openLabel : closedLabel);
    // accordion should open with enter and space
    const a11YKeydown = (e) => {
        const k = e.key;
        // space or enter
        if (k === ' ' || k === 'Enter') {
            e.preventDefault();
            onSelect();
        }
    };
    useEffect(() => {
        setLabel(selected ? openLabel : closedLabel);
    }, [selected, openLabel]);
    return (React.createElement("a", Object.assign({ tabIndex: disabled && !inTabOrder ? -1 : 0, role: "button", className: cn('cb-accordion-trigger', 'cb-link-blue', selected && 'cb-active', disabled && 'cb-disabled', iconPosition === ICON_LEFT && 'cb-accordion-left-icon', className) }, props, { onClick: !disabled ? extendEvent(onClick, onSelect) : undefined, onKeyDown: !disabled ? a11YKeydown : undefined, "aria-expanded": selected || false, "aria-controls": panelId, "aria-disabled": disabled ? true : undefined, id: buttonId, ref: ref }),
        iconPosition === ICON_LEFT && (React.createElement(SimpleAccordionIcon, { disabled: disabled, disabledLabel: disabledLabel, iconPosition: iconPosition })),
        label,
        iconPosition === ICON_RIGHT && (React.createElement(SimpleAccordionIcon, { disabled: disabled, disabledLabel: disabledLabel, iconPosition: iconPosition }))));
});
SimpleAccordionTrigger.displayName = 'SimpleAccordionTrigger';
/**
 * Accordion Panel
 */
const SimpleAccordionPanel = React.forwardRef((_a, ref) => {
    var { children, className, offScreen, ariaLabelledbyID, classNameContent } = _a, props = __rest(_a, ["children", "className", "offScreen", "ariaLabelledbyID", "classNameContent"]);
    const { buttonId, panelId, selected, expandable } = React.useContext(SimpleAccordionItemContext);
    const divRef = useRef(null);
    useEffect(() => {
        var _a;
        if (expandable && selected) {
            if (divRef)
                (_a = divRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [selected, expandable]);
    useImperativeHandle(ref, () => divRef.current);
    return (React.createElement("div", Object.assign({ role: "region", className: cn('cb-accordion-panel', selected && 'cb-in', className) }, props, { id: panelId, "aria-labelledby": !offScreen ? ariaLabelledbyID || buttonId : undefined, "aria-label": offScreen || undefined, "aria-hidden": !selected, hidden: !selected, tabIndex: expandable ? -1 : undefined, ref: divRef }),
        React.createElement("div", { className: cn('cb-accordion-panel-content', classNameContent) }, children)));
});
SimpleAccordionPanel.displayName = 'SimpleAccordionPanel';

const AnchorMenu = ({ anchorId, anchorMenuId, children, selector = 'h2', offsetTop = 120, close = true, bookmark = true, btnLabel = null, anchorLabel = 'page navigation', items, }) => {
    const menuRef = useRef(null);
    const contentRef = useRef(null);
    const anchorPlugin = useRef(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    anchorMenuId = anchorMenuId || `apricot_anchor_${uid}`;
    useEffect(() => {
        const elem = menuRef.current;
        if (anchorPlugin.current) {
            anchorPlugin.current.destroy();
        }
        anchorPlugin.current = CBAnchorMenu({
            elem,
            markup: !items,
            container: contentRef.current,
            selector,
            offsetTop,
            close,
            bookmark,
        });
        return () => {
            if (anchorPlugin) {
                anchorPlugin.current.destroy();
            }
        };
    }, [offsetTop, close, bookmark, items, btnLabel]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: "cb-anchor-menu", "data-cbtrack-comp": "apricot-react:anchor-menu", ref: menuRef },
            btnLabel ? (React__default.createElement("a", { role: "button", href: "/#", className: "cb-btn cb-btn-black cb-btn-floating cb-anchor-menu-btn", "aria-expanded": "false", "aria-controls": anchorMenuId, id: anchorId }, btnLabel)) : (React__default.createElement("div", { className: "cb-btn cb-btn-square cb-btn-floating cb-anchor-menu-btn", role: "button", "aria-label": anchorLabel, "aria-expanded": "false", "aria-controls": anchorMenuId, id: anchorId, tabIndex: 0 },
                React__default.createElement("span", { className: "cb-menu-icon", "aria-hidden": "true" },
                    React__default.createElement("span", null),
                    React__default.createElement("span", null),
                    React__default.createElement("span", null),
                    React__default.createElement("span", null)))),
            React__default.createElement("nav", { className: "cb-menu-list cb-menu-list-vertical", "aria-label": anchorLabel, id: anchorMenuId, "aria-hidden": true }, items ? (React__default.createElement("ul", null, items.map((item, index) => (React__default.createElement("li", { key: `l${index}` },
                React__default.createElement("a", { href: `#${item.bookmark}`, tabIndex: -1, className: "cb-menu-link" }, item.label)))))) : null)),
        React__default.createElement("div", { className: "cb-anchor-container", ref: contentRef }, children)));
};

/**
 * Button variations
 */
const buttonVariants = [
    'primary',
    'naked',
    'tag',
    'circle',
    'square',
    'black',
    'yellow',
    'card-primary',
    'card-secondary',
];

// Note: Removed PrimaryCardButton and SecondaryCardButton
const LoadingDots = () => (React__default.createElement("div", { className: "cb-btn-pulse-dots" },
    React__default.createElement("div", { className: "cb-btn-dots" })));
const BaseButton = React__default.forwardRef((_a, ref) => {
    var { children, icon, iconLeft, iconDecorative = true, legacy = false, variant, disabled, small, large, className, withAriaLive, loading, light, hoverShade, floating, greyscale, reverse, noPadding, noOutline } = _a, props = __rest(_a, ["children", "icon", "iconLeft", "iconDecorative", "legacy", "variant", "disabled", "small", "large", "className", "withAriaLive", "loading", "light", "hoverShade", "floating", "greyscale", "reverse", "noPadding", "noOutline"]);
    const btnClassName = cn('cb-btn', variant && `cb-btn-${variant}`, light && 'cb-btn-light', small && 'cb-btn-sm', floating && 'cb-btn-floating', greyscale && 'cb-btn-greyscale', className, loading && 'cb-btn-pulse', noPadding && 'cb-no-padding', noOutline && 'cb-no-outline', hoverShade && 'cb-hover-shade', reverse && 'cb-btn-reverse', icon && !legacy && 'cb-no-text-decoration-icon', large && (!variant || ['primary', 'black'].includes(variant)) && 'cb-btn-lg');
    const iconOnly = variant === 'circle' || variant === 'square';
    const iconClassName = cn(icon && 'cb-icon', `cb-${icon}`, iconLeft && 'cb-no-margin-left cb-margin-right-8');
    return (React__default.createElement("button", Object.assign({ type: "button", className: btnClassName, disabled: disabled, "data-cbtrack-comp": "apricot-react:button" }, props, { ref: ref }),
        icon && iconLeft && React__default.createElement("span", { className: iconClassName, "aria-hidden": iconDecorative }),
        !icon && !iconOnly && !loading && children,
        icon && !iconOnly && !loading && legacy && children,
        icon && !iconOnly && !loading && !legacy && React__default.createElement("span", null, children),
        loading && React__default.createElement("span", { "aria-live": withAriaLive ? 'polite' : undefined }, "Loading"),
        icon && !iconLeft && React__default.createElement("span", { className: iconClassName, "aria-hidden": iconDecorative }),
        iconOnly && !loading && React__default.createElement("span", { className: "sr-only" }, children),
        loading && React__default.createElement(LoadingDots, null)));
});
const LinkTagButton = React__default.forwardRef((_a, ref) => {
    var { children, icon, offScreen, iconLeft, iconDecorative = false, variant, disabled, small, className, legacy = false } = _a, props = __rest(_a, ["children", "icon", "offScreen", "iconLeft", "iconDecorative", "variant", "disabled", "small", "className", "legacy"]);
    const btnRef = useRef(null);
    const btnClassName = cn('cb-btn', variant && `cb-btn-${variant}`, disabled && 'cb-disabled', small && 'cb-btn-sm', icon && !legacy && 'cb-no-text-decoration-icon', className);
    const iconClassName = cn(icon && 'cb-icon', `cb-${icon}`, iconLeft && 'cb-no-margin-left cb-margin-right-8');
    const a11YKeydown = (e, node) => {
        const k = e.key;
        // space or enter
        if (k === ' ' || k === 'Enter') {
            e.preventDefault();
            node === null || node === void 0 ? void 0 : node.current.click();
        }
    };
    return (React__default.createElement("a", Object.assign({ href: "/#", role: "button", className: btnClassName, "aria-disabled": disabled, "data-cbtrack-comp": "apricot-react:button" }, props, { ref: ref || btnRef, onKeyDown: e => a11YKeydown(e, ref || btnRef) }),
        icon && iconLeft && (React__default.createElement("span", { className: iconClassName, "aria-hidden": iconDecorative }, offScreen && React__default.createElement("span", { className: "sr-only" }, offScreen))),
        !icon && children,
        icon && legacy && children,
        icon && !legacy && React__default.createElement("span", null, children),
        icon && !iconLeft && (React__default.createElement("span", { className: iconClassName, "aria-hidden": iconDecorative }, offScreen && React__default.createElement("span", { className: "sr-only" }, offScreen)))));
});
const CircularButton = React__default.forwardRef((_a, ref) => {
    var { label } = _a, props = __rest(_a, ["label"]);
    return (React__default.createElement(BaseButton, Object.assign({ ref: ref, variant: "circle" }, props), label));
});
const SquareButton = React__default.forwardRef((_a, ref) => {
    var { label } = _a, props = __rest(_a, ["label"]);
    return (React__default.createElement(BaseButton, Object.assign({ ref: ref, variant: "square" }, props), label));
});
const CloseButton = React__default.forwardRef((_a, ref) => {
    var { small, label = 'Close', greyscale = true, className } = _a, props = __rest(_a, ["small", "label", "greyscale", "className"]);
    return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    React__default.createElement(SquareButton, Object.assign({ greyscale: greyscale, icon: "x-mark", label: label, className: cn('cb-btn-close', className), small: small, ref: ref }, props)));
});
const NakedButton = React__default.forwardRef((props, ref) => (React__default.createElement(BaseButton, Object.assign({ variant: "naked", ref: ref }, props))));
const Tag = (_a) => {
    var { offScreen } = _a, props = __rest(_a, ["offScreen"]);
    return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    React__default.createElement(React__default.Fragment, null, props.icon ? (React__default.createElement(LinkTagButton, Object.assign({ variant: "tag", offScreen: offScreen }, props))) : (React__default.createElement(BaseButton, Object.assign({ variant: "tag" }, props)))));
};
const Button = React__default.forwardRef((props, ref) => (React__default.createElement(BaseButton, Object.assign({ ref: ref }, props))));
const PrimaryButton = React__default.forwardRef((props, ref) => (React__default.createElement(BaseButton, Object.assign({ variant: "primary", ref: ref }, props))));
const YellowButton = React__default.forwardRef((props, ref) => (React__default.createElement(BaseButton, Object.assign({ variant: "yellow", ref: ref }, props))));
const SecondaryButton = React__default.forwardRef((props, ref) => (React__default.createElement(BaseButton, Object.assign({ variant: "black", ref: ref }, props))));
const BlackButton = React__default.forwardRef((props, ref) => (React__default.createElement(BaseButton, Object.assign({ variant: "black", ref: ref }, props))));
const ButtonRow = ({ small, align, children }) => (React__default.createElement("div", { className: cn('cb-btn-row', small && 'cb-btn-row-sm', align && `display-flex justify-content-${align}`) }, children));

const TagSet = ({ tags, onClick, tagIcon, label, icon = 'x-mark', legacy = false, offScreen, small, withRegion = true, disabled, }) => {
    const tagClassName = cn(tagIcon && 'cb-icon', `cb-${tagIcon}`);
    return (React__default.createElement("div", { className: cn('cb-btn-row cb-tag-set', small && 'cb-btn-row-sm'), role: withRegion ? 'region' : undefined, "aria-label": withRegion ? label : undefined },
        tagIcon && React__default.createElement("span", { className: tagClassName, "aria-hidden": true }),
        tags.map((tag, key) => (React__default.createElement(Tag, { disabled: disabled, icon: icon, legacy: legacy, key: key, small: small, offScreen: offScreen, onClick: (e) => onClick && onClick(tag, e) }, tag)))));
};

const BackToTop = ({ buttonId, ariaLabel = 'back to top', topOfPageAnchorId, offsetTop = 72, animation = true, animationTime = 500, }) => {
    const btnRef = useRef(null);
    useEffect(() => {
        const bttPlugin = CBBackToTop({
            elem: btnRef.current,
            topOfPageAnchor: document.querySelector(`#${topOfPageAnchorId}`),
            offsetTop,
            animation,
            animationTime,
        });
        return () => {
            if (bttPlugin) {
                bttPlugin.destroy();
            }
        };
    }, []);
    return React__default.createElement(SquareButton, { ref: btnRef, label: ariaLabel, floating: true, icon: "up", className: "cb-back-to-top", id: buttonId });
};

const ArticleBand = ({ heading, title, description, className, minHeight, children }) => {
    const heroClassName = cn('cb-band', 'cb-band-hero', 'cb-band-hero-article', className);
    const vPort = useViewport();
    return (React__default.createElement("div", { className: heroClassName, "data-cbtrack-comp": "apricot-react:hero" },
        React__default.createElement("div", { className: "container" },
            React__default.createElement("div", { className: cn('row', 'cb-band-hero-height-auto'), style: minHeight && vPort !== 'xs' ? { minHeight: `${minHeight}px` } : undefined },
                React__default.createElement("div", { className: "col-xs-12 col-md-10 offset-md-1 col-xl-8 offset-xl-2" },
                    React__default.createElement("div", { className: "cb-band-hero-content" },
                        heading && React__default.createElement("p", { className: "cb-text-overline" }, heading),
                        title && React__default.createElement("h1", { className: "cb-band-hero-title" }, title),
                        description && React__default.createElement("p", { className: "cb-band-hero-desc" }, description),
                        children && React__default.createElement("div", { className: "cb-btn-row" }, children)))))));
};
const LeftTextBand = ({ heading, title, description, image = null, condensed = false, className, autoHeight, minHeight, children, }) => {
    const heroClassName = cn('cb-band', 'cb-band-hero', className, image && 'cb-band-hero-bg', condensed && 'cb-band-hero-condensed');
    const vPort = useViewport();
    return (React__default.createElement("div", { className: heroClassName, style: image ? { backgroundImage: `url(${image})` } : undefined, "data-cbtrack-comp": "apricot-react:hero" },
        React__default.createElement("div", { className: "container" },
            React__default.createElement("div", { className: cn('row', autoHeight && 'cb-band-hero-height-auto'), style: minHeight && vPort !== 'xs' ? { minHeight: `${minHeight}px` } : undefined },
                React__default.createElement("div", { className: "col-xs-12 col-sm-8 col-md-6 offset-md-1 offset-3xl-2" },
                    React__default.createElement("div", { className: "cb-band-hero-content" },
                        !condensed && heading && React__default.createElement("p", { className: "cb-text-overline" }, heading),
                        title && React__default.createElement("h1", { className: "cb-band-hero-title" }, title),
                        description && React__default.createElement("p", { className: "cb-band-hero-desc" }, description),
                        children && React__default.createElement("div", { className: "cb-btn-row" }, children)))))));
};
const CenterTextBand = ({ heading, title, description, condensed = false, image, className, autoHeight, minHeight, children, }) => {
    const heroClassName = cn('cb-band', 'cb-band-hero', 'cb-align-center', className, image && 'cb-band-hero-bg', condensed && 'cb-band-hero-condensed');
    const vPort = useViewport();
    return (React__default.createElement("div", { className: heroClassName, style: image ? { backgroundImage: `url(${image})` } : undefined, "data-cbtrack-comp": "apricot-react:hero" },
        React__default.createElement("div", { className: "container" },
            React__default.createElement("div", { className: cn('row', autoHeight && 'cb-band-hero-height-auto'), style: minHeight && vPort !== 'xs' ? { minHeight: `${minHeight}px` } : undefined },
                React__default.createElement("div", { className: "col-xs-12 col-sm-8 offset-sm-2 col-xl-6 offset-xl-3" },
                    React__default.createElement("div", { className: "cb-band-hero-content cb-white-color" },
                        !condensed && heading && React__default.createElement("p", { className: "cb-text-overline" }, heading),
                        title && React__default.createElement("h1", { className: "cb-band-hero-title" }, title),
                        description && React__default.createElement("p", { className: "cb-band-hero-desc" }, description),
                        children && React__default.createElement("div", { className: "cb-btn-row" }, children)))))));
};
const FocusedBand = ({ heading, title, description, image = null, imageClass, imageContainerClass, className, autoHeight, minHeight, children, }) => {
    const heroClassName = cn('cb-band', 'cb-band-hero', 'cb-band-hero-focus', 'cb-band-hero-xs-stack', className);
    const vPort = useViewport();
    return (React__default.createElement("div", { className: heroClassName, "data-cbtrack-comp": "apricot-react:hero" },
        React__default.createElement("div", { className: "container" },
            React__default.createElement("div", { className: cn('row', autoHeight && 'cb-band-hero-height-auto'), style: minHeight && vPort !== 'xs' ? { minHeight: `${minHeight}px` } : undefined },
                React__default.createElement("div", { className: cn('col-xs-12 col-sm-5 col-md-6 col-2xl-6 offset-2xl-0 col-3xl-5 offset-3xl-1 cb-align-center cb-align-sm-right', imageContainerClass) }, image && React__default.createElement("img", { src: image, alt: "", className: cn('cb-img-fluid', imageClass) })),
                React__default.createElement("div", { className: "col-xs-12 col-sm-7 col-md-6 cb-band-hero-content cb-align-left" },
                    heading && React__default.createElement("p", { className: "cb-text-overline" }, heading),
                    title && React__default.createElement("h1", { className: "cb-band-hero-title" }, title),
                    description && React__default.createElement("p", { className: "cb-band-hero-desc" }, description),
                    children && React__default.createElement("div", { className: "cb-btn-row" }, children))))));
};
const CardBand = ({ heading, title, description, className, polygon = true, tintClass, autoHeight, minHeight, children, }) => {
    const bandRef = useRef(null);
    useEffect(() => {
        let heroPolygonInst;
        if (polygon) {
            heroPolygonInst = CBBand.HeroPolygon({
                elem: bandRef.current,
            });
        }
        return () => {
            if (bandRef.current && heroPolygonInst) {
                heroPolygonInst.destroy();
            }
        };
    }, []);
    const heroClassName = cn('cb-band', 'cb-band-hero', 'cb-band-hero-card', className);
    const vPort = useViewport();
    return (React__default.createElement("div", { className: heroClassName, ref: bandRef, "data-cbtrack-comp": "apricot-react:hero" },
        polygon && React__default.createElement("div", { className: `cb-band-hero-card-polygon ${tintClass}` }),
        React__default.createElement("div", { className: "container" },
            React__default.createElement("div", { className: cn('row', autoHeight && 'cb-band-hero-height-auto'), style: minHeight && vPort !== 'xs' ? { minHeight: `${minHeight}px` } : undefined },
                React__default.createElement("div", { className: "col-xs-10 offset-xs-1 col-md-7 offset-md-0" },
                    React__default.createElement("div", { className: "cb-band-hero-content cb-white-color" },
                        heading && React__default.createElement("p", { className: "cb-text-overline" }, heading),
                        title && React__default.createElement("h1", { className: "cb-band-hero-title" }, title),
                        description && React__default.createElement("p", { className: "cb-band-hero-desc" }, description))),
                React__default.createElement("div", { className: "col-xs-10 offset-xs-1 col-md-5 offset-md-0" }, children)))));
};

const Crumb = ({ title, url = '/#', onClick, buttonId }) => {
    const onClickHandler = e => {
        if (url === '/#') {
            e.preventDefault();
        }
        if (onClick)
            onClick(e);
    };
    return (React__default.createElement("li", { key: title },
        React__default.createElement("a", { href: url, id: buttonId, onClick: onClickHandler }, title)));
};
const Breadcrumbs = ({ path, pageTitle, className, offScreen = 'Active Page:', children, }) => {
    return (React__default.createElement("nav", { "aria-label": "breadcrumbs", "data-cbtrack-comp": "apricot-react:breadcrumbs" },
        React__default.createElement("ol", { className: cn('cb-breadcrumb', className) },
            path &&
                path.map(item => (React__default.createElement("li", { key: item.title },
                    React__default.createElement("a", { href: item.url, id: item.buttonId }, item.title)))),
            children,
            React__default.createElement("li", null,
                offScreen && React__default.createElement("span", { className: "sr-only" }, offScreen),
                React__default.createElement("span", null, pageTitle)))));
};

const CardWrapper = ({ eventYear, featured, repeatYear, cardRole, ariaLabel, ariaLabelledbyID, className, disabled, children, }) => {
    return (React__default.createElement(React__default.Fragment, null,
        eventYear && (React__default.createElement("li", { className: cn('cb-card', 'cb-card-event', !repeatYear ? 'cb-event-year-stamp' : null, className), "data-cbtrack-comp": "apricot-react:card" }, children)),
        !eventYear && (React__default.createElement("div", { className: cn('cb-card', 'cb-card-event', featured && 'cb-card-media', disabled && 'cb-disabled', className), "data-cbtrack-comp": "apricot-react:card", role: cardRole || undefined, "aria-label": ariaLabel || undefined, "aria-labelledby": ariaLabelledbyID || undefined }, children))));
};
const EventCard = ({ dateTimeId, titleId, descId, overlineId, cardRole, ariaLabel, ariaLabelledbyID, dateTime, cardLabel, title, titleHeadingLevel = 3, description, primary, secondary, featured, cardImage, overline, eventYear, yearHeadingLevel = 2, repeatYear, className, titleClassName, descriptionClassName, disabled, }) => {
    const seed = useUIDSeed();
    const dateTimeIdCB = dateTimeId || `apricot_${seed('date')}`;
    const titleIdCB = titleId || `apricot_${seed('title')}`;
    const descIdCB = description ? descId || `apricot_${seed('desc')}` : undefined;
    const overlineIdCB = overline ? overlineId || `apricot_${seed('overline')}` : undefined;
    return (React__default.createElement(CardWrapper, { repeatYear: repeatYear, eventYear: eventYear, featured: featured, className: className, cardRole: cardRole, ariaLabel: ariaLabel, ariaLabelledbyID: ariaLabelledbyID, disabled: disabled },
        React__default.createElement(React__default.Fragment, null,
            eventYear && (React__default.createElement("p", { role: "heading", "aria-level": yearHeadingLevel, className: cn('cb-event-year', repeatYear && 'cb-hidden') }, eventYear)),
            React__default.createElement("div", { className: "cb-card-header" }, featured ? (overline && (React__default.createElement("p", { className: "cb-text-overline-small", id: overlineIdCB }, overline))) : (React__default.createElement("div", { className: "cb-date-time-tags", id: dateTimeIdCB }, dateTime && dateTime.map((dt, index) => React__default.createElement("p", { key: `dateTime${index}` }, dt))))),
            featured && (React__default.createElement("div", { className: "cb-card-media-content" },
                React__default.createElement("div", { className: "cb-card-media-img", style: { backgroundImage: `url(${cardImage})` } }))),
            React__default.createElement("div", { className: "cb-card-content" },
                typeof title === 'string' ? (React__default.createElement("p", { role: "heading", "aria-level": titleHeadingLevel, className: cn('cb-card-title', titleClassName), id: titleIdCB }, title)) : (React__default.createElement("div", { className: cn('cb-card-title', titleClassName), id: titleIdCB }, title)),
                !featured &&
                    description &&
                    (typeof description === 'string' ? (React__default.createElement("p", { className: cn('cb-card-desc', descriptionClassName), id: descIdCB }, description)) : (React__default.createElement("div", { className: "cb-card-desc", id: descIdCB }, description))),
                cardLabel && React__default.createElement("span", { className: "cb-card-label" }, cardLabel),
                React__default.createElement("div", { className: "cb-card-action" },
                    React__default.createElement("div", { className: "cb-btn-row" }, secondary &&
                        secondary.map((btn, index) => (React__default.createElement("a", { id: btn.buttonId, key: `cardBtn${index}`, href: btn.href, onClick: e => {
                                if (disabled) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            }, tabIndex: disabled ? -1 : undefined, "aria-disabled": disabled ? true : undefined, className: cn('cb-btn cb-btn-naked cb-btn-black cb-btn-reverse cb-no-padding cb-btn-sm', disabled && 'cb-disabled'), "aria-describedby": `${dateTimeIdCB} ${titleIdCB} ${descIdCB}`, target: btn.target }, btn.label)))),
                    primary && (React__default.createElement("a", { id: primary.buttonId, href: primary.href, onClick: e => {
                            if (disabled) {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                        }, tabIndex: disabled ? -1 : undefined, "aria-disabled": disabled ? true : undefined, className: cn('cb-btn cb-btn-black cb-btn-sm', disabled && 'cb-disabled'), "aria-describedby": `${dateTimeIdCB} ${titleIdCB} ${descIdCB}`, target: primary.target }, primary.label)))))));
};

const iconSizes = ['12', '14', '16', '24', '32'];
const circularIconSizes = ['24', '28', '32', '48', '64'];
const numericIconSizes = ['24', '28', '32', '48', '64'];

const Icon = (_a) => {
    var { iconId, name, size, circleSize, color, circular = false, className, decorative = false, focusable = false } = _a, props = __rest(_a, ["iconId", "name", "size", "circleSize", "color", "circular", "className", "decorative", "focusable"]);
    return (React__default.createElement("i", Object.assign({ id: iconId, className: cn(className, 'cb-icon', circular && 'cb-icon-circular', `cb-${name}`, !circular && size && `cb-icon-${size}`, circular && circleSize && `cb-icon-${circleSize}`, color && `cb-${color}-color`), "aria-hidden": decorative, "data-cbtrack-comp": "apricot-react:icon", tabIndex: focusable ? 0 : undefined }, props)));
};
const NumericIcon = (_a) => {
    var { iconId, size, children, color, inverted = false, className, decorative = false, focusable = false, srOnly = false } = _a, props = __rest(_a, ["iconId", "size", "children", "color", "inverted", "className", "decorative", "focusable", "srOnly"]);
    return (React__default.createElement("span", Object.assign({ id: iconId, className: cn('cb-icon-numeric', size && `cb-icon-${size}`, color && `cb-${color}-color`, inverted && 'cb-icon-circular', className), "data-cb-number": children, "aria-hidden": decorative, tabIndex: focusable ? 0 : undefined }, props), srOnly && React__default.createElement("span", { className: "sr-only" }, children)));
};
const AvatarIcon = (_a) => {
    var { iconId, name, className, decorative = false, focusable = false } = _a, props = __rest(_a, ["iconId", "name", "className", "decorative", "focusable"]);
    return (React__default.createElement("i", Object.assign({ id: iconId, className: cn('cb-icon cb-icon-avatar', `cb-${name}`, className), "aria-hidden": decorative, tabIndex: focusable ? 0 : undefined }, props)));
};

/**
 * Icon Color
 */
const iconColors = [
    'black1',
    'blue1',
    'blue2',
    'blue3',
    'blue4',
    'blue5',
    'gray1',
    'gray2',
    'gray3',
    'gray4',
    'gray5',
    'green3',
    'orange1',
    'caution',
    'purple1',
    'purple2',
    'red1',
    'yellow1',
    'white',
];

/**
 * Icon Names
 */
const iconNames = [
    'accessibility',
    'acorn',
    'apple',
    'ap',
    'archive',
    'attachment',
    'award-medal',
    'bell',
    'bold',
    'book',
    'book-open',
    'bookmark',
    'box',
    'box-fill',
    'briefcase',
    'bullet-list',
    'calendar-add',
    'calendar-save-the-date',
    'cal-add',
    'cal-save-the-date',
    'cal-empty',
    'cal-error',
    'cal-full',
    'calculator',
    'cart',
    'caution',
    'caution-fill',
    'center-align',
    'chat',
    'check',
    'checkbox',
    'check-circle',
    'check-fill',
    'checklist',
    'circle',
    'clipboard',
    'clipboard-check',
    'coin',
    'code',
    'compose',
    'compose-cell',
    'compose-pen',
    'computer',
    'computer-list',
    'copy',
    'copyright',
    'cut',
    'disc',
    'disc-alt',
    'document-award',
    'document-check',
    'document-share',
    'dollar',
    'down',
    'download',
    'download-alt',
    'edit',
    'east',
    'error',
    'exclamation',
    'exclamation-circle',
    'exclamation-fill',
    'exit-full-screen',
    'expand-window',
    'explore',
    'face-bad',
    'face-excellent',
    'face-good',
    'face-neutral',
    'face-very-bad',
    'facebook',
    'feedback',
    'filter',
    'financial-aid',
    'folder',
    'full-screen',
    'globe',
    'graduate',
    'graph',
    'heart',
    'heart-fill',
    'home',
    'horz-grab-handle',
    'hyperlink',
    'image',
    'image-submit',
    'indent',
    'info-circle',
    'info-page',
    'instagram',
    'italic',
    'khan-academy',
    'laptop',
    'laptop-check',
    'laptop-exclamation',
    'layout-50-50',
    'layout-left-pane',
    'layout-right-pane',
    'left',
    'left-align',
    'left-double',
    'lightbulb',
    'linkedin',
    'list',
    'list-edit',
    'location',
    'location-pin',
    'location-tag',
    'lock',
    'lock-open',
    'logout',
    'mail',
    'math-essential',
    'maths',
    'megaphone',
    'menu',
    'microscope',
    'minus',
    'more',
    'more-alt',
    'new-page',
    'new-window',
    'north',
    'north-east',
    'north-west',
    'note',
    'notes',
    'notepad-check',
    'number-list',
    'omega',
    'order-asc',
    'order-desc',
    'page-error',
    'page-list',
    'pages',
    'paper-airplane',
    'paste',
    'pause',
    'pause-circle',
    'pause-fill',
    'pencil-ruler',
    'pending',
    'phone',
    'pi',
    'pillar',
    'play',
    'play-circle',
    'play-fill',
    'play-video',
    'plus',
    'practice',
    'practice-test',
    'presentation',
    'presentation-alt',
    'presentation-video',
    'print',
    'question',
    'question-circle',
    'question-fill',
    'quotes',
    'raised-pencil',
    'redo',
    'refresh',
    'registration',
    'right',
    'right-align',
    'right-double',
    'save',
    'sat',
    'school',
    'school-urban',
    'script',
    'search',
    'see-off',
    'see-on',
    'send-scores',
    'settings',
    'share',
    'share-alt',
    'share-arrow',
    'sound',
    'sort',
    'sort-asc',
    'sort-desc',
    'south',
    'star',
    'stop',
    'stop-circle',
    'stop-fill',
    'stopwatch',
    'subscript',
    'submit',
    'superscript',
    'tag',
    'table-ico',
    'tablet',
    'task-complete',
    'teacher',
    'team-leader',
    'text-style-off',
    'tool',
    'trademark',
    'trash',
    'trophy',
    'twitter',
    'underline',
    'undo',
    'university',
    'up',
    'upload',
    'upload-alt',
    'user',
    'user-add',
    'user-check',
    'user-circle',
    'user-remove',
    'users',
    'vert-grab-handle',
    'west',
    'window',
    'window-check',
    'x',
    'x-mark',
    'youtube',
    'zoom-in',
    'zoom-out',
];

const ResourceCard = ({ titleId, descId, overlineId, title, titleHeadingLevel = 3, cardRole, ariaLabel, ariaLabelledbyID, description, overline = 'DOCUMENT', resourceURL, resourceMetaInfo = [], icon = 'download', target, className, titleClassName, descriptionClassName, disabled, }) => {
    const seed = useUIDSeed();
    const titleIdCB = titleId || `apricot_${seed('title')}`;
    const descIdCB = description ? descId || `apricot_${seed('desc')}` : undefined;
    const overlineIdCB = overline ? overlineId || `apricot_${seed('overline')}` : undefined;
    const metaID = resourceMetaInfo.length > 0 ? `apricot_${seed('meta')}` : undefined;
    return (React__default.createElement("div", { className: cn('cb-card cb-card-title-link', disabled && 'cb-disabled', className), "data-cbtrack-comp": "apricot-react:card", role: cardRole || undefined, "aria-label": ariaLabel || undefined, "aria-labelledby": ariaLabelledbyID || undefined },
        React__default.createElement("div", { className: "cb-card-header" },
            React__default.createElement("p", { className: "cb-text-overline-small", id: overlineIdCB }, overline)),
        React__default.createElement("div", { className: "cb-card-content" },
            React__default.createElement("a", { href: resourceURL, "aria-describedby": `${overlineIdCB} ${descIdCB} ${metaID}`, target: target, onClick: e => {
                    if (disabled) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }, tabIndex: disabled ? -1 : undefined, "aria-disabled": disabled ? true : undefined },
                React__default.createElement("p", { role: "heading", "aria-level": titleHeadingLevel, className: cn('cb-card-title', titleClassName), id: titleIdCB }, title)),
            React__default.createElement("p", { className: cn('cb-card-desc', descriptionClassName), id: descIdCB }, description),
            React__default.createElement("div", { className: "cb-card-action" },
                resourceMetaInfo && (React__default.createElement("ul", { className: "cb-meta-tags", id: metaID }, resourceMetaInfo.map((meta, index) => (React__default.createElement("li", { key: `resourceMeta_${index}` }, meta))))),
                React__default.createElement(Icon, { decorative: true, name: icon })))));
};

const illustrationSize = ['64', '72', '96'];

const Illustration = (_a) => {
    var { illustrationId, name, size, circular = false, shadow = false, customCircular = false, className, decorative = false, focusable = false, k12 = false, higherEd = false } = _a, props = __rest(_a, ["illustrationId", "name", "size", "circular", "shadow", "customCircular", "className", "decorative", "focusable", "k12", "higherEd"]);
    return (React__default.createElement("i", Object.assign({ id: illustrationId, className: cn(className, 'cb-ilus', circular && 'cb-ilus-circular', shadow && 'cb-ilus-shadow', customCircular && 'cb-ilus-custom-circular', k12 && 'cb-ilus-k-12', higherEd && 'cb-ilus-higher-ed', `cb-ilus-${name}`, size && `cb-ilus-${size}`), "aria-hidden": decorative, 
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex: focusable ? 0 : undefined, "data-cbtrack-comp": "apricot-react:ilus" }, props)));
};

/**
 * Illustration Names
 */
const illustrationName = [
    'accessibility',
    'acorn',
    'add',
    'ap',
    'apple',
    'arrow-right',
    'attach',
    'award-medal',
    'award',
    'bell',
    'book-index',
    'book-open',
    'briefcase',
    'browser-add',
    'browser-check',
    'browser-expand',
    'browser',
    'bullet-list-device',
    'bullet-list-doc',
    'bullet-list',
    'calculator',
    'calendar-add',
    'calendar-empty',
    'calendar-exclamation',
    'calendar-full',
    'calendar-save-the-date',
    'cart',
    'chat',
    'checkbox',
    'checklist-device',
    'checklist-doc',
    'checklist',
    'checkmark',
    'clock',
    'coin',
    'column',
    'compose-alt',
    'compose',
    'computer-bullet-list',
    'computer',
    'condense',
    'document-award',
    'dollar',
    'download',
    'error',
    'exclamation',
    'expand',
    'computer-explore',
    'face-bad',
    'face-excellent',
    'face-good',
    'face-neutral',
    'face-very-bad',
    'feedback',
    'financial-aid',
    'funnel',
    'settings',
    'globe',
    'graduate',
    'graph-bar',
    'heart',
    'home',
    'info',
    'khan-academy',
    'laptop-check',
    'laptop-exclamation',
    'laptop',
    'light-bulb',
    'list-alt',
    'list-pencil',
    'list',
    'location',
    'lock',
    'mail-compose',
    'mail-send',
    'math-alt',
    'math-essential',
    'math',
    'megaphone',
    'new-page',
    'information',
    'note',
    'notepad-check',
    'pages',
    'paper-airplane',
    'pause',
    'pencil',
    'phone',
    'photo',
    'play',
    'practice-alt',
    'practice',
    'presentation-bar-graph',
    'presentation',
    'print',
    'question',
    'quotes',
    'raised-pencil',
    'refresh',
    'remove-page',
    'remove',
    'right-arrow',
    'sat',
    'school-urban',
    'school',
    'science',
    'scroll',
    'search',
    'send-scores',
    'share-alt',
    'share-link',
    'share',
    'facebook',
    'instagram',
    'linkedin',
    'tiktok',
    'x',
    'youtube',
    'sound',
    'stopwatch',
    'submit-photo',
    'submit',
    'task-complete-alt',
    'task-complete',
    'star',
    'university',
    'unlock',
    'up-arrow',
    'up-down-arrow',
    'upload',
    'user-add',
    'user-approve',
    'user-remove',
    'user',
    'users',
    'wrench',
];

const CTACard = ({ className, title, description, titleHeadingLevel = 3, titleId, name, circular = false, shadow = false, customCircular = false, k12 = false, higherEd = false, buttonId, buttonLabel, href, target, buttonClass, titleClassName, descriptionClassName, cardRole, ariaLabel, ariaLabelledbyID, disabled, }) => {
    return (React__default.createElement("div", { className: cn('cb-card cb-card-cta', disabled && 'cb-disabled', className), "data-cbtrack-comp": "apricot-react:card", role: cardRole || undefined, "aria-label": ariaLabel || undefined, "aria-labelledby": ariaLabelledbyID || undefined },
        React__default.createElement("div", { className: "cb-card-header justify-content-center" },
            React__default.createElement(Illustration, { size: "72", name: name, circular: circular, shadow: shadow, customCircular: customCircular, k12: k12, higherEd: higherEd, decorative: true })),
        React__default.createElement("div", { className: "cb-card-content cb-align-center" },
            React__default.createElement("p", { role: "heading", "aria-level": titleHeadingLevel, className: cn('cb-h3 cb-font-weight-light', titleClassName), id: titleId || undefined }, title),
            description && React__default.createElement("p", { className: cn('cb-margin-top-8', descriptionClassName) }, description),
            React__default.createElement("div", { className: "cb-card-action justify-content-center" },
                React__default.createElement("a", { id: buttonId, href: href, onClick: e => {
                        if (disabled) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }, tabIndex: disabled ? -1 : undefined, "aria-disabled": disabled ? true : undefined, className: cn('cb-btn', buttonClass, disabled && 'cb-disabled'), target: target }, buttonLabel)))));
};

const noticeHLevel = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

const Notification = React__default.forwardRef((_a, ref) => {
    var { notificationId, headingLevel = 'h4', ariaLevel, headingClass, title, icon, circularIcon = true, iconClass, decorativeIcon = true, ariaLabelIcon, color, dismissible = false, onDismiss, children, inModal = false, withRegion = true, className } = _a, props = __rest(_a, ["notificationId", "headingLevel", "ariaLevel", "headingClass", "title", "icon", "circularIcon", "iconClass", "decorativeIcon", "ariaLabelIcon", "color", "dismissible", "onDismiss", "children", "inModal", "withRegion", "className"]);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    notificationId = notificationId || `apricot_notification_${uid}`;
    const notificationRef = useRef(null);
    const Heading = headingLevel;
    useImperativeHandle(ref, () => notificationRef.current);
    useEffect(() => {
        if (dismissible)
            CBNotification.Dismissible({ elem: notificationRef.current, notificationId });
        notificationRef.current.addEventListener('apricot_notificationHidden', () => {
            if (onDismiss)
                onDismiss(notificationId);
        });
    }, [dismissible]);
    return (React__default.createElement("div", Object.assign({ ref: notificationRef, className: cn(className, 'cb-notification'), "aria-labelledby": withRegion ? notificationId : undefined, role: withRegion ? 'region' : undefined, "data-cbtrack-comp": "apricot-react:notification" }, props),
        React__default.createElement("div", { className: "cb-notification-container" },
            React__default.createElement("div", { className: "cb-notification-header" },
                icon && (React__default.createElement(Icon, { name: icon, circular: circularIcon, color: color, decorative: decorativeIcon, role: !decorativeIcon ? 'img' : undefined, "aria-label": !decorativeIcon ? ariaLabelIcon : undefined, className: iconClass })),
                ariaLevel ? (React__default.createElement("p", { role: "heading", "aria-level": ariaLevel, id: notificationId, className: headingClass }, title)) : (React__default.createElement(Heading, { id: notificationId, className: cn('cb-notification-title', headingClass) }, title)),
                dismissible && (React__default.createElement(CloseButton, { "data-cb-notification-close": !inModal || null, "data-cb-modal-close": inModal || null, "aria-describedby": notificationId }))),
            children && React__default.createElement("div", { className: "cb-notification-content" }, children))));
});
const InfoNotification = React__default.forwardRef((props, ref) => (React__default.createElement(Notification, Object.assign({ icon: "megaphone", color: "blue5", ref: ref }, props))));
const SuccessNotification = React__default.forwardRef((props, ref) => (React__default.createElement(Notification, Object.assign({ icon: "check", color: "green3", ref: ref }, props))));
const CautionNotification = React__default.forwardRef((props, ref) => (React__default.createElement(Notification, Object.assign({ icon: "caution", color: "caution", ref: ref }, props))));
const ErrorNotification = React__default.forwardRef((props, ref) => (React__default.createElement(Notification, Object.assign({ icon: "exclamation", color: "red1", ref: ref }, props))));
const InputNotification = React__default.forwardRef((props, ref) => (React__default.createElement(Notification, Object.assign({ icon: "compose", color: "blue5", ref: ref }, props))));

const spinnerPalette = ['blue5', 'blue2', 'purple1'];
const spinnerSize = ['16', '24', '32', '64'];

const CSSTransition$1 = ({ children, shouldMount = true, timeout = 500, className = 'cb-css-transition' }) => (React__default.createElement(CSSTransition$2, { in: shouldMount, unmountOnExit: true, timeout: timeout, classNames: className }, children));
const Spinner = ({ spinnerId, show = true, ariaLabel = 'Loading ...', className, light = false, timeout = 500, duration, min, max, end, palette, size, text, animated = false, progress = false, suffix, transitionClassName = 'cb-css-transition', removeMainRole = false, }) => {
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    spinnerId = spinnerId || `apricot_spinner_${uid}`;
    const spinnerClassName = cn(`cb-spinner`, light && `cb-spinner-light`, className, palette && `cb-palette-${palette}`, size && `cb-spinner-${size}`);
    const progressClassName = cn(`cb-progress-spinner`, palette && `cb-palette-${palette}`);
    const spinnerRef = useRef(null);
    const getRole = () => {
        if (animated)
            return 'progressbar';
        return progress ? 'img' : 'status';
    };
    const role = getRole();
    const staticSpinner = !animated && !progress;
    const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);
    useEffect(() => {
        const spinnerAnimatedPlugin = animated &&
            CBSpinner.Animated(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ elem: spinnerRef.current }, (duration && { duration })), (min && { min })), (max && { max })), (end && { end })), (suffix && { suffix })));
        return () => {
            if (spinnerAnimatedPlugin)
                spinnerAnimatedPlugin.destroy();
        };
    }, [animated, text, suffix, duration, end, min, max, palette]);
    useEffect(() => {
        const spinnerProgressPlugin = progress &&
            CBSpinner.Progress(Object.assign(Object.assign(Object.assign({ elem: spinnerRef.current }, (suffix && { suffix })), (duration && { duration })), (end && { end })));
        return () => {
            if (spinnerProgressPlugin)
                spinnerProgressPlugin.destroy();
        };
    }, [progress, suffix, duration, end, text, palette]);
    /* eslint-disable react/no-unstable-nested-components */
    return (React__default.createElement(CSSTransition$1, { shouldMount: show, timeout: timeout, className: transitionClassName },
        React__default.createElement("div", Object.assign({}, (!removeMainRole ? { role } : {}), (staticSpinner && !removeMainRole ? { 'aria-labelledby': spinnerId } : {}), { className: progress ? progressClassName : undefined, "data-cbtrack-comp": "apricot-react:spinner", ref: spinnerRef }),
            React__default.createElement("span", { className: "ms-high-contrast" }, "Loading"),
            React__default.createElement(ConditionalWrapper, { condition: !progress, wrapper: children => (React__default.createElement("div", { className: spinnerClassName, id: spinnerId, role: !animated ? 'img' : undefined, "aria-label": ariaLabel }, children)) },
                progress && (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement("div", { className: "cb-spinner-background" }),
                    React__default.createElement("div", { className: "cb-spinner-rotate" }),
                    React__default.createElement("div", { className: "cb-spinner-right" }),
                    React__default.createElement("div", { className: "cb-spinner-left" }))),
                React__default.createElement("p", { className: "cb-spinner-indicator" },
                    text,
                    suffix),
                !progress &&
                    Array(12)
                        .fill(null)
                        .map((el, i) => React__default.createElement("div", { key: i }))))));
};

// -------------------------------- HEADER
const ModalHeader = ({ title, centeredTitle, showHeaderCloseButton, closeHeaderLabel = 'Close Modal', closeBtnHeaderClass, lightClose, headerId, classNameHeader, classNameHeaderTitle, }) => {
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    headerId = headerId || `apricot_modal_header_${uid}`;
    const headerClass = cn('cb-modal-header', showHeaderCloseButton && 'cb-modal-has-close', centeredTitle && 'cb-modal-title-center', classNameHeader);
    return (React__default.createElement("div", { className: headerClass },
        typeof title === 'string' ? (React__default.createElement("h2", { className: cn('cb-modal-title', classNameHeaderTitle), id: headerId }, title)) : (React__default.createElement("div", { id: headerId }, title)),
        showHeaderCloseButton && (React__default.createElement(CloseButton, { className: cn(lightClose && 'cb-btn-greyscale', lightClose && 'cb-white-color', closeBtnHeaderClass), "aria-describedby": headerId, label: closeHeaderLabel, "data-cb-modal-close": true }))));
};
// -------------------------------- FOOTER
const FooterButtons = ({ children, closeButton = true, closeButtonLabel, closeButtonId }) => {
    return (React__default.createElement(ButtonRow, null,
        children,
        closeButton && (React__default.createElement(PrimaryButton, { id: closeButtonId, "data-cb-modal-close": true, "aria-label": "Close this dialog window" }, closeButtonLabel || 'Close'))));
};
// -------------------------------- BAR Modal
const BareModal = ({ children, open = false, trigger, className, classNameOverlay, classNameContainer, onClose, onShow, clickOverlayToClose = true, disableHeightAdjustment = false, disableHeightAdjustmentAria = false, escClose = true, role = 'dialog', ariaLabel, modalId, modalNode = false, shadowRoot = false, headerId, focusId, nested = false, videoModal, promoModal, analytics = false, analyticsTitle, analyticsOnClose = false, enableChildOverflow = false, customAttributes, awaitOpenAnimation = true, awaitCloseAnimation = true, openAnimation = true, closeAnimation = true, }) => {
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    modalId = modalId || `apricot_modal_${uid}`;
    const ref = useRef(null);
    const controlled = !trigger;
    // disable if we want overflow to work
    // eslint-disable-next-line no-param-reassign
    disableHeightAdjustment = enableChildOverflow ? true : disableHeightAdjustment;
    // Uncontrolled modal
    useEffect(() => {
        if (ref.current && trigger && !controlled) {
            CBModal({
                elem: modalNode ? ref.current : modalId,
                trigger: typeof trigger === 'string' ? document.getElementById(trigger) : trigger,
                focusElem: focusId ? document.getElementById(focusId) : undefined,
                nested,
                disableHeightAdjustment,
                disableHeightAdjustmentAria,
                closeOnEscape: !controlled,
                onShow,
                escClose,
                onClose,
                videoModal,
                promoModal,
                analytics,
                analyticsTitle,
                analyticsOnClose,
                awaitCloseAnimation,
                awaitOpenAnimation,
                openAnimation,
                closeAnimation,
                shadowRoot,
            });
        }
        return () => {
            // make sure the modal is closed
            if (ref.current && ref.current.classList.contains('cb-open')) {
                CBModal({
                    elem: modalNode ? ref.current : modalId,
                    trigger: typeof trigger === 'string' ? document.getElementById(trigger) : trigger,
                    nested,
                    shadowRoot,
                    escClose,
                    onClose,
                    videoModal,
                    promoModal,
                    analytics,
                    analyticsTitle,
                    analyticsOnClose,
                    awaitCloseAnimation,
                    closeAnimation,
                }).close();
            }
            try {
                const body = document.getElementsByTagName('body')[0];
                if (body.classList.contains('cb-modal-open')) {
                    body.classList.remove('cb-modal-open');
                }
            }
            catch (error) {
                console.warn(error);
            }
        };
    }, []);
    // controlled modal
    useEffect(() => {
        if (!controlled)
            return;
        if (open && ref.current) {
            CBModal({
                elem: modalNode ? ref.current : modalId,
                focusElem: focusId ? document.getElementById(focusId) : undefined,
                nested,
                shadowRoot,
                escClose,
                disableHeightAdjustment,
                disableHeightAdjustmentAria,
                videoModal,
                promoModal,
                analytics,
                analyticsTitle,
                analyticsOnClose,
                controlled: true,
                awaitCloseAnimation,
                awaitOpenAnimation,
                openAnimation,
                closeAnimation,
                onClose,
                onShow,
            }).show();
        }
        else if (ref.current && ref.current.classList.contains('cb-open')) {
            CBModal({
                elem: modalNode ? ref.current : modalId,
                focusElem: focusId ? document.getElementById(focusId) : undefined,
                nested,
                shadowRoot,
                escClose,
                analytics,
                analyticsTitle,
                analyticsOnClose,
                controlled: true,
                awaitCloseAnimation,
                closeAnimation,
                onClose,
            }).close();
        }
    }, [open]);
    useEffect(() => {
        // only adjust the height if the Modal is open
        // controlled and un-controlled
        if (CBModal && ref.current && ref.current.classList.contains('cb-open')) {
            CBModal({
                elem: modalNode ? ref.current : modalId,
                trigger: typeof trigger === 'string' ? document.getElementById(trigger) : trigger,
                focusElem: focusId ? document.getElementById(focusId) : undefined,
                nested,
                shadowRoot,
                disableHeightAdjustment,
                disableHeightAdjustmentAria,
                onShow,
                escClose,
                onClose,
                videoModal,
                promoModal,
                analytics,
                analyticsTitle,
                analyticsOnClose,
                awaitCloseAnimation,
                awaitOpenAnimation,
                openAnimation,
                closeAnimation,
            }).adjustHeight();
        }
    }, [children]);
    return (React__default.createElement("div", Object.assign({ className: cn('cb-modal', className), id: modalId, "aria-hidden": "true", "data-cbtrack-comp": "apricot-react:modal", ref: ref }, customAttributes),
        React__default.createElement("div", { className: cn('cb-modal-overlay', classNameOverlay), tabIndex: -1, "data-cb-modal-close": isNil(clickOverlayToClose) ? true : !!clickOverlayToClose || null },
            React__default.createElement("div", { className: cn('cb-modal-container', classNameContainer, enableChildOverflow && 'cb-modal-overflow'), role: role, "aria-modal": "true", "aria-labelledby": !ariaLabel ? headerId : undefined, "aria-label": ariaLabel }, children))));
};
// -------------------------------- MODAL
const Modal = (_a) => {
    var { open, trigger, modalId, focusId, nested = false, headerId, classNameHeader, classNameHeaderTitle, title, centeredTitle = false, showHeaderCloseButton = true, closeHeaderLabel, actions, className, classNameOverlay, classNameContainer, onClose, onShow, role, clickOverlayToClose, disableHeightAdjustment, disableHeightAdjustmentAria, escClose, closeButton, closeButtonLabel, closeButtonId, lightClose, footer, videoModal, withFooter = true, withHeader = true, ariaLabel, analytics = false, analyticsTitle, analyticsOnClose = false, enableChildOverflow = false, customAttributes, awaitCloseAnimation = true, awaitOpenAnimation = true, openAnimation = true, closeAnimation = true, children } = _a, props = __rest(_a, ["open", "trigger", "modalId", "focusId", "nested", "headerId", "classNameHeader", "classNameHeaderTitle", "title", "centeredTitle", "showHeaderCloseButton", "closeHeaderLabel", "actions", "className", "classNameOverlay", "classNameContainer", "onClose", "onShow", "role", "clickOverlayToClose", "disableHeightAdjustment", "disableHeightAdjustmentAria", "escClose", "closeButton", "closeButtonLabel", "closeButtonId", "lightClose", "footer", "videoModal", "withFooter", "withHeader", "ariaLabel", "analytics", "analyticsTitle", "analyticsOnClose", "enableChildOverflow", "customAttributes", "awaitCloseAnimation", "awaitOpenAnimation", "openAnimation", "closeAnimation", "children"]);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    headerId = !videoModal && withHeader ? headerId || `apricot_modal_header_${uid}` : undefined;
    return (React__default.createElement(BareModal, Object.assign({ open: open, trigger: trigger, onClose: onClose, onShow: onShow, clickOverlayToClose: clickOverlayToClose, disableHeightAdjustment: disableHeightAdjustment, disableHeightAdjustmentAria: disableHeightAdjustmentAria, className: className, classNameOverlay: classNameOverlay, classNameContainer: classNameContainer, escClose: escClose, role: role, videoModal: videoModal, modalId: modalId, headerId: headerId, focusId: focusId, nested: nested, ariaLabel: ariaLabel, customAttributes: customAttributes, analytics: analytics, analyticsTitle: analyticsTitle, analyticsOnClose: analyticsOnClose, enableChildOverflow: enableChildOverflow, awaitCloseAnimation: awaitCloseAnimation, awaitOpenAnimation: awaitOpenAnimation, openAnimation: openAnimation, closeAnimation: closeAnimation }, props),
        withHeader && (React__default.createElement(ModalHeader, { title: title, showHeaderCloseButton: showHeaderCloseButton, closeHeaderLabel: closeHeaderLabel, lightClose: lightClose, headerId: headerId, classNameHeader: classNameHeader, classNameHeaderTitle: classNameHeaderTitle, centeredTitle: centeredTitle })),
        React__default.createElement("div", { className: "cb-modal-content" }, children),
        withFooter && (React__default.createElement("div", { className: "cb-modal-footer" }, footer || (React__default.createElement(FooterButtons, { closeButtonLabel: closeButtonLabel, closeButton: closeButton, closeButtonId: closeButtonId }, actions))))));
};
// -------------------------------- DIALOG MODAL
const dialogs = {
    success: SuccessNotification,
    info: InfoNotification,
    caution: CautionNotification,
    error: ErrorNotification,
    input: InfoNotification,
};
const ModalDialog = (_a) => {
    var { children, open, trigger, onClose, onShow, clickOverlayToClose, disableHeightAdjustment, disableHeightAdjustmentAria, className, classNameOverlay, classNameContainer, escClose, role = 'dialog', modalId, focusId, variant, analytics = false, analyticsTitle, analyticsOnClose = false, customAttributes, awaitCloseAnimation = true, awaitOpenAnimation = true, openAnimation = true, closeAnimation = true, withRegion = false } = _a, dialogProps = __rest(_a, ["children", "open", "trigger", "onClose", "onShow", "clickOverlayToClose", "disableHeightAdjustment", "disableHeightAdjustmentAria", "className", "classNameOverlay", "classNameContainer", "escClose", "role", "modalId", "focusId", "variant", "analytics", "analyticsTitle", "analyticsOnClose", "customAttributes", "awaitCloseAnimation", "awaitOpenAnimation", "openAnimation", "closeAnimation", "withRegion"]);
    const NotificationComponent = dialogs[variant];
    const uid = useUID();
    const notificationId = `apricot_notification_${uid}`;
    return (React__default.createElement(BareModal, { open: open, trigger: trigger, className: cn('cb-dialog-modal', className), classNameOverlay: classNameOverlay, classNameContainer: classNameContainer, onClose: onClose, onShow: onShow, role: role, modalId: modalId, headerId: notificationId, focusId: focusId, clickOverlayToClose: clickOverlayToClose, disableHeightAdjustment: disableHeightAdjustment, disableHeightAdjustmentAria: disableHeightAdjustmentAria, escClose: escClose, customAttributes: customAttributes, analytics: analytics, analyticsTitle: analyticsTitle, analyticsOnClose: analyticsOnClose, awaitCloseAnimation: awaitCloseAnimation, awaitOpenAnimation: awaitOpenAnimation, openAnimation: openAnimation, closeAnimation: closeAnimation },
        React__default.createElement("div", { className: "cb-modal-content" },
            React__default.createElement(NotificationComponent, Object.assign({}, dialogProps, { inModal: true, dismissible: true, withRegion: withRegion, notificationId: notificationId }), children))));
};
// -------------------------------- SPINNER MODAL
const ModalSpinner = (_a) => {
    var { open = false, trigger, className, classNameOverlay, classNameContainer, classNameSpinner, onClose, onShow, clickOverlayToClose = true, disableHeightAdjustment, disableHeightAdjustmentAria, escClose = true, role = 'dialog', ariaLabel, modalId, analytics = false, analyticsTitle, analyticsOnClose = false, customAttributes, awaitCloseAnimation = true, awaitOpenAnimation = true, openAnimation = true, closeAnimation = true } = _a, spinnerProps = __rest(_a, ["open", "trigger", "className", "classNameOverlay", "classNameContainer", "classNameSpinner", "onClose", "onShow", "clickOverlayToClose", "disableHeightAdjustment", "disableHeightAdjustmentAria", "escClose", "role", "ariaLabel", "modalId", "analytics", "analyticsTitle", "analyticsOnClose", "customAttributes", "awaitCloseAnimation", "awaitOpenAnimation", "openAnimation", "closeAnimation"]);
    return (React__default.createElement(BareModal, { open: open, trigger: trigger, className: cn('cb-spinner-modal', className), classNameOverlay: classNameOverlay, classNameContainer: classNameContainer, onClose: onClose, onShow: onShow, role: role, clickOverlayToClose: clickOverlayToClose, disableHeightAdjustment: disableHeightAdjustment, disableHeightAdjustmentAria: disableHeightAdjustmentAria, escClose: escClose, ariaLabel: ariaLabel, modalId: modalId, customAttributes: customAttributes, analytics: analytics, analyticsTitle: analyticsTitle, analyticsOnClose: analyticsOnClose, awaitCloseAnimation: awaitCloseAnimation, awaitOpenAnimation: awaitOpenAnimation, openAnimation: openAnimation, closeAnimation: closeAnimation },
        React__default.createElement("div", { className: "cb-modal-content" },
            React__default.createElement(Spinner, Object.assign({}, spinnerProps, { className: classNameSpinner })))));
};
// -------------------------------- VIDEO MODAL
const ModalVideo = ({ open = false, trigger, className, classNameOverlay, classNameContainer, onClose, onShow, clickOverlayToClose = true, disableHeightAdjustment, disableHeightAdjustmentAria, escClose = true, role = 'dialog', modalId, headerId, videoSrc, videoTitle, analytics = false, analyticsTitle, analyticsOnClose = false, awaitCloseAnimation = true, awaitOpenAnimation = true, openAnimation = true, closeAnimation = true, customAttributes, }) => (React__default.createElement(Modal, { open: open, trigger: trigger, className: cn('cb-video-modal', className), classNameOverlay: classNameOverlay, classNameContainer: classNameContainer, onClose: onClose, onShow: onShow, role: role, clickOverlayToClose: clickOverlayToClose, disableHeightAdjustment: disableHeightAdjustment, disableHeightAdjustmentAria: disableHeightAdjustmentAria, escClose: escClose, modalId: modalId, headerId: headerId, awaitCloseAnimation: awaitCloseAnimation, awaitOpenAnimation: awaitOpenAnimation, openAnimation: openAnimation, closeAnimation: closeAnimation, videoModal: true, footer: React__default.createElement(ButtonRow, null,
        React__default.createElement(NakedButton, { className: "cb-no-padding", light: true, "data-cb-modal-close": true, "aria-label": "Close Modal" }, "Close")), lightClose: true, ariaLabel: videoTitle, customAttributes: customAttributes, analytics: analytics, analyticsTitle: analyticsTitle, analyticsOnClose: analyticsOnClose },
    React__default.createElement("div", { className: "cb-modal-content" },
        React__default.createElement("iframe", { src: "", "data-cb-src": videoSrc, title: videoTitle, allowFullScreen: true }))));
// -------------------------------- PROMOTIONAL MODAL
const ModalPromo = (_a) => {
    var { open, trigger, modalId, focusId, headerId, title, centeredTitle = false, lightClosePromo = true, headerCloseButtonPromo = true, closeBtnHeaderClass, closeHeaderLabel, actions, className, classNameOverlay, classNameContainer, promoImgClass, image, onClose, onShow, role, clickOverlayToClose, disableHeightAdjustment, disableHeightAdjustmentAria, escClose, closeButton, closeButtonLabel, closeButtonId, footer, withFooter = true, withHeader = true, ariaLabel, analytics = false, analyticsTitle, analyticsOnClose = false, enableChildOverflow = false, customAttributes, awaitCloseAnimation = true, awaitOpenAnimation = true, openAnimation = true, closeAnimation = true, variant = 'top', children } = _a, props = __rest(_a, ["open", "trigger", "modalId", "focusId", "headerId", "title", "centeredTitle", "lightClosePromo", "headerCloseButtonPromo", "closeBtnHeaderClass", "closeHeaderLabel", "actions", "className", "classNameOverlay", "classNameContainer", "promoImgClass", "image", "onClose", "onShow", "role", "clickOverlayToClose", "disableHeightAdjustment", "disableHeightAdjustmentAria", "escClose", "closeButton", "closeButtonLabel", "closeButtonId", "footer", "withFooter", "withHeader", "ariaLabel", "analytics", "analyticsTitle", "analyticsOnClose", "enableChildOverflow", "customAttributes", "awaitCloseAnimation", "awaitOpenAnimation", "openAnimation", "closeAnimation", "variant", "children"]);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    headerId = withHeader ? headerId || `apricot_modal_header_${uid}` : undefined;
    return (React__default.createElement(BareModal, Object.assign({ open: open, trigger: trigger, onClose: onClose, onShow: onShow, clickOverlayToClose: clickOverlayToClose, disableHeightAdjustment: disableHeightAdjustment, disableHeightAdjustmentAria: disableHeightAdjustmentAria, className: cn('cb-promo-modal', className), classNameOverlay: classNameOverlay, classNameContainer: cn(`cb-promo-modal-${variant}`, classNameContainer), escClose: escClose, role: role, promoModal: true, modalId: modalId, headerId: headerId, focusId: focusId, ariaLabel: ariaLabel, customAttributes: customAttributes, analytics: analytics, analyticsTitle: analyticsTitle, analyticsOnClose: analyticsOnClose, enableChildOverflow: enableChildOverflow, awaitCloseAnimation: awaitCloseAnimation, awaitOpenAnimation: awaitOpenAnimation, openAnimation: openAnimation, closeAnimation: closeAnimation }, props),
        React__default.createElement("div", { className: cn('cb-promo-modal-img', promoImgClass), style: image ? { backgroundImage: `url(${image})` } : undefined }, headerCloseButtonPromo && (React__default.createElement(CloseButton, { className: cn(lightClosePromo && 'cb-btn-light', !lightClosePromo && 'cb-btn-black', variant === 'left' && 'hidden-sm-up', closeBtnHeaderClass), "aria-describedby": headerId, label: closeHeaderLabel, "data-cb-modal-close": true, greyscale: false }))),
        React__default.createElement("div", { className: "cb-promo-modal-container" },
            withHeader && (React__default.createElement(ModalHeader, { title: title, closeHeaderLabel: closeHeaderLabel, lightClose: false, showHeaderCloseButton: variant === 'left', headerId: headerId, centeredTitle: centeredTitle, closeBtnHeaderClass: cn('hidden-xs-only', closeBtnHeaderClass) })),
            React__default.createElement("div", { className: "cb-modal-content" }, children),
            withFooter && (React__default.createElement("div", { className: "cb-modal-footer" }, footer || (React__default.createElement(FooterButtons, { closeButtonLabel: closeButtonLabel, closeButton: closeButton, closeButtonId: closeButtonId }, actions)))))));
};

const MediaCard = ({ videoCardId, titleId, descId, overlineId, title, titleHeadingLevel = 3, description, overline = 'Video', cardImage, mediaMetaInfo = [], className, titleClassName, descriptionClassName, modalClassName, classNameOverlay, onClose, onShow, clickOverlayToClose = true, disableHeightAdjustment, disableHeightAdjustmentAria, escClose = true, role = 'dialog', cardRole, ariaLabel, ariaLabelledbyID, modalId, headerId, videoSrc, videoTitle, analytics = false, analyticsTitle, analyticsOnClose = false, awaitCloseAnimation = true, awaitOpenAnimation = true, openAnimation = true, closeAnimation = true, customAttributes, shadowRoot, disabled, }) => {
    const seed = useUIDSeed();
    const videoTriggerId = videoCardId || `apricot_${seed('trigger')}`;
    const titleIdCB = titleId || `apricot_${seed('title')}`;
    const descIdCB = description ? descId || `apricot_${seed('desc')}` : undefined;
    const overlineIdCB = overline ? overlineId || `apricot_${seed('overline')}` : undefined;
    const metaID = mediaMetaInfo.length > 0 ? `apricot_${seed('meta')}` : undefined;
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: cn('cb-card cb-card-title-link cb-card-media', disabled && 'cb-disabled', className), "data-cbtrack-comp": "apricot-react:card", role: cardRole || undefined, "aria-label": ariaLabel || undefined, "aria-labelledby": ariaLabelledbyID || undefined },
            React__default.createElement("div", { className: "cb-card-header" },
                React__default.createElement("p", { className: "cb-text-overline-small", id: overlineIdCB }, overline)),
            React__default.createElement("div", { className: "cb-card-media-content cb-video-block" },
                React__default.createElement("div", { className: "cb-card-media-img", style: { backgroundImage: `url(${cardImage})` } }),
                React__default.createElement("div", { className: "cb-video-effect cb-black1-bg cb-opacity-9" },
                    React__default.createElement("span", { className: "cb-icon cb-play-video cb-white-color" }))),
            React__default.createElement("div", { className: "cb-card-content" },
                React__default.createElement("a", { href: "/#", "aria-labelledby": titleIdCB, id: videoTriggerId, "aria-describedby": `${overlineIdCB} ${descIdCB} ${metaID}`, className: cn(disabled && 'cb-disabled'), tabIndex: disabled ? -1 : undefined, "aria-disabled": disabled ? true : undefined, onClick: e => {
                        if (disabled) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    } },
                    React__default.createElement("p", { role: "heading", "aria-level": titleHeadingLevel, className: cn('cb-card-title', titleClassName), id: titleIdCB }, title)),
                React__default.createElement("p", { className: cn('cb-card-desc', descriptionClassName), id: descIdCB }, description),
                React__default.createElement("div", { className: "cb-card-action" }, mediaMetaInfo && (React__default.createElement("ul", { className: "cb-meta-tags", id: metaID }, mediaMetaInfo.map((meta, index) => (React__default.createElement("li", { key: `mediaMeta_${index}` }, meta)))))))),
        !disabled && (React__default.createElement(ModalVideo, { trigger: videoTriggerId, className: cn('cb-video-modal', modalClassName), classNameOverlay: classNameOverlay, onClose: onClose, onShow: onShow, role: role, shadowRoot: shadowRoot, clickOverlayToClose: clickOverlayToClose, disableHeightAdjustment: disableHeightAdjustment, disableHeightAdjustmentAria: disableHeightAdjustmentAria, escClose: escClose, modalId: modalId, headerId: headerId, awaitCloseAnimation: awaitCloseAnimation, awaitOpenAnimation: awaitOpenAnimation, openAnimation: openAnimation, closeAnimation: closeAnimation, videoModal: true, footer: React__default.createElement(ButtonRow, null,
                React__default.createElement(NakedButton, { className: "cb-no-padding", light: true, "data-cb-modal-close": true, "aria-label": "Close Modal" }, "Close")), lightClose: true, ariaLabel: videoTitle, customAttributes: customAttributes, analytics: analytics, analyticsTitle: analyticsTitle, analyticsOnClose: analyticsOnClose, videoSrc: videoSrc },
            React__default.createElement("div", { className: "cb-modal-content" },
                React__default.createElement("iframe", { src: "", "data-cb-src": videoSrc, title: videoTitle, allowFullScreen: true }))))));
};

const PromoCard = ({ className, title, description = null, titleHeadingLevel = 2, titleClassName, descriptionClassName, metaInfo = null, icon = null, border, author, disabled, }) => {
    return (React__default.createElement("div", { className: cn('cb-card', 'cb-card-promo', border && `cb-${border}-border`, !border && 'cb-no-border', (border === 'blue3' || border === 'blue5') && 'cb-blue2-tint-2', border === 'purple1' && 'cb-purple1-tint-1', disabled && 'cb-disabled', className), "data-cbtrack-comp": "apricot-react:card" },
        React__default.createElement("div", { className: "cb-card-header" },
            icon && React__default.createElement(Icon, { decorative: true, name: icon }),
            typeof title === 'string' ? (React__default.createElement("p", { role: "heading", "aria-level": titleHeadingLevel, className: cn('cb-card-title', titleClassName) }, title)) : (React__default.createElement("div", { className: cn('cb-card-title', titleClassName) }, title))),
        React__default.createElement("div", { className: "cb-card-content" },
            description && React__default.createElement("p", { className: cn('cb-card-desc', descriptionClassName) }, description),
            metaInfo && (React__default.createElement("div", { className: "cb-card-action" },
                React__default.createElement("ul", { className: cn('cb-meta-tags', `${author && 'cb-text-author'}`) }, metaInfo.map((meta, index) => (React__default.createElement("li", { key: `meta_${index}` }, meta)))))))));
};

const SimpleCard = (_a) => {
    var { id, className, boxShadow = true, borderRadius = true, disabled, children } = _a, props = __rest(_a, ["id", "className", "boxShadow", "borderRadius", "disabled", "children"]);
    return (React__default.createElement("div", Object.assign({ id: id, className: cn('cb-card', !boxShadow && 'cb-no-box-shadow', !borderRadius && 'cb-no-border-radius', disabled && 'cb-disabled', className) }, props), children));
};

const popoverDatePickerPlacement = ['bottom-start', 'top-start'];
const datePickerPalette = ['blue5', 'blue2', 'purple1'];

const validationState = ['success', 'error', 'general'];
const floatingBg = ['dark', 'light'];
const ariaLive = ['polite', 'assertive', 'off'];
// Based on Button component buttonVariants
const fileButtonVariants = ['primary', 'yellow', 'black'];
const fileButtonSize = ['sm', 'lg'];
const fdbkPosition = ['first', 'middle', 'last'];
const autocomplete = ['on', 'off'];
const labelPosition = ['left', 'right'];
// ---------------------------------------- Multi Select
const msLabelPosition = ['left', 'right'];
const msTagPosition = ['top', 'bottom'];

const ClearButton = ({ inputId, buttonId, onClear }) => (React__default.createElement(SquareButton, { id: buttonId, greyscale: true, icon: "x-mark", label: "Clear", tabIndex: -1, "aria-controls": inputId, "aria-hidden": "true", onClick: (event) => {
        if (onClear)
            onClear(event);
    }, onMouseDown: e => {
        // order: onMouseDown -> onBlur -> onMouseUp -> onClick
        // clicking on the button, the onMouseDown event
        // of the button is triggered first.
        // calling e.preventDefault() in the onMouseDown event handler of the button,
        // prevents the default behavior of the onMouseDown event, which includes
        // causing the input to lose focus and trigger its onBlur event
        e.preventDefault();
    } }));
const Input = forwardRef((_a, ref) => {
    var { validationMessageId, validationMsgIconOnly = false, blankValidationIcon = false, type = 'text', label, ariaLabel, labelClassName, labelId, name, floating = true, floatingBg, condensed = false, disabled = false, clearable = true, required, icon, iconRight = false, iconDecorative = true, placeholder, validation, validationMsg, helperClassName, ariaLive, ariaDescribedby, validationMsgRole, button, buttonId, buttonOffScreen, buttonDisabled = false, buttonClassName, mainClassName, onChange = () => { }, onFocus = () => { }, onClick = () => { }, onClear = () => { }, onBlur = () => { }, onKeyUp = () => { }, onKeyDown = () => { }, onClickInput = () => { }, defaultValue, value, mask, maskChar = '_', maskNum = '#xXdDmMyY9', maskPlaceholder = null, maskCallback, inputWithDropdown = false, mainRef = null, divAttributes, children } = _a, props = __rest(_a, ["validationMessageId", "validationMsgIconOnly", "blankValidationIcon", "type", "label", "ariaLabel", "labelClassName", "labelId", "name", "floating", "floatingBg", "condensed", "disabled", "clearable", "required", "icon", "iconRight", "iconDecorative", "placeholder", "validation", "validationMsg", "helperClassName", "ariaLive", "ariaDescribedby", "validationMsgRole", "button", "buttonId", "buttonOffScreen", "buttonDisabled", "buttonClassName", "mainClassName", "onChange", "onFocus", "onClick", "onClear", "onBlur", "onKeyUp", "onKeyDown", "onClickInput", "defaultValue", "value", "mask", "maskChar", "maskNum", "maskPlaceholder", "maskCallback", "inputWithDropdown", "mainRef", "divAttributes", "children"]);
    const uid = useUID();
    const inputId = props.id || `apricot_input_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_input_err_${uid}`;
    const inputRef = useRef(null);
    const [password, setPassword] = useState(false);
    useImperativeHandle(ref, () => inputRef.current);
    useEffect(() => {
        var _a, _b;
        if (mask || maskPlaceholder) {
            CBInputMask({
                elem: inputRef.current,
                cbPlaceholder: maskPlaceholder,
                cbChar: maskChar,
                cbNum: maskNum,
                cbMask: mask,
                callBack: maskCallback,
            });
        }
        if (floating)
            CBForm.floatingLabel(inputRef.current);
        if (clearable) {
            CBForm.clearInput({ elem: inputRef.current });
            if (onChange)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('change', (e) => {
                    onChange(e, e.target.validation);
                });
            if (onKeyUp)
                (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.addEventListener('keyup', (e) => {
                    onKeyUp(e);
                });
        }
        if (inputRef.current.getAttribute('type') === 'password') {
            CBForm.passwordInput({ elem: inputRef.current });
            setPassword(true);
        }
    }, [disabled]);
    const mainClass = cn('cb-input', (clearable || button) && 'cb-btn-input', floating && label && 'cb-floating-label', clearable && 'cb-clear-input', condensed && 'cb-condensed', disabled && 'cb-disabled', validation && !inputWithDropdown && `cb-validation-${validation}`, blankValidationIcon && 'cb-validation-state', password && 'cb-password-input', !label && 'cb-input-no-label', (mask || maskPlaceholder) && 'cb-input-mask', mainClassName);
    const labelClass = cn(required && 'cb-required', floating && floatingBg && `cb-${floatingBg}-bg`, floating && value && value.toLocaleString().length > 0 && 'cb-focus-fl', labelClassName);
    const wrapperClassName = cn(icon && `cb-input-icon-${iconRight ? 'right' : 'left'}`, !icon && validation && 'cb-validation-label-input');
    const helperClass = cn('cb-input-helper', validation && `cb-validation-${validation}`, helperClassName);
    const attributesArray = (attributes) => {
        return attributes.reduce((attrib, [aName, aValue]) => (Object.assign(Object.assign({}, attrib), { [aName]: aValue })), {});
    };
    const getDescribedBy = () => {
        if (validation) {
            if (validationMsg && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMsg ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    return (React__default.createElement(React__default.Fragment, null,
        inputWithDropdown && !floating && !!label && (React__default.createElement("label", { htmlFor: inputId, className: labelClass, id: labelId }, label)),
        React__default.createElement("div", Object.assign({ className: mainClass, ref: mainRef }, (divAttributes && attributesArray(divAttributes)), { "data-cbtrack-comp": "apricot-react:input" }),
            React__default.createElement("div", { className: wrapperClassName },
                (!inputWithDropdown || (inputWithDropdown && floating)) && !!label && (React__default.createElement("label", { htmlFor: inputId, className: labelClass, id: labelId }, label)),
                React__default.createElement("input", Object.assign({ type: type, name: name, ref: inputRef, id: inputId, onChange: e => onChange(e, e.target.value), onFocus: onFocus, onBlur: onBlur, onKeyUp: onKeyUp, onKeyDown: onKeyDown, onClick: onClickInput, disabled: disabled, placeholder: placeholder, "aria-required": required, "aria-label": ariaLabel, "aria-describedby": describedbyValue || undefined, defaultValue: defaultValue, value: value, "aria-invalid": validation === 'error' }, props)),
                icon && React__default.createElement(Icon, { name: icon, decorative: iconDecorative }),
                clearable && React__default.createElement(ClearButton, { buttonId: buttonId, inputId: inputId, onClear: onClear }),
                button && (React__default.createElement(SquareButton, { onClick: onClick, greyscale: true, icon: button, label: buttonOffScreen || '', "aria-controls": inputId, disabled: password ? disabled : buttonDisabled, className: buttonClassName, id: buttonId }))),
            inputWithDropdown && children,
            (validation === 'error' || validation === 'success') &&
                !inputWithDropdown &&
                !validationMsgIconOnly && (React__default.createElement(Icon, { className: "cb-validation-icon", name: validation === 'error' ? 'exclamation-fill' : 'check-fill', decorative: true })),
            blankValidationIcon && React__default.createElement("i", { className: "cb-validation-icon" })),
        validation && validationMsg && (React__default.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
});

const Password = props => {
    return (React__default.createElement(Input, Object.assign({ clearable: false, label: "Password", buttonOffScreen: "Show Password", button: "see-off", iconRight: true }, props, { type: "password" })));
};

const InputStepper = forwardRef((_a, ref) => {
    var { step = 1, min, max, digits = 2, validationMessageId, label, labelId, ariaLabel, labelClassName, name, condensed = false, disabled = false, increaseButtonDisabled = false, decreaseButtonDisabled = false, required, validation, validationMsg, validationMsgRole, helperMsg, helperClassName, ariaLive, ariaDescribedby, mainClassName, onValueChange = () => { }, onValidation = () => { }, onChange = () => { }, onFocus = () => { }, onDecreaseClick = () => { }, onIncreaseClick = () => { }, onBlur = () => { }, onKeyUp = () => { }, onKeyDown = () => { }, value } = _a, props = __rest(_a, ["step", "min", "max", "digits", "validationMessageId", "label", "labelId", "ariaLabel", "labelClassName", "name", "condensed", "disabled", "increaseButtonDisabled", "decreaseButtonDisabled", "required", "validation", "validationMsg", "validationMsgRole", "helperMsg", "helperClassName", "ariaLive", "ariaDescribedby", "mainClassName", "onValueChange", "onValidation", "onChange", "onFocus", "onDecreaseClick", "onIncreaseClick", "onBlur", "onKeyUp", "onKeyDown", "value"]);
    const uid = useUID();
    const inputId = props.id || `apricot_input_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_input_err_${uid}`;
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);
    const [currentStepValue, setCurrentStepValue] = useState(value || min);
    const [validationState, setValidationState] = useState(validation);
    const errorMsg = validationMsg || `Please enter a value between ${min} and ${max}`;
    const [validationMessage, setValidationMessage] = useState(errorMsg);
    const mainClass = cn('cb-input', 'cb-stepper-input', disabled && 'cb-disabled', condensed && 'cb-condensed', validationState && `cb-validation-${validationState}`, digits && digits > 2 ? `cb-stepper-input-${digits <= 4 ? digits : 'unset'}` : null, mainClassName);
    const labelClass = cn(required && 'cb-required', condensed && 'cb-condensed', disabled && 'cb-disabled', labelClassName);
    const helperClass = cn('cb-input-helper', validationState && `cb-validation-${validationState}`, helperClassName);
    const isNumber = (n) => {
        if (n === '' || n === undefined)
            return false;
        const v = Number(n);
        return typeof v === 'number' && Number.isFinite(v);
    };
    const validateInput = (val) => {
        if (isNumber(min) && isNumber(max)) {
            if (val < Number(min) || val > Number(max)) {
                setValidationState('error');
                setValidationMessage(errorMsg);
                if (onValidation)
                    onValidation(val);
            }
            else {
                setValidationState(undefined);
            }
        }
    };
    const stepDecrease = (e) => {
        setCurrentStepValue((prev) => {
            const newValue = Number(prev) - step;
            if (isNumber(newValue))
                validateInput(newValue);
            if (onValueChange)
                onValueChange(newValue);
            return newValue;
        });
        if (onDecreaseClick)
            onDecreaseClick(e);
    };
    const stepIncrease = (e) => {
        setCurrentStepValue((prev) => {
            const newValue = Number(prev) + step;
            if (isNumber(newValue))
                validateInput(newValue);
            if (onValueChange)
                onValueChange(newValue);
            return newValue;
        });
        if (onIncreaseClick)
            onIncreaseClick(e);
    };
    const handleInputChange = (e) => {
        setCurrentStepValue(e.target.value);
        if (onChange)
            onChange(e);
        if (onValueChange)
            onValueChange(Number(e.target.value));
    };
    const handleInputBlur = (e) => {
        if (isNumber(e.target.value) && isNumber(min) && isNumber(max)) {
            validateInput(e.target.value);
        }
        if (onBlur)
            onBlur(e);
    };
    const getDescribedBy = () => {
        if (validationState) {
            if (validationMessage && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMessage ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    useEffect(() => {
        setValidationMessage(validationMsg);
    }, [validationMsg]);
    useEffect(() => {
        setValidationState(validation);
    }, [validation]);
    useEffect(() => {
        if (value !== undefined) {
            setCurrentStepValue(value);
            if (isNumber(min) && isNumber(max)) {
                if (value)
                    validateInput(value);
            }
        }
    }, [value]);
    return (React__default.createElement(React__default.Fragment, null,
        !!label && (React__default.createElement("label", { htmlFor: inputId, className: labelClass, id: labelId }, label)),
        React__default.createElement("div", { className: mainClass, "data-cbtrack-comp": "apricot-react:stepper-input" },
            React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-square cb-btn-greyscale cb-stepper-decrease", "aria-controls": inputId, onClick: stepDecrease, disabled: disabled ||
                    decreaseButtonDisabled ||
                    (min !== undefined && currentStepValue ? currentStepValue < min + step : false) ||
                    currentStepValue === min },
                React__default.createElement("span", { className: "cb-icon cb-minus", "aria-hidden": "true" }),
                React__default.createElement("span", { className: "sr-only" },
                    "Decrease by ",
                    step)),
            React__default.createElement("input", Object.assign({ name: name, type: "number", "aria-live": "polite", id: inputId, ref: inputRef, value: currentStepValue, "aria-label": ariaLabel, onChange: handleInputChange, onFocus: onFocus, onBlur: handleInputBlur, onKeyUp: onKeyUp, onKeyDown: onKeyDown, "aria-describedby": describedbyValue || undefined, "aria-invalid": validation === 'error', disabled: disabled, min: min, max: max }, props)),
            React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-square cb-btn-greyscale cb-stepper-increase", "aria-controls": inputId, onClick: stepIncrease, disabled: disabled ||
                    increaseButtonDisabled ||
                    (max !== undefined && currentStepValue ? currentStepValue > max - step : false) ||
                    currentStepValue === max },
                React__default.createElement("span", { className: "cb-icon cb-plus", "aria-hidden": "true" }),
                React__default.createElement("span", { className: "sr-only" },
                    "Increase by ",
                    step))),
        validationState && validationMessage && (React__default.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMessage)),
        !validationState && (React__default.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, helperMsg))));
});

const verticalSpacings = ['0', '8', '16', '24', '32', '48', '72'];
const horizontalSpacings = ['0', '4', '8', '16', '24', '32', '72'];

const Spacer = ({ size }) => size === '0' ? null : React__default.createElement("div", { className: size ? `cb-spacerv-${size}` : 'cb-spacerv' });
const SpacerV = ({ size }) => size === '0' ? null : React__default.createElement("div", { className: size ? `cb-spacerv-${size}` : 'cb-spacerv' });
const SpacerH = ({ size }) => React__default.createElement("div", { className: size ? `cb-spacerh-${size}` : 'cb-spacerh' });

const intersperse$1 = (arr, inter) => flatMap(arr, (a, i) => (i ? [inter, a] : [a]));
const RadioButtonContext = React__default.createContext({
    name: '',
    groupLabelPosition: '',
});
// ---------------------------------------- Radio Button Group ----------------------------------------
const RadioButtonGroup = ({ fieldsetId, validationMessageId, legend, legendClassName, legendId, name, required, labelPosition = 'right', children, onChange, value, defaultValue, vertical = false, condensed = false, vSpacing = '16', hSpacing = '16', hasGrid = false, validation, validationMsg, validationMsgRole = 'alert', helperClassName, ariaLive, ariaLabel, ariaLabelledBy, ariaDescribedby, }) => {
    const uid = useUID();
    /* eslint-disable no-param-reassign */
    legendId = legend ? legendId || `apricot_legend_${uid}` : undefined;
    /* eslint-disable no-param-reassign */
    validationMessageId = validationMessageId || `apricot_radio_group_err_${uid}`;
    const spacedChildren = intersperse$1(children, vertical ? React__default.createElement(Spacer, { size: vSpacing }) : React__default.createElement(SpacerH, { size: hSpacing }));
    const withKey = spacedChildren
        .filter(child => isElement(child) || isReactComponent(child))
        .map((child, i) => {
        return cloneElement(child, { key: `key_${uid}${i}` });
    });
    const legendClass = cn('cb-label', validation === 'error' && `cb-validation-${validation}`, required && 'cb-required', legendClassName);
    const helperClass = cn('cb-input-helper', validation === 'error' && `cb-validation-${validation}`, helperClassName);
    const acceptedValidation = validation === 'error' || validation === 'general';
    const getDescribedBy = () => {
        if (validationMsg && acceptedValidation && ariaDescribedby) {
            return `${ariaDescribedby} ${validationMessageId}`;
        }
        return validationMsg && acceptedValidation ? validationMessageId : ariaDescribedby;
    };
    const describedbyValue = (acceptedValidation || ariaDescribedby) && getDescribedBy();
    const labelledBy = ariaLabelledBy || legendId;
    const context = useMemo(() => ({
        name,
        onChange,
        value,
        defaultValue,
        condensed,
        groupLabelPosition: vertical && labelPosition === 'left' ? 'left' : 'right',
    }), [name, value, defaultValue, condensed, vertical, onChange, labelPosition]);
    return (React__default.createElement("fieldset", { id: fieldsetId, "aria-label": ariaLabel || undefined, "aria-labelledby": labelledBy || undefined, 
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
        role: "radiogroup", "aria-required": required, "aria-invalid": validation === 'error', "aria-describedby": describedbyValue || undefined, className: cn(validation === 'error' && `cb-validation-${validation}`) },
        legend && (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("legend", { className: legendClass, id: legendId }, legend),
            React__default.createElement(Spacer, { size: "8" }))),
        React__default.createElement("div", { className: cn(!vertical && 'display-flex flex-row', vertical && labelPosition === 'left' && 'cb-width-100', hasGrid && 'cb-width-100') },
            React__default.createElement(RadioButtonContext.Provider, { value: context }, hasGrid ? children : withKey)),
        (validation === 'error' || validation === 'general') && validationMsg && (React__default.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
};
// ---------------------------------------- Radio Button
const RadioButton = (_a) => {
    var { id, value, label, ariaLabel, ariaDescribedby, labelClassName, labelPosition: radioLabelPosition, disabled = false, className } = _a, props = __rest(_a, ["id", "value", "label", "ariaLabel", "ariaDescribedby", "labelClassName", "labelPosition", "disabled", "className"]);
    const { name, onChange, defaultValue, condensed, value: contextValue, groupLabelPosition, } = useContext(RadioButtonContext);
    const labelPosition = radioLabelPosition || groupLabelPosition;
    const cName = cn('cb-radio', className, disabled && 'cb-disabled', condensed && 'cb-condensed', labelPosition === 'left' && 'cb-label-left');
    const uid = useUID();
    id = id || `apricot_radio_${uid}`;
    // Determine if we're using controlled or uncontrolled behavior
    const isControlled = contextValue !== undefined;
    const inputProps = isControlled
        ? {
            checked: value === contextValue,
        }
        : {
            defaultChecked: value === defaultValue,
        };
    return (React__default.createElement("div", { className: cName, "data-cbtrack-comp": "apricot-react:radio" },
        React__default.createElement("label", { className: labelClassName, htmlFor: id },
            labelPosition === 'left' && label,
            React__default.createElement("input", Object.assign({ id: id, type: "radio", name: name, disabled: disabled, "aria-label": ariaLabel, "aria-describedby": ariaDescribedby }, inputProps, { onChange: e => onChange && onChange(e.target.value, e), value: value }, props)),
            labelPosition !== 'left' && label)));
};

const ToggleSwitch = (_a) => {
    var { switchId, descId, labelId, mainClassName, on, onLabel = 'On', offLabel = 'Off', labelPosition = 'right', ariaLabel, additionalDesc, additionalDescClass, onChange = () => { }, disabled = false, condensed = false, defaultOn } = _a, props = __rest(_a, ["switchId", "descId", "labelId", "mainClassName", "on", "onLabel", "offLabel", "labelPosition", "ariaLabel", "additionalDesc", "additionalDescClass", "onChange", "disabled", "condensed", "defaultOn"]);
    const switchRef = useRef(null);
    const [focused, setFocus] = useState(false);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    switchId = switchId || `apricot_switch_${uid}`;
    // eslint-disable-next-line no-param-reassign
    descId = additionalDesc && (descId || `apricot_des_${uid}`);
    const [uncontrolledOn, setUncontrolledOn] = useState(!!defaultOn);
    const isControlled = typeof on === 'boolean';
    const checked = isControlled ? on : uncontrolledOn;
    const label = onLabel && (React__default.createElement("label", { htmlFor: switchId, id: labelId }, checked ? onLabel : offLabel));
    return (React__default.createElement(React__default.Fragment, null,
        additionalDesc && (React__default.createElement("p", { id: descId, className: additionalDescClass }, additionalDesc)),
        React__default.createElement("div", { className: cn('cb-toggle-switch', condensed && 'cb-condensed', labelPosition === 'left' && 'cb-label-left cb-width-100', mainClassName), "data-cbtrack-comp": "apricot-react:toggles-switch" },
            labelPosition === 'left' && label,
            React__default.createElement("div", { className: cn('cb-switch', checked && 'cb-checked', focused && 'cb-focus', disabled && 'cb-disabled'), ref: switchRef },
                React__default.createElement("div", { className: "cb-switch-track" }),
                React__default.createElement("div", { className: "cb-switch-wrap" },
                    React__default.createElement("div", { className: "cb-switch-thumb" },
                        React__default.createElement("input", Object.assign({ type: "checkbox", id: switchId, role: "switch", disabled: disabled, checked: checked, "aria-checked": checked ? 'true' : 'false', "aria-label": ariaLabel, onBlur: () => {
                                setFocus(false);
                            }, onFocus: () => {
                                setFocus(true);
                            }, "aria-labelledby": descId && `${descId} ${switchId}`, onChange: e => {
                                if (onChange) {
                                    onChange(e.target.checked);
                                }
                                if (!isControlled) {
                                    setUncontrolledOn(e.target.checked);
                                }
                            } }, props))))),
            labelPosition !== 'left' && label)));
};

const ToggleButtonContext = React__default.createContext({
    name: '',
    onChange: () => { },
    defaultValue: '',
    groupValue: '',
});
// ---------------------------------------- Toggle Button Group
const ToggleButtonGroup = ({ toggleButtonId, name, className, children, onChange, defaultValue, groupValue, ariaLabel, ariaLabelledbyID, }) => {
    const context = useMemo(() => ({ name, onChange, defaultValue, groupValue }), [name, onChange, defaultValue, groupValue]);
    return (React__default.createElement("div", { className: cn('cb-toggles-btn', className), id: toggleButtonId, role: "radiogroup", "aria-label": ariaLabel && ariaLabel, "aria-labelledby": ariaLabelledbyID && ariaLabelledbyID, "data-cbtrack-comp": "apricot-react:toggles-btn" },
        React__default.createElement(ToggleButtonContext.Provider, { value: context }, children)));
};
// ---------------------------------------- Toggle Button
const ToggleButton = (_a) => {
    var { buttonId, labelId, value, ariaLabel, icon, children, disabled = false } = _a, props = __rest(_a, ["buttonId", "labelId", "value", "ariaLabel", "icon", "children", "disabled"]);
    const radioRef = useRef(null);
    const context = useContext(ToggleButtonContext);
    return (React__default.createElement("label", { htmlFor: buttonId, id: labelId },
        React__default.createElement("input", Object.assign({ ref: radioRef, id: buttonId, type: "radio", name: context.name, value: value, defaultChecked: value === context.defaultValue, checked: context.groupValue ? value === context.groupValue : undefined, disabled: disabled, onChange: e => context.onChange(e.target.value), "aria-label": ariaLabel }, props)),
        React__default.createElement("span", null, icon ? null : children),
        icon && React__default.createElement(Icon, { name: icon, decorative: true })));
};

const Select = forwardRef((_a, ref) => {
    var { selectId, validationMessageId, name, values, labelNoValue, defaultValue, mainClassName, label, labelId, labelClassName, truncate = true, ariaLabel, ariaDescribedby, floating, floatingBg, condensed, required, disabled, reset, validation, validationMsg, validationMsgRole, helperClassName, ariaLive, onChange, onFocus, onBlur, value, cbRef, 
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    css = '' } = _a, props = __rest(_a, ["selectId", "validationMessageId", "name", "values", "labelNoValue", "defaultValue", "mainClassName", "label", "labelId", "labelClassName", "truncate", "ariaLabel", "ariaDescribedby", "floating", "floatingBg", "condensed", "required", "disabled", "reset", "validation", "validationMsg", "validationMsgRole", "helperClassName", "ariaLive", "onChange", "onFocus", "onBlur", "value", "cbRef", "css"]);
    let apricotSelect;
    const selectRef = useRef(null);
    /**
     * Select changed - pass the update in the callback
     */
    const onSelectChange = () => {
        const elValue = selectRef.current ? selectRef.current.value : '';
        if (labelNoValue && elValue === '') {
            // GS-9131
            // selectElement.selectedIndex = -1;
            if (floating) {
                selectRef.current.selectedIndex = -1;
            }
            else {
                selectRef.current.selectedIndex = 0;
            }
            apricotSelect === null || apricotSelect === void 0 ? void 0 : apricotSelect.change();
        }
        onChange(elValue);
    };
    /**
     * Render Regular options for select box
     * @param options
     */
    const renderOptions = (options) => {
        return options.map((option, index) => {
            return (React__default.createElement("option", { key: index, value: option.value, disabled: option.disabled }, option.label));
        });
    };
    /**
     * Render grouped options for select box
     * @param groupedOptions
     */
    const renderGroupOptions = (groupedOptions) => {
        return groupedOptions.map((options, index) => {
            return (React__default.createElement("optgroup", { key: index, label: options.optgroup }, renderOptions(options.options)));
        });
    };
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    selectId = selectId || `apricot_select_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_select_err_${uid}`;
    const labelClass = cn(required && 'cb-required', floating && floatingBg && `cb-${floatingBg}-bg`, labelClassName);
    const helperClass = cn('cb-input-helper', validation && `cb-validation-${validation}`, helperClassName);
    const getDescribedBy = () => {
        if (validation) {
            if (validationMsg && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMsg ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    const selectClass = cn('cb-select', disabled && 'cb-disabled', floating && 'cb-floating-label', condensed && 'cb-input-condensed', validation && `cb-validation-${validation}`, validation && 'display-block', mainClassName);
    useImperativeHandle(ref, () => selectRef.current);
    const isCalledRef = useRef(false);
    useEffect(() => {
        if (floating)
            CBForm.floatingLabel(selectRef.current);
        if (labelNoValue && !defaultValue && !value) {
            if (floating) {
                selectRef.current.selectedIndex = -1;
            }
            else {
                selectRef.current.selectedIndex = 0;
            }
            apricotSelect === null || apricotSelect === void 0 ? void 0 : apricotSelect.change();
        }
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = apricotSelect;
        }
        return () => {
            // We just want this to run once
            if (isCalledRef.current) {
                apricotSelect === null || apricotSelect === void 0 ? void 0 : apricotSelect.destroy();
            }
            else {
                isCalledRef.current = true;
            }
        };
    }, []);
    useEffect(() => {
        if (reset) {
            if (floating) {
                selectRef.current.selectedIndex = -1;
            }
            else {
                selectRef.current.selectedIndex = 0;
            }
            apricotSelect === null || apricotSelect === void 0 ? void 0 : apricotSelect.change();
        }
    }, [reset]);
    useEffect(() => {
        if (disabled) {
            const event = new CustomEvent('disable');
            selectRef.current.dispatchEvent(event);
        }
        else {
            const event = new CustomEvent('enable');
            selectRef.current.dispatchEvent(event);
        }
    }, [disabled]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: selectClass, "data-cbtrack-comp": "apricot-react:select" },
            label && (React__default.createElement("label", { htmlFor: selectId, className: labelClass, id: labelId }, label)),
            React__default.createElement("select", Object.assign({ ref: el => {
                    if (el) {
                        selectRef.current = el;
                        apricotSelect = CBForm.customSelectElement({
                            elem: el,
                            truncate,
                        });
                        apricotSelect === null || apricotSelect === void 0 ? void 0 : apricotSelect.change();
                    }
                }, id: selectId, name: name, "aria-label": ariaLabel, "aria-describedby": describedbyValue || undefined, "aria-invalid": validation === 'error', disabled: disabled, defaultValue: defaultValue, value: value, onChange: onSelectChange, onFocus: onFocus, onBlur: onBlur, "aria-required": required }, props),
                labelNoValue && React__default.createElement("option", { value: "" }, labelNoValue),
                (values === null || values === void 0 ? void 0 : values.some((item) => item.hasOwnProperty('optgroup')))
                    ? renderGroupOptions(values)
                    : renderOptions(values))),
        validation && validationMsg && (React__default.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
});

/**
 * miscellaneous Divider element
 */
const Divider$1 = () => React__default.createElement("li", { role: "separator", className: "cb-divider" });
/**
 * Select Dropdown Item - supposed to be inside the Select Dropdown component
 */
const SelectDropdownItem = ({ disabled, iconLeft = false, icon = null, label, value, divider, selected, }) => {
    return divider ? (React__default.createElement(Divider$1, null)) : (React__default.createElement("li", { role: "presentation", className: cn(disabled && 'cb-disabled') },
        React__default.createElement("a", { role: "option", href: "/#", "aria-disabled": disabled ? true : undefined, tabIndex: disabled ? -1 : undefined, "aria-selected": selected, className: cn(selected && 'cb-selected'), "data-value": value },
            iconLeft && icon && (typeof icon === 'string' ? React__default.createElement(Icon, { decorative: true, name: icon }) : icon),
            label,
            !iconLeft && icon && (typeof icon === 'string' ? React__default.createElement(Icon, { decorative: true, name: icon }) : icon))));
};

/**
 * Select Dropdown Group Item - supposed to be rendered inside the Select Dropdown component
 */
const SelectDropdownGroup = ({ optgroup, children }) => {
    const seed = useUIDSeed();
    const ID = `apricot_select_${seed('dropdown')}`;
    return (React__default.createElement("li", { role: "none" },
        React__default.createElement("ul", { role: "group", "aria-labelledby": ID },
            React__default.createElement("li", { "aria-hidden": "true", role: "presentation", className: "cb-dropdown-header", tabIndex: -1 },
                React__default.createElement("p", { id: ID }, optgroup)),
            children)));
};

/* eslint-disable max-lines-per-function */
const SelectDropdown = forwardRef(({ selectId, validationMessageId, mainClassName, classNameMenu, defaultValue, value = defaultValue, values, label, labelId, labelClassName, ariaLabel, ariaDescribedby, floating, floatingBg, condensed, required, disabled, reset, validation, validationMsg, validationMsgRole, helperClassName, ariaLive, iconLeft = false, onChange, onFocus, onBlur, cbRef, }, ref) => {
    const CBSelectDropdownRef = useRef(null);
    const selectDivRef = useRef(null);
    const selectButtonRef = useRef(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    selectId = selectId || `apricot_select_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_select_err_${uid}`;
    const dropdownMenuId = `apricot_select_menu_${uid}`;
    const selectClass = cn('cb-select', disabled && 'cb-disabled', floating && 'cb-floating-label', condensed && 'cb-condensed', validation && 'display-block', validation && `cb-validation-${validation}`, mainClassName);
    const labelClass = cn(floating && floatingBg && `cb-${floatingBg}-bg`, required && 'cb-required', labelClassName);
    const helperClass = cn('cb-input-helper', helperClassName, validation && `cb-validation-${validation}`);
    const getDescribedBy = () => {
        if (validation) {
            if (validationMsg && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMsg ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    /**
     * Render Regular options for select dropdown
     * @param options
     */
    const renderOptions = (options) => {
        return options.map((option, index) => {
            return (React__default.createElement(SelectDropdownItem, { key: index, label: option.label, value: option.value, disabled: option.disabled, divider: option.divider, icon: option.icon, iconLeft: true }));
        });
    };
    /**
     * Render grouped options for select dropdown
     * @param groupedOptions
     */
    const renderGroupOptions = (groupedOptions) => {
        return groupedOptions.map((options, index) => {
            return (React__default.createElement(SelectDropdownGroup, { key: index, optgroup: options.optgroup }, options.options && renderOptions(options.options)));
        });
    };
    const onSelectChange = (selValue, selLabel) => {
        // Handle the change event here
        if (onChange)
            onChange(selValue, selLabel);
    };
    useImperativeHandle(ref, () => selectButtonRef.current);
    const isCalledRef = useRef(false);
    useEffect(() => {
        if (floating)
            CBForm.floatingLabel(selectButtonRef.current);
        const selectDropdownPlugin = CBDropdown({
            elem: selectDivRef.current,
            selectDropdown: true,
            closeOnClick: true,
            defaultValue,
            callBack: onSelectChange,
        });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = selectDropdownPlugin;
        }
        CBSelectDropdownRef.current = selectDropdownPlugin;
        return () => {
            // We just want this to run once
            if (isCalledRef.current) {
                selectDropdownPlugin === null || selectDropdownPlugin === void 0 ? void 0 : selectDropdownPlugin.destroy();
            }
            else {
                isCalledRef.current = true;
            }
        };
    }, []);
    // make sure latest data has all the options
    useEffect(() => {
        if (CBSelectDropdownRef.current)
            CBSelectDropdownRef.current.selectDropdownInit();
    }, [values]);
    useEffect(() => {
        if (CBSelectDropdownRef.current)
            CBSelectDropdownRef.current.setValue(value);
    }, [value]);
    useEffect(() => {
        if (reset) {
            if (CBSelectDropdownRef.current)
                CBSelectDropdownRef.current.reset();
        }
    }, [reset]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: selectClass, ref: selectDivRef, "data-cbtrack-comp": "apricot-react:select-dropdown" },
            label && (React__default.createElement("label", { htmlFor: selectId, className: labelClass, id: labelId }, label)),
            React__default.createElement("div", { className: "cb-dropdown cb-dropdown-select" },
                React__default.createElement("button", { ref: selectButtonRef, type: "button", className: "cb-dropdown-toggle", id: selectId, role: "combobox", "aria-expanded": "false", "aria-haspopup": "listbox", "aria-controls": dropdownMenuId, "aria-label": ariaLabel, "aria-labelledby": labelId, "aria-invalid": validation === 'error', "aria-describedby": describedbyValue || undefined, "aria-required": required, "aria-disabled": disabled, tabIndex: disabled ? -1 : undefined, onFocus: (event) => {
                        if (onFocus)
                            onFocus(event);
                    }, onBlur: (event) => {
                        if (onBlur)
                            onBlur(event);
                    } },
                    React__default.createElement("span", { className: "cb-select-dropdown-value" }),
                    React__default.createElement("i", { className: "cb-icon cb-down", "aria-hidden": "true" }),
                    React__default.createElement("i", { className: "cb-icon cb-up cb-hidden", "aria-hidden": "true" })),
                React__default.createElement("div", { className: cn('cb-dropdown-menu', classNameMenu) },
                    React__default.createElement("ul", { role: "listbox", "aria-labelledby": labelId, id: dropdownMenuId, className: cn(iconLeft && 'cb-dropdown-left-icon') }, (values === null || values === void 0 ? void 0 : values.some((item) => Object.prototype.hasOwnProperty.call(item, 'optgroup')))
                        ? renderGroupOptions(values)
                        : renderOptions(values))))),
        validation && validationMsg && (React__default.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
});

// ref is for the wrapping div
const InputDropdown = React__default.forwardRef((_a, ref) => {
    var { labelId, inputId, dropdownId, validationMessageId, ariaDescribedby, type = 'text', label, ariaLabel, className, labelClassName, name, floating = true, floatingBg, condensed = false, disabled = false, clearable = true, required, icon, iconRight = false, dropdownOverlay = false, placeholder, validation, validationMsg, helperClassName, ariaLive, validationMsgRole, inputAutocomplete = 'off', scrollLimit = 0, itemHeight = 40, scrollLimitHelper = false, scrollLimitHelperText = 'There are more results available. Keep typing to refine your search.', scrollLimitHelperClass, dataItems = [], defaultItems = false, itemsText = ['suggestion available', 'suggestions available'], noItemsText = 'no suggestions available', button, buttonOffScreen, onChange = () => { }, onFocus = () => { }, onClick = () => { }, onClear = () => { }, onBlur = () => { }, onKeyUp = () => { }, onKeyDown = () => { }, callBack = () => { }, onBuild = null, defaultValue, value, event, dropdownIcon = false, dropdownIconLeft = false, escClose = true, closeOnClickOutside = true, closeOnSelect = true, cbRef } = _a, props = __rest(_a, ["labelId", "inputId", "dropdownId", "validationMessageId", "ariaDescribedby", "type", "label", "ariaLabel", "className", "labelClassName", "name", "floating", "floatingBg", "condensed", "disabled", "clearable", "required", "icon", "iconRight", "dropdownOverlay", "placeholder", "validation", "validationMsg", "helperClassName", "ariaLive", "validationMsgRole", "inputAutocomplete", "scrollLimit", "itemHeight", "scrollLimitHelper", "scrollLimitHelperText", "scrollLimitHelperClass", "dataItems", "defaultItems", "itemsText", "noItemsText", "button", "buttonOffScreen", "onChange", "onFocus", "onClick", "onClear", "onBlur", "onKeyUp", "onKeyDown", "callBack", "onBuild", "defaultValue", "value", "event", "dropdownIcon", "dropdownIconLeft", "escClose", "closeOnClickOutside", "closeOnSelect", "cbRef"]);
    const inputDropdownRef = useRef(null);
    const inputRef = useRef(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    inputId = inputId || `apricot_input_dropdown_${uid}`;
    // eslint-disable-next-line no-param-reassign
    dropdownId = dropdownId || `apricot_dropdown_${uid}`;
    // eslint-disable-next-line no-param-reassign
    labelId = labelId || `apricot_label_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_input_err_${uid}`;
    useImperativeHandle(ref, () => inputDropdownRef.current);
    useEffect(() => {
        const inputDropdownPlugin = CBForm.inputDropdown({
            elem: inputDropdownRef.current,
            event,
            callBack,
            onBuild,
            clearInput: clearable,
            icon: dropdownIcon,
            iconLeft: dropdownIconLeft,
            escClose,
            closeOnSelect,
            closeOnClickOutside,
            scrollLimit,
            itemHeight,
            scrollLimitHelper,
            scrollLimitHelperText,
            dataItems,
            defaultItems,
            itemsText,
            noItemsText,
        });
        if (floating)
            CBForm.floatingLabel(inputRef.current);
        if (clearable)
            CBForm.clearInput({ elem: inputRef.current });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = inputDropdownPlugin;
        }
        return () => {
            if (inputDropdownPlugin)
                inputDropdownPlugin.destroy();
        };
    }, [disabled]);
    const mainClassName = cn('cb-input', 'cb-input-dropdown', dropdownOverlay && 'cb-input-dropdown-overlay', (!label || (!!label && !floating)) && 'cb-input-no-label', (clearable || button) && 'cb-btn-input', floating && label && 'cb-floating-label', clearable && 'cb-clear-input', condensed && 'cb-input-condensed', disabled && 'cb-disabled', validation && `cb-validation-${validation}`, validation && 'display-block', className);
    const wrapperClassName = cn(icon && `cb-input-icon-${iconRight ? 'right' : 'left'}`);
    const labelClass = cn(required && 'cb-required', floating && floatingBg && `cb-${floatingBg}-bg`, disabled && 'cb-text-disabled', labelClassName);
    const helperClass = cn('cb-input-helper', validation && `cb-validation-${validation}`, helperClassName);
    const getDescribedBy = () => {
        if (validation) {
            if (validationMsg && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMsg ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    return (React__default.createElement(React__default.Fragment, null,
        !!label && !floating && (React__default.createElement("label", { htmlFor: inputId, id: labelId, className: labelClass }, label)),
        React__default.createElement("div", { className: mainClassName, ref: inputDropdownRef, "data-cbtrack-comp": "apricot-react:input-dropdown" },
            React__default.createElement("div", { className: wrapperClassName },
                !!label && floating && (React__default.createElement("label", { htmlFor: inputId, id: labelId, className: labelClass }, label)),
                React__default.createElement("input", Object.assign({ type: type, name: name, autoComplete: inputAutocomplete, ref: inputRef, id: inputId, "aria-label": ariaLabel, "aria-describedby": describedbyValue || undefined, "aria-autocomplete": "list", "aria-controls": dropdownId, "aria-required": required, defaultValue: defaultValue, value: value, role: "combobox", "aria-expanded": "false", "aria-haspopup": "listbox", disabled: disabled, placeholder: placeholder, onChange: onChange, onFocus: onFocus, onBlur: onBlur, onKeyUp: onKeyUp, onKeyDown: onKeyDown }, props)),
                icon && React__default.createElement(Icon, { name: icon, decorative: true }),
                clearable && !button && React__default.createElement(ClearButton, { inputId: inputId, onClear: onClear }),
                button && (React__default.createElement(SquareButton, { onClick: onClick, greyscale: true, icon: button, label: buttonOffScreen || '', "aria-controls": inputId }))),
            React__default.createElement("div", { className: "cb-dropdown-input", "data-cb-input": "dropdown-input" },
                React__default.createElement("span", { role: "status", className: "cb-dropdown-input-items sr-only" }),
                React__default.createElement("div", { className: "cb-dropdown-container" },
                    React__default.createElement("div", { className: "cb-dropdown-menu" },
                        React__default.createElement("ul", { id: dropdownId, role: "listbox", "aria-labelledby": label ? labelId : undefined })),
                    scrollLimitHelper && (React__default.createElement("p", { className: cn('cb-dropdown-helper', scrollLimitHelperClass), "aria-hidden": true }))))),
        validation && validationMsg && (React__default.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
});

const FileUpload = forwardRef((_a, ref) => {
    var { id, name, label = 'Choose File', btnClassName, disabled = false, className, btnType, btnSize = 'sm', btnId, fdbk = true, fdbkPath = false, fdbkMsg = 'No file selected...', fdbkTruncate = true, fdbkMaxChars = 'auto', fdbkRemove = true, fdbkPosition = 'middle', fdbkId, ellipseText = '...', fdbkValidation, onFileSelected, onFileRemoved, cbRef } = _a, props = __rest(_a, ["id", "name", "label", "btnClassName", "disabled", "className", "btnType", "btnSize", "btnId", "fdbk", "fdbkPath", "fdbkMsg", "fdbkTruncate", "fdbkMaxChars", "fdbkRemove", "fdbkPosition", "fdbkId", "ellipseText", "fdbkValidation", "onFileSelected", "onFileRemoved", "cbRef"]);
    const inputEl = useRef(null);
    useImperativeHandle(ref, () => inputEl.current);
    useEffect(() => {
        // initialize the apricot fileUpload plugin
        const apricotFileUpload = CBForm.fileUpload({
            elem: inputEl.current,
            markup: false,
            fdbk,
            fdbkPath,
            fdbkMsg,
            fdbkTruncate,
            fdbkMaxChars,
            fdbkRemove,
            fdbkPosition,
            ellipseText,
            fdbkValidation,
        });
        // custom event when a file has been selected
        inputEl.current.addEventListener('apricot_fileSelected', e => {
            if (onFileSelected)
                onFileSelected(e);
        });
        // custom event when selected file has been removed
        inputEl.current.addEventListener('apricot_fileRemoved', e => {
            if (onFileRemoved)
                onFileRemoved(e);
        });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = apricotFileUpload;
        }
        return () => {
            apricotFileUpload === null || apricotFileUpload === void 0 ? void 0 : apricotFileUpload.destroy();
        };
    }, []);
    const uid = useUID();
    const inputId = id || `apricot_input_file_${uid}`;
    const buttonClassName = cn('cb-file-button', btnType ? `cb-btn cb-btn-${btnType}` : 'cb-btn', btnSize ? `cb-btn-${btnSize}` : null, btnClassName);
    return (React__default.createElement("div", { className: cn('cb-file-upload', className), "data-cbtrack-comp": "apricot-react:file-upload" },
        React__default.createElement("input", Object.assign({ type: "file", ref: inputEl, id: inputId, name: name, disabled: disabled }, props)),
        React__default.createElement("button", { type: "button", className: buttonClassName, disabled: disabled, id: btnId }, label),
        React__default.createElement("span", { id: fdbkId, className: "cb-file-element" })));
});

// ref is for the wrapping div
const InputPhoneInt = React__default.forwardRef((_a, ref) => {
    var { inputId, dropdownId, validationMessageId, countries, XPMAll = false, usTerritories = true, preferredCountries = ['us', 'de'], defaultCountry = 'us', country, defaultValue, value, placeholder = '(###) ###-####', label, labelId, ariaLabel, ariaDescribedby, className, labelClassName, floating = false, condensed = false, disabled = false, disabledDropdown = false, required = false, validation, validationMsg, validationMsgRole, helperClassName, ariaLive, cbRef, maxLength, onCountryChange, onNumberChange, onChange } = _a, props = __rest(_a, ["inputId", "dropdownId", "validationMessageId", "countries", "XPMAll", "usTerritories", "preferredCountries", "defaultCountry", "country", "defaultValue", "value", "placeholder", "label", "labelId", "ariaLabel", "ariaDescribedby", "className", "labelClassName", "floating", "condensed", "disabled", "disabledDropdown", "required", "validation", "validationMsg", "validationMsgRole", "helperClassName", "ariaLive", "cbRef", "maxLength", "onCountryChange", "onNumberChange", "onChange"]);
    const CBInternationalPhoneRef = useRef(null);
    const inputDropdownRef = useRef(null);
    const inputRef = useRef(null);
    const [code, setCode] = useState();
    const [startCursorPosition, setStartCursorPosition] = useState(null);
    const [endCursorPosition, setEndCursorPosition] = useState(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    inputId = inputId || `apricot_input_phone_${uid}`;
    // eslint-disable-next-line no-param-reassign
    dropdownId = dropdownId || `apricot_dropdown_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_input_err_${uid}`;
    useImperativeHandle(ref, () => inputRef.current);
    useEffect(() => {
        const internationalPhonePlugin = CBInternationalPhone({
            elem: inputDropdownRef.current,
            XPMAll,
            usTerritories,
            countries,
            defaultCountry,
            defaultValue,
            preferredCountries,
            placeholder,
            maxLength,
            onCountryChange: (countryCode) => {
                setCode(countryCode);
                if (onCountryChange)
                    onCountryChange(countryCode);
                // to cover change event for input
                const e = new Event('change');
                if (onChange)
                    onChange(e, countryCode);
            },
            onNumberChange,
        });
        if (floating)
            CBForm.floatingLabel(inputRef.current);
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = internationalPhonePlugin;
        }
        CBInternationalPhoneRef.current = internationalPhonePlugin;
        return () => {
            if (internationalPhonePlugin)
                internationalPhonePlugin.destroy();
        };
    }, [disabled, disabledDropdown]);
    useEffect(() => {
        if (CBInternationalPhoneRef && country) {
            CBInternationalPhoneRef.current.updateCountry(country);
        }
    }, [country]);
    useEffect(() => {
        if (CBInternationalPhoneRef && value) {
            CBInternationalPhoneRef.current.updateNumber(value, startCursorPosition, endCursorPosition);
        }
    }, [value]);
    useEffect(() => {
        if (CBInternationalPhoneRef && maxLength) {
            CBInternationalPhoneRef.current.updateMaxLength(maxLength);
        }
    }, [maxLength]);
    const mainClassName = cn('cb-input', 'cb-int-phone', floating && label && 'cb-floating-label', condensed && 'cb-input-condensed', disabled && 'cb-disabled', validation && `cb-validation-${validation}`, validation && 'display-block', className);
    const labelClass = cn(label && required && 'cb-required', labelClassName);
    const helperClass = cn('cb-input-helper', validation && `cb-validation-${validation}`, helperClassName);
    const getDescribedBy = () => {
        if (validation) {
            if (validationMsg && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMsg ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    /**
     * Input changed - pass the update in the callback
     */
    const onInputChange = (e) => {
        var _a, _b;
        setStartCursorPosition((_a = e.target) === null || _a === void 0 ? void 0 : _a.selectionStart);
        setEndCursorPosition((_b = e.target) === null || _b === void 0 ? void 0 : _b.selectionEnd);
        if (onChange)
            onChange(e, code);
    };
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: mainClassName, ref: inputDropdownRef, "data-cbtrack-comp": "apricot-react:int-phone" },
            React__default.createElement("label", { className: labelClass, htmlFor: inputId, id: labelId }, label),
            React__default.createElement("div", { className: "cb-int-phone-container" },
                React__default.createElement("div", { className: "cb-flag-dropdown" },
                    React__default.createElement("a", { href: "/#", className: "cb-flag-selected", id: dropdownId, role: "button", "aria-expanded": "false", "aria-haspopup": "listbox", "aria-controls": inputId, "data-cb-dropdown-disabled": !!disabledDropdown },
                        React__default.createElement("span", { className: "cb-flag" },
                            React__default.createElement("span", { className: "cb-dial-code" }),
                            React__default.createElement("span", { className: "sr-only" })),
                        React__default.createElement("span", { className: "cb-dropdown-toggle" },
                            React__default.createElement("i", { className: "cb-icon cb-down", "aria-hidden": "true" }),
                            React__default.createElement("i", { className: "cb-icon cb-up cb-hidden", "aria-hidden": "true" }))),
                    React__default.createElement("span", { className: "cb-line" }),
                    React__default.createElement("ul", { className: "cb-country-list", role: "listbox" })),
                React__default.createElement("input", Object.assign({ type: "text", id: inputId, "aria-label": ariaLabel, ref: inputRef, disabled: !!disabled, "aria-describedby": describedbyValue || undefined, onChange: onInputChange }, props)))),
        validation && validationMsg && (React__default.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
});

const intersperse = (arr, inter) => flatMap(arr, (a, i) => (i ? [inter, a] : [a]));
const CheckboxContext = React__default.createContext({
    groupLabelPosition: '',
});
const CheckboxGroup = ({ fieldsetId, validationMessageId, ariaDescribedby, legend, legendClassName, legendId, children, required, labelPosition = 'right', vertical = true, vSpacing = '16', hSpacing = '16', hasGrid = false, validation, validationMsg, a11yValidation = false, helperClassName, ariaLive, ariaLabel, validationMsgRole = 'alert', role = 'group', }) => {
    const uid = useUID();
    // if legendId is not provided, generate one for the internal legend otherwise use what's given
    // eslint-disable-next-line no-param-reassign
    legendId = legend ? legendId || `apricot_legend_${uid}` : undefined;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_check_group_err_${uid}`;
    const spacedChildren = intersperse(children, vertical ? React__default.createElement(Spacer, { size: vSpacing }) : React__default.createElement(SpacerH, { size: hSpacing }));
    const withKey = spacedChildren
        .filter(child => isElement(child) || isReactComponent(child))
        .map((child, i) => {
        return cloneElement(child, { key: `key_${uid}${i}` });
    });
    const legendClass = cn('cb-label', validation === 'error' && `cb-validation-${validation}`, required && 'cb-required', legendClassName);
    const helperClass = cn('cb-input-helper', validation === 'error' && `cb-validation-${validation}`, helperClassName);
    const offScreenMsg = [];
    if (required)
        offScreenMsg.push('required');
    if (validation === 'error')
        offScreenMsg.push('invalid');
    const acceptedValidation = validation === 'error' || validation === 'general';
    const getDescribedBy = () => {
        if (validationMsg && acceptedValidation && ariaDescribedby) {
            return `${ariaDescribedby} ${validationMessageId}`;
        }
        return validationMsg && acceptedValidation ? validationMessageId : ariaDescribedby;
    };
    const describedbyValue = (acceptedValidation || ariaDescribedby) && getDescribedBy();
    const context = useMemo(() => ({ groupLabelPosition: vertical && labelPosition === 'left' ? 'left' : 'right' }), [vertical, labelPosition]);
    return (React__default.createElement("fieldset", { id: fieldsetId, "aria-labelledby": legendId, "aria-label": ariaLabel || undefined, className: cn(validation === 'error' && `cb-validation-${validation}`), "aria-describedby": describedbyValue || undefined, role: role || undefined },
        legend && (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("legend", { className: legendClass, id: legendId },
                legend,
                React__default.createElement("span", { className: "sr-only" }, offScreenMsg.join(' - '))),
            React__default.createElement(Spacer, { size: "8" }))),
        React__default.createElement("div", { className: cn(!vertical && 'display-flex flex-row', vertical && labelPosition === 'left' && 'cb-width-100', hasGrid && 'cb-width-100') },
            React__default.createElement(CheckboxContext.Provider, { value: context }, hasGrid ? children : withKey)),
        ((validation === 'error' && validationMsg) || a11yValidation) && (React__default.createElement("p", { id: validationMessageId, className: cn(helperClass, a11yValidation && 'cb-no-margin'), "aria-live": ariaLive, role: validationMsgRole }, !a11yValidation && validationMsg))));
};
const Checkbox = (_a) => {
    var { validationMessageId, ariaDescribedby, label, labelId, ariaLabel, labelClassName, required, labelPosition: checkboxLabelPosition = 'right', disabled, condensed = false, checked, defaultChecked, indeterminate, id, name, className, validation, validationMsg, validationMsgRole, helperClassName, ariaLive, onChange } = _a, props = __rest(_a, ["validationMessageId", "ariaDescribedby", "label", "labelId", "ariaLabel", "labelClassName", "required", "labelPosition", "disabled", "condensed", "checked", "defaultChecked", "indeterminate", "id", "name", "className", "validation", "validationMsg", "validationMsgRole", "helperClassName", "ariaLive", "onChange"]);
    const { groupLabelPosition } = useContext(CheckboxContext);
    const [isIndeterminate, setIsIndeterminate] = useState(indeterminate);
    const labelPosition = checkboxLabelPosition || groupLabelPosition;
    const ref = useCallback((node) => {
        if (node) {
            CBForm.customFormElement({
                elem: node,
                markup: false,
                type: 'checkbox',
                onKeydownReact: true,
            });
        }
    }, []);
    const cName = cn('cb-checkbox', className, disabled && 'cb-disabled', condensed && 'cb-condensed', labelPosition === 'left' && 'cb-label-left', validation === 'error' && `cb-validation-error`);
    const helperClass = cn('cb-input-helper', validation === 'error' && `cb-validation-${validation}`, helperClassName);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    id = id || `apricot_check_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_check_err_${uid}`;
    const acceptedValidation = validation === 'error' || validation === 'general';
    const getDescribedBy = () => {
        if (validationMsg && acceptedValidation && ariaDescribedby) {
            return `${ariaDescribedby} ${validationMessageId}`;
        }
        return validationMsg && acceptedValidation ? validationMessageId : ariaDescribedby;
    };
    const describedbyValue = (acceptedValidation || ariaDescribedby) && getDescribedBy();
    /** Enter key handler */
    const handleKeyDown = (e) => {
        // if it's enter ket
        if (e.key === 'Enter') {
            const target = e.target;
            target.checked = !target.checked;
            if (onChange)
                onChange(target.checked, e);
        }
    };
    useEffect(() => {
        if (indeterminate && id) {
            const elem = document.getElementById(id);
            if (elem) {
                elem.indeterminate = indeterminate || false;
                setIsIndeterminate(indeterminate);
            }
        }
    }, [indeterminate]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: cName, "data-cbtrack-comp": "apricot-react:checkbox" },
            React__default.createElement("label", { id: labelId, className: cn(required && 'cb-required', labelClassName), htmlFor: id },
                labelPosition === 'left' && label,
                React__default.createElement("input", Object.assign({ id: id, name: name, ref: ref, type: "checkbox", checked: checked, defaultChecked: defaultChecked, disabled: disabled, onKeyDown: e => handleKeyDown(e), onChange: e => onChange && onChange(e.target.checked, e), "aria-label": ariaLabel, "aria-invalid": validation === 'error', "aria-describedby": describedbyValue || undefined, "aria-checked": isIndeterminate ? 'mixed' : undefined }, props)),
                labelPosition !== 'left' && label)),
        (validation === 'error' || validation === 'general') && validationMsg && (React__default.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
};

const TextArea = forwardRef((_a, ref) => {
    var { validationMessageId, validationMsgIconOnly = false, label, labelId, ariaLabel, labelClassName, mainClassName, name, rows = 3, floating = true, floatingBg, condensed = false, disabled = false, required, placeholder, validation, validationMsg, validationMsgRole, helperClassName, ariaLive, ariaDescribedby, onChange = () => { }, onFocus = () => { }, onBlur = () => { }, onKeyUp = () => { }, onKeyDown = () => { }, defaultValue, value } = _a, props = __rest(_a, ["validationMessageId", "validationMsgIconOnly", "label", "labelId", "ariaLabel", "labelClassName", "mainClassName", "name", "rows", "floating", "floatingBg", "condensed", "disabled", "required", "placeholder", "validation", "validationMsg", "validationMsgRole", "helperClassName", "ariaLive", "ariaDescribedby", "onChange", "onFocus", "onBlur", "onKeyUp", "onKeyDown", "defaultValue", "value"]);
    const uid = useUID();
    const textAreaId = props.id || `apricot_text_area_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_text_area_err_${uid}`;
    const textAreaRef = useRef(null);
    useImperativeHandle(ref, () => textAreaRef.current);
    useEffect(() => {
        if (floating)
            CBForm.floatingLabel(textAreaRef.current);
    }, [disabled]);
    const textareaClassName = cn('cb-input', 'cb-textarea', floating && label && 'cb-floating-label', condensed && 'cb-input-condensed', disabled && 'cb-disabled', validation && `cb-validation-${validation}`, mainClassName);
    const labelClass = cn(required && 'cb-required', floating && floatingBg && `cb-${floatingBg}-bg`, labelClassName);
    const wrapperClassName = cn(validation && 'cb-validation-label-input');
    const helperClass = cn('cb-input-helper', validation && `cb-validation-${validation}`, helperClassName);
    const getDescribedBy = () => {
        if (validation) {
            if (validationMsg && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMsg ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: textareaClassName, "data-cbtrack-comp": "apricot-react:text-area" },
            React__default.createElement("div", { className: wrapperClassName },
                !!label && (React__default.createElement("label", { htmlFor: textAreaId, className: labelClass, id: labelId }, label)),
                React__default.createElement("textarea", Object.assign({ name: name, ref: textAreaRef, id: textAreaId, rows: rows, onChange: onChange, onFocus: onFocus, onBlur: onBlur, onKeyUp: onKeyUp, onKeyDown: onKeyDown, disabled: disabled, placeholder: placeholder, "aria-required": required, "aria-label": ariaLabel, "aria-describedby": describedbyValue || undefined, defaultValue: defaultValue, value: value, "aria-invalid": validation === 'error' }, props))),
            (validation === 'error' || validation === 'success') && !validationMsgIconOnly && (React__default.createElement(Icon, { className: "cb-validation-icon", name: validation === 'error' ? 'exclamation-fill' : 'check-fill', decorative: true }))),
        validation && validationMsg && (React__default.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
});

/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
const getOptionList = (msOptions) => {
    const newItems = [];
    msOptions.forEach((items) => {
        items.options.forEach((item) => {
            var _a, _b;
            newItems.push(Object.assign(Object.assign({}, item), { groupTitle: (_a = items.optgroup) === null || _a === void 0 ? void 0 : _a.title, groupDesc: (_b = items.optgroup) === null || _b === void 0 ? void 0 : _b.desc }));
        });
    });
    return newItems;
};
const MultiSelect = ({ options, label, labelId, ariaLabel, ariaDescribedby, placeholder, placeholderId, selectId, required, floating = true, fieldName, defaultValue = [], value, inputId, className, filter, filterLabelValue = false, filterPlaceholder, filterAriaLabel, dropdownOverlay = false, reset, disabled, fieldsetId, condensed = false, validation = true, noResultMsg = 'No Results', validationMsg, validationMsgRole, validationMsgIconOnly = false, helperMsg, selectedLabel = 'Selected', closeOnSelect, selectAll, checkboxLabelPosition = 'right', groupedCheckbox = false, groupedDescription = true, maxCheckbox = 100, maxCheckboxText = `{availableOptions} option(s) available. Filter to select individual options.`, tags, tagPosition = 'bottom', tagSmall, tagIcon, onChange, onReset, onSelectAll, checkboxLabelFunc, groupLabelFunc, shadowRoot = false, }) => {
    const [firstRound, setFirstLoad] = useState(true);
    const containerRef = useRef(null);
    const anchorRef = useRef(null);
    const [filterValue, setFilterValue] = useState('');
    const [grouped, setGrouped] = useState(false);
    const [visited, setVisited] = useState(false);
    const [selected, setSelected] = useState(defaultValue || value);
    const [expanded, setExpanded] = useState(false);
    const selectedCount = selected.filter((s) => !s.disabled).length;
    const valid = selected.length || expanded || !visited ? 'general' : 'error';
    const hasError = valid === 'error';
    const uid = useUID();
    const dropdownId = selectId || `apricot_multiselect_${uid}`;
    const dropdownMenuId = `${dropdownId}-menu`;
    const selectedCountId = `${dropdownId}-selectedCount`;
    const fLabelId = labelId || `${dropdownId}-floating`;
    const uid2 = useUID();
    const placeholderSpanId = placeholderId || `apricot_placeholder_${uid2}`;
    const inputMsg = hasError ? validationMsg : helperMsg;
    const msgId = inputMsg ? `${dropdownId}-${valid}-msg` : null;
    const optionList = (options === null || options === void 0 ? void 0 : options.some((item) => Object.prototype.hasOwnProperty.call(item, 'optgroup')))
        ? getOptionList(options)
        : options;
    // Use the type guard in the filter method
    const availableOptions = optionList.filter((o) => {
        if (!filterValue) {
            return true;
        }
        const labelMatches = o.label.toLowerCase().includes(filterValue.toLowerCase());
        const valueMatches = filterLabelValue
            ? String(o.value).toLowerCase().includes(filterValue.toLowerCase())
            : true;
        return filterLabelValue ? labelMatches || valueMatches : labelMatches;
    });
    const filterRequired = availableOptions.length >= maxCheckbox;
    const allSelected = availableOptions.length > 0 &&
        availableOptions.every((o) => selected.find((s) => s.value === o.value));
    const dropdownClasses = cn('cb-multiselect-checkbox', expanded && 'open', selectedCount && 'cb-multiselect-checkbox-selected', validation && 'cb-validation-state', `cb-validation-${valid}`, disabled && 'cb-disabled', dropdownOverlay && 'cb-input-dropdown-overlay');
    const eventFocusOut = () => {
        // when this is in a modal, checking a checkbox fires a focusout event
        // we need to pause before closing until we can determine if the focus is really outside this container
        setTimeout(() => {
            const activeEL = shadowRoot ? CBUtils.getActiveElementShadowRoot() : document.activeElement;
            if (containerRef.current && !containerRef.current.contains(activeEL))
                setExpanded(false);
        }, 200);
    };
    const eventEscape = e => {
        if (e.keyCode === CBUtils.KEYS.ESC) {
            setExpanded(false);
            if (anchorRef.current && anchorRef.current.getAttribute('aria-expanded') === 'true') {
                anchorRef.current.focus();
            }
        }
    };
    const updateSelected = (updates) => {
        setSelected(updates);
        if (onChange)
            onChange(updates);
    };
    const groupBy = (array, key) => {
        // Return the end result
        return array.reduce((rv, x) => {
            // eslint-disable-next-line no-param-reassign
            (rv[x[key]] = rv[x[key]] || []).push(x);
            return rv;
        }, {});
    };
    const getGroup = (group, revert) => {
        const item = availableOptions.filter((item) => {
            if (revert) {
                return (item === null || item === void 0 ? void 0 : item.groupTitle) !== group;
            }
            return (item === null || item === void 0 ? void 0 : item.groupTitle) === group;
        });
        return item;
    };
    const getSelectedByGroup = (group) => {
        return filterValue
            ? selected.filter((item) => (item === null || item === void 0 ? void 0 : item.groupTitle) === group && item.label.toLowerCase().includes(filterValue.toLowerCase())).length
            : selected.filter((item) => (item === null || item === void 0 ? void 0 : item.groupTitle) === group).length;
    };
    // groupedCheckbox: All selected
    const groupIsSelected = (group) => {
        const groupCount = getGroup(group, false).length;
        const groupSelected = getSelectedByGroup(group);
        return groupCount === groupSelected;
    };
    // groupedCheckbox: mix selected
    const groupIsMixed = (group) => {
        const groupCount = getGroup(group, false).length;
        const groupSelected = getSelectedByGroup(group);
        return groupSelected > 0 && groupSelected < groupCount;
    };
    const getDescribedBy = () => {
        if (msgId && ariaDescribedby) {
            return `${ariaDescribedby} ${msgId}`;
        }
        if (ariaDescribedby) {
            return ariaDescribedby;
        }
        return msgId;
    };
    const getLabelledBy = () => {
        if (ariaLabel)
            return null;
        let label = '';
        if (floating)
            label = fLabelId;
        if (expanded || selectedCount > 0)
            label += ` ${selectedCountId}`;
        return label.trim();
    };
    const getAriaLabel = () => {
        if (!ariaLabel)
            return null;
        let label = ariaLabel;
        if (expanded || selectedCount > 0)
            label += ` - ${selectedCount} ${selectedLabel}`;
        return label;
    };
    useEffect(() => {
        if (firstRound) {
            // eslint-disable-next-line no-prototype-builtins
            if (options === null || options === void 0 ? void 0 : options.some((item) => item.hasOwnProperty('optgroup'))) {
                const items = getOptionList(options).filter((item) => {
                    if (value) {
                        return ((value === null || value === void 0 ? void 0 : value.filter((obj) => obj.value === item.value && !obj.disabled).length) >
                            0);
                    }
                    if (defaultValue) {
                        return ((defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.filter((obj) => obj.value === item.value && !obj.disabled).length) > 0);
                    }
                    return false;
                });
                setSelected(items);
            }
            else {
                setSelected(value || defaultValue || []);
            }
        }
        else {
            setSelected(value || []);
        }
        setFirstLoad(false);
    }, [value]);
    useEffect(() => {
        if (expanded) {
            setVisited(true);
        }
    }, [expanded]);
    // A11Y
    useEffect(() => {
        setGrouped(!!(options === null || options === void 0 ? void 0 : options.some((item) => Object.prototype.hasOwnProperty.call(item, 'optgroup'))));
        const container = (containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) || null;
        container === null || container === void 0 ? void 0 : container.addEventListener('focusout', eventFocusOut);
        document.addEventListener('keydown', eventEscape);
        return () => {
            document.removeEventListener('focusout', eventFocusOut);
            document.removeEventListener('keydown', eventEscape);
        };
    }, [options]);
    useEffect(() => {
        if (reset) {
            setSelected([]);
            if (onReset)
                onReset();
        }
    }, [reset]);
    return (React__default.createElement("fieldset", { id: fieldsetId, disabled: disabled, className: cn('cb-multiselect-checkbox-container', condensed && 'cb-condensed', className), "data-cbtrack-comp": "apricot-react:multi-select" },
        tags && tagPosition === 'top' && selected.length > 0 && (React__default.createElement("div", { className: "cb-margin-bottom-32" },
            React__default.createElement(TagSet, { disabled: disabled, tagIcon: tagIcon, small: tagSmall, label: `Selected ${label}`, offScreen: "Remove", tags: selected.filter((s) => !s.disabled).map((s) => s.label), onClick: (tagName, e) => {
                    e.preventDefault();
                    if (!disabled) {
                        const remainingItems = selected.filter((s) => s.label !== tagName && !s.disabled);
                        updateSelected(remainingItems);
                        if (remainingItems.length <= 0) {
                            if (anchorRef.current)
                                anchorRef.current.focus();
                        }
                    }
                } }))),
        React__default.createElement("div", { ref: containerRef },
            !floating &&
                (typeof label === 'string' ? (React__default.createElement("label", { id: fLabelId, htmlFor: dropdownId, className: required ? 'cb-required' : '' }, label)) : (label)),
            React__default.createElement("div", { className: dropdownClasses },
                React__default.createElement("div", { className: cn('flex-grow-1', dropdownOverlay && 'position-relative'), tabIndex: -1 },
                    React__default.createElement("a", { ref: anchorRef, tabIndex: disabled ? -1 : 0, "aria-disabled": disabled, className: "cb-multiselect-checkbox-toggle", id: dropdownId, href: "/#", role: "button", "aria-label": getAriaLabel() || undefined, "aria-expanded": expanded, "aria-controls": dropdownMenuId, "aria-describedby": getDescribedBy() || undefined, "aria-labelledby": getLabelledBy() || undefined, onClick: e => {
                            e.preventDefault();
                            if (!disabled)
                                setExpanded(!expanded);
                        }, onKeyDown: (e) => {
                            const k = e.key;
                            // space
                            if (k === ' ') {
                                e.preventDefault();
                                if (!disabled)
                                    setExpanded(!expanded);
                            }
                        } },
                        floating &&
                            (typeof label === 'string' ? (React__default.createElement("span", { id: fLabelId, className: cn(required ? 'cb-required' : '', 'cb-label') }, label)) : (label)),
                        expanded || selectedCount ? (React__default.createElement("span", { id: selectedCountId, role: "status", className: "cb-font-weight-bold" },
                            selectedCount,
                            " ",
                            selectedLabel)) : (placeholder &&
                            !floating && (React__default.createElement("span", { id: placeholderSpanId, className: "cb-text-placeholder" }, placeholder))),
                        React__default.createElement("span", { className: "cb-multiselect-checkbox-toggle-icon" }, expanded ? (React__default.createElement(Icon, { name: "x-mark", decorative: true, size: "16" })) : (React__default.createElement(Icon, { name: "down", decorative: true })))),
                    expanded ? (React__default.createElement("div", { className: cn('cb-multiselect-checkbox-dropdown', dropdownOverlay && 'cb-has-dropdown-overlay') },
                        filter || filterRequired ? (React__default.createElement("div", { className: "cb-multiselect-filter" },
                            React__default.createElement(Input, { id: inputId, name: `${fieldName}-filter`, ariaLabel: filterAriaLabel || `Filter ${label}`, placeholder: filterPlaceholder || `Filter ${label}`, "aria-controls": dropdownMenuId, iconRight: true, icon: "filter", value: filterValue, onClear: () => setFilterValue(''), onFocus: e => e.target.select(), onChange: e => setFilterValue(e.target.value), disabled: disabled, condensed: condensed }))) : null,
                        React__default.createElement("div", { className: "cb-multiselect-checkbox-list", id: dropdownMenuId, role: "region", "aria-labelledby": dropdownId },
                            selectAll && (React__default.createElement("div", { className: "cb-divider cb-multiselect-checkbox-all" },
                                React__default.createElement(Checkbox, { labelPosition: checkboxLabelPosition, label: React__default.createElement("span", null,
                                        "Select All ",
                                        filterValue && !filterRequired ? 'Filtered' : '',
                                        ' ',
                                        label), disabled: availableOptions.length <= 0, checked: allSelected, condensed: condensed, ariaDescribedby: filterRequired && typeof maxCheckboxText === 'string'
                                        ? `checkboxAllText${uid}`
                                        : undefined, onChange: (checked) => {
                                        if (onSelectAll)
                                            onSelectAll(checked);
                                        updateSelected(checked
                                            ? availableOptions
                                            : selected.filter((s) => !availableOptions.find((o) => o.value === s.value)));
                                    } }))),
                            filterRequired && typeof maxCheckboxText === 'string' ? (React__default.createElement("p", { className: "cb-multiselect-checkbox-all-text", id: `checkboxAllText${uid}` }, availableOptions &&
                                maxCheckboxText.replace('{availableOptions}', availableOptions.length.toString()))) : null,
                            !grouped &&
                                !filterRequired &&
                                availableOptions.map((o, index) => (React__default.createElement(Checkbox, { labelPosition: checkboxLabelPosition, key: `checkbox${selectId}-${index}`, label: checkboxLabelFunc ? checkboxLabelFunc(o.label, o.desc) : o.label, disabled: o.disabled, condensed: condensed, checked: (allSelected && !o.disabled) ||
                                        o.isChecked ||
                                        (Boolean(selected.find((s) => s.value === o.value)) &&
                                            !o.disabled), className: o.className, onChange: (checked) => {
                                        if (o.onChange) {
                                            o.onChange(checked);
                                        }
                                        else {
                                            updateSelected(checked
                                                ? [...selected, o]
                                                : selected.filter((s) => s.value !== o.value));
                                        }
                                        // close when something is checked
                                        if (closeOnSelect)
                                            setExpanded(!expanded);
                                    } }))),
                            grouped &&
                                !filterRequired &&
                                Object.entries(groupBy(availableOptions, 'groupTitle')).map(([key, value], index) => {
                                    var _a, _b, _c, _d;
                                    return (React__default.createElement("fieldset", { key: `group${key}-${index}`, "aria-labelledby": `group${key}-${index}` },
                                        React__default.createElement("div", { className: cn('cb-divider', groupedCheckbox && 'cb-multiselect-grouped-checkbox') },
                                            groupedCheckbox ? (React__default.createElement(Checkbox, { labelPosition: checkboxLabelPosition, label: groupLabelFunc ? (groupLabelFunc(key, (_a = value[0]) === null || _a === void 0 ? void 0 : _a.groupDesc)) : (React__default.createElement("span", null, key)), disabled: value.length <= 0, checked: allSelected || groupIsSelected(key), condensed: condensed, labelId: `checkbox${key}-${index}`, className: cn('cb-checkbox-nested', groupIsMixed(key) && 'cb-checkbox-mixed'), "aria-checked": groupIsMixed(key) ? 'mixed' : undefined, onChange: (checked) => {
                                                    updateSelected(checked
                                                        ? [
                                                            ...new Map([...selected, ...getGroup(key)].map(v => [v.value, v])).values(),
                                                        ]
                                                        : selected.filter((s) => s.groupTitle !== key));
                                                } })) : (React__default.createElement("p", { className: "cb-group-title", id: `group${key}-${index}` }, groupLabelFunc
                                                ? groupLabelFunc(key, (_b = value[0]) === null || _b === void 0 ? void 0 : _b.groupDesc)
                                                : key)),
                                            ((_c = value[0]) === null || _c === void 0 ? void 0 : _c.groupDesc) && groupedDescription && (React__default.createElement("p", { className: "cb-group-desc" }, (_d = value[0]) === null || _d === void 0 ? void 0 : _d.groupDesc)),
                                            React__default.createElement("div", { role: "group", "aria-labelledby": `checkbox${key}-${index}` }, value.map((o, index) => (React__default.createElement(Checkbox, { labelPosition: checkboxLabelPosition, key: `checkbox${selectId}-${index}`, label: checkboxLabelFunc
                                                    ? checkboxLabelFunc(o.label, o.desc)
                                                    : o.label, disabled: o.disabled, condensed: condensed, checked: (allSelected && !o.disabled) ||
                                                    o.isChecked ||
                                                    (Boolean(selected.find((s) => s.value === o.value)) &&
                                                        !o.disabled), className: o.className, onChange: (checked) => {
                                                    if (o.onChange) {
                                                        o.onChange(checked);
                                                    }
                                                    else {
                                                        updateSelected(checked
                                                            ? [...selected, o]
                                                            : selected.filter((s) => s.value !== o.value));
                                                    }
                                                    // close when something is checked
                                                    if (closeOnSelect)
                                                        setExpanded(!expanded);
                                                } })))))));
                                }),
                            React__default.createElement("div", { "aria-live": "polite" }, availableOptions.length <= 0 && React__default.createElement("p", { className: "cb-no-results" }, noResultMsg))))) : null),
                validation && hasError && !validationMsgIconOnly ? (React__default.createElement(Icon, { name: "exclamation", className: "cb-validation-icon", decorative: true })) : (validation && !validationMsgIconOnly && React__default.createElement("i", { className: "cb-validation-icon", "aria-hidden": "true" }))),
            validation && inputMsg ? (React__default.createElement("p", { className: `cb-input-helper cb-validation-${valid}`, id: msgId || '', role: validationMsgRole }, inputMsg)) : null,
            React__default.createElement("span", { role: "status", className: "sr-only" }, expanded &&
                (availableOptions.length <= 0 ? (React__default.createElement(React__default.Fragment, null,
                    "Option list has been filtered, no results out of ",
                    optionList.length,
                    " options")) : (React__default.createElement(React__default.Fragment, null,
                    availableOptions.length,
                    " options are available"))))),
        tags && tagPosition === 'bottom' && selected.length > 0 && (React__default.createElement("div", { className: "cb-margin-top-24" },
            React__default.createElement(TagSet, { disabled: disabled, tagIcon: tagIcon, small: tagSmall, label: `Selected ${label}`, offScreen: "Remove", tags: selected.filter((s) => !s.disabled).map((s) => s.label), onClick: (tagName, e) => {
                    e.preventDefault();
                    if (!disabled) {
                        const remainingItems = selected.filter((s) => s.label !== tagName && !s.disabled);
                        updateSelected(remainingItems);
                        if (remainingItems.length <= 0) {
                            if (anchorRef.current)
                                anchorRef.current.focus();
                        }
                    }
                } })))));
};

const HighlightedString = (_a) => {
    var { sourceString, matcherString } = _a, props = __rest(_a, ["sourceString", "matcherString"]);
    const sourceStr = sourceString !== null && sourceString !== void 0 ? sourceString : '';
    const matcherStr = matcherString ? matcherString === null || matcherString === void 0 ? void 0 : matcherString.trim() : '';
    const firstI = sourceStr === null || sourceStr === void 0 ? void 0 : sourceStr.toLowerCase().indexOf(matcherStr.toLowerCase());
    const initial = sourceStr.slice(0, firstI);
    const last = sourceStr.slice(firstI + matcherStr.length, sourceStr.length);
    const middle = sourceStr.slice(firstI, firstI + matcherStr.length);
    if (firstI === -1 || matcherString === '' || sourceStr === '') {
        return React__default.createElement("span", null, sourceStr);
    }
    return (React__default.createElement("p", Object.assign({}, props),
        initial,
        React__default.createElement("span", { className: "cb-font-weight-bold" }, middle),
        last));
};
const DropdownItem$1 = (_a) => {
    var { label, value, icon, dropDownItemIconLeft = false, searchValue, highlightInputValue, dropDownItemAnchorAttributes, onClickDropdownItem, dropDownItemClassNameFunc, dropDownItemFunc } = _a, props = __rest(_a, ["label", "value", "icon", "dropDownItemIconLeft", "searchValue", "highlightInputValue", "dropDownItemAnchorAttributes", "onClickDropdownItem", "dropDownItemClassNameFunc", "dropDownItemFunc"]);
    const attributesArray = (attributes) => {
        return attributes.reduce((attrib, [aName, aValue]) => (Object.assign(Object.assign({}, attrib), { [aName]: aValue })), {});
    };
    return (React__default.createElement("li", { role: "presentation", className: cn(dropDownItemClassNameFunc && dropDownItemClassNameFunc(label, value)) },
        React__default.createElement("a", Object.assign({}, props, { href: "/#", role: "option", onClick: e => {
                e.preventDefault();
                if (onClickDropdownItem)
                    onClickDropdownItem(e, label, value);
            }, onKeyDown: e => {
                if (onClickDropdownItem)
                    onClickDropdownItem(e, label, value);
            }, className: cn(dropDownItemIconLeft && icon && 'justify-content-start', props.className) }, (dropDownItemAnchorAttributes && attributesArray(dropDownItemAnchorAttributes))), dropDownItemFunc ? (React__default.createElement(React__default.Fragment, null, dropDownItemFunc(label, value, searchValue))) : (React__default.createElement(React__default.Fragment, null,
            dropDownItemIconLeft && icon && React__default.createElement(Icon, { name: icon, decorative: true, className: "cb-margin-right-8" }),
            !highlightInputValue ? (React__default.createElement("span", null, label)) : (React__default.createElement(HighlightedString, { sourceString: label, matcherString: searchValue })),
            !dropDownItemIconLeft && icon && React__default.createElement(Icon, { name: icon, decorative: true }))))));
};

// ref is for the wrapping div
const InputDropdownR = React__default.forwardRef((_a, ref) => {
    var { labelId, inputId, dropdownId, validationMessageId, ariaDescribedby, type = 'text', label, ariaLabel, validationMsgRole, className, labelClassName, mainClassName, name, floating = true, floatingBg, condensed = false, disabled = false, clearable = true, required, icon, iconRight = false, divAttributes, dropdownOverlay = false, placeholder, dataItems = [], defaultItems = [], openOnLoad = false, showNoItemsText = false, noItemsText = 'no suggestions available', srMessageText = ['suggestion available', 'suggestions available'], defaultValue = '', dropDownItemIcon, dropDownItemIconLeft = false, highlightInputValue = false, dropDownItemAnchorAttributes, dropDownItemClassNameFunc, dropDownItemFunc, scrollLimit = 0, itemHeight = 48, scrollLimitHelper = false, scrollLimitHelperLimit = scrollLimit, scrollLimitHelperText = 'There are more results available. Keep typing to refine your search.', scrollLimitHelperClass, reset, escClose = true, closeOnClickOutside = true, closeOnSelect = true, onReset, onChange, onClear, callBack, itemLabelFunc, shadowRoot = false, onOpen, onClose } = _a, props = __rest(_a, ["labelId", "inputId", "dropdownId", "validationMessageId", "ariaDescribedby", "type", "label", "ariaLabel", "validationMsgRole", "className", "labelClassName", "mainClassName", "name", "floating", "floatingBg", "condensed", "disabled", "clearable", "required", "icon", "iconRight", "divAttributes", "dropdownOverlay", "placeholder", "dataItems", "defaultItems", "openOnLoad", "showNoItemsText", "noItemsText", "srMessageText", "defaultValue", "dropDownItemIcon", "dropDownItemIconLeft", "highlightInputValue", "dropDownItemAnchorAttributes", "dropDownItemClassNameFunc", "dropDownItemFunc", "scrollLimit", "itemHeight", "scrollLimitHelper", "scrollLimitHelperLimit", "scrollLimitHelperText", "scrollLimitHelperClass", "reset", "escClose", "closeOnClickOutside", "closeOnSelect", "onReset", "onChange", "onClear", "callBack", "itemLabelFunc", "shadowRoot", "onOpen", "onClose"]);
    const uid = useUID();
    const mainInputRef = useRef(null);
    const inputRef = useRef(null);
    const inputDropdownItemContainerRef = useRef(null);
    const helperTextRef = useRef(null);
    const didMount = React__default.useRef(false);
    // eslint-disable-next-line no-param-reassign
    inputId = inputId || `apricot_input_dropdown_${uid}`;
    // eslint-disable-next-line no-param-reassign
    dropdownId = dropdownId || `apricot_dropdown_${uid}`;
    // eslint-disable-next-line no-param-reassign
    labelId = labelId || `apricot_label_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_input_err_${uid}`;
    const [isOpen, setIsOpen] = useState(false);
    const [clickEvent, setClickEvent] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const [dropDownDataItem, setDropDownDataItem] = useState(defaultItems || dataItems);
    const [inputValue, setInputValue] = useState(defaultValue);
    const [srMessage, setSrMessage] = useState('');
    const [scrollLimitObj, setScrollLimitObj] = useState({});
    const [showHelper, setShowHelper] = useState(false);
    const [helperContainerObj, setHelperContainerObj] = useState({});
    const _clearInputValue = () => {
        setInputValue('');
        setDropDownDataItem([]);
        if (onReset)
            onReset();
        setSrMessage('');
        setClickEvent(false);
        setHasFocus(false);
        // Call the onChange handler if provided
        if (onChange) {
            const syntheticEvent = {
                target: inputRef.current,
                currentTarget: inputRef.current,
                type: 'change',
                bubbles: true,
                cancelable: true,
                preventDefault: () => { },
                stopPropagation: () => { },
            };
            onChange(syntheticEvent, '');
        }
    };
    const _setInputValue = (valueObj) => {
        if (valueObj.label && valueObj.value) {
            setDropDownDataItem([valueObj]);
            setInputValue(valueObj.label);
            setIsOpen(false);
            // Call the onChange handler if provided
            if (onChange) {
                const syntheticEvent = {
                    target: inputRef.current,
                    currentTarget: inputRef.current,
                    type: 'change',
                    bubbles: true,
                    cancelable: true,
                    preventDefault: () => { },
                    stopPropagation: () => { },
                };
                onChange(syntheticEvent, valueObj.label);
            }
            if (callBack)
                callBack(valueObj.label, valueObj.value);
        }
    };
    useImperativeHandle(ref, () => {
        var _a;
        return ({
            value: (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.value,
            clear: () => {
                _clearInputValue();
            },
            getValue: () => inputValue,
            setValue: (valueObj) => _setInputValue(valueObj),
            dispatchEvent: (event) => {
                var _a;
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.dispatchEvent(event);
            },
        });
    }, [inputValue]);
    // open on arrow down
    const inputKeydownEvents = e => {
        // 38: up
        // 40: down
        // 16: shift
        // 9: tab
        const k = e.which || e.keyCode;
        const key = CBUtils.whichKey(e);
        if (key !== 'TAB' && key !== 'DOWN')
            return;
        if (k === 9) {
            setIsOpen(false);
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (k === 40) {
            // Manage Focus
            setIsOpen(true);
            const inputDropdownItemContainer = inputDropdownItemContainerRef.current;
            const inputDropdownItemLi = inputDropdownItemContainer === null || inputDropdownItemContainer === void 0 ? void 0 : inputDropdownItemContainer.querySelectorAll(`#${dropdownId}>li>a`);
            if (inputDropdownItemLi === null || inputDropdownItemLi === void 0 ? void 0 : inputDropdownItemLi.length) {
                let index = 0;
                const itemNode = inputDropdownItemLi[index];
                itemNode.focus();
                Array.prototype.forEach.call(inputDropdownItemLi, (node, i) => {
                    node.addEventListener('keydown', (event) => {
                        var _a, _b;
                        const keyValue = event.key;
                        index = i;
                        // make sure menus are closed after tab away
                        if (keyValue === 'Tab') {
                            // make sure menus are closed after tab away
                            setIsOpen(false);
                            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                            index = 0;
                        }
                        if (!['ArrowUp', 'ArrowDown', 'Shift'].includes(keyValue))
                            return;
                        event.preventDefault();
                        event.stopPropagation();
                        // up/down arrows
                        if (keyValue === 'ArrowUp') {
                            index -= 1; // up
                        }
                        else if (keyValue === 'ArrowDown') {
                            index += 1; // down
                        }
                        if (index < 0 && keyValue === 'ArrowUp') {
                            (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                            index = 0;
                            return;
                        }
                        if (index < 0 || index === inputDropdownItemLi.length) {
                            index = 0;
                        }
                        const newActive = inputDropdownItemLi[index];
                        newActive.focus();
                    });
                });
            }
        }
    };
    const eventEscape = e => {
        // ESC: 27,
        if (e.keyCode === 27) {
            setIsOpen(false);
            if (mainInputRef.current)
                mainInputRef.current.focus();
        }
    };
    const eventFocusOut = () => {
        // we need to pause before closing until we can determine if the focus is really outside this container
        setTimeout(() => {
            const activeEL = shadowRoot ? CBUtils.getActiveElementShadowRoot() : document.activeElement;
            if (mainInputRef.current && !mainInputRef.current.contains(activeEL)) {
                setHasFocus(false);
                setIsOpen(false);
            }
        }, 200);
    };
    const eventFocusIn = () => {
        // we need to pause before closing until we can determine if the focus is really outside this container
        setTimeout(() => {
            const activeEL = shadowRoot ? CBUtils.getActiveElementShadowRoot() : document.activeElement;
            if (mainInputRef.current && !!mainInputRef.current.contains(activeEL)) {
                setHasFocus(true);
            }
        }, 200);
    };
    const handleOnChange = (e, value) => {
        // call users change event/filter
        if (onChange)
            onChange(e, value);
        // it's a controlled component
        if (value !== undefined) {
            setInputValue(value);
        }
    };
    const clickDropdownItem = (e, itemLabel, itemValue) => {
        var _a, _b;
        e.preventDefault();
        setClickEvent(true);
        if (itemValue !== '') {
            // call external function to set input value
            if (itemLabelFunc) {
                setInputValue(itemLabelFunc(e, itemLabel, itemValue));
            }
            else {
                setInputValue(itemLabel);
            }
        }
        if (closeOnSelect)
            setIsOpen(false);
        if (onChange)
            onChange(e, itemLabel);
        if (callBack)
            callBack(itemLabel, itemValue);
        const event1 = new Event('input');
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.dispatchEvent(event1);
        (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
    };
    const debounce = (func, delay) => {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };
    const removeScrollLimitHelper = () => {
        setHelperContainerObj({});
        setShowHelper(false);
    };
    const resizeEventHandler = () => {
        if (helperTextRef.current && scrollLimitHelper && isOpen) {
            if (dataItems.length > scrollLimit) {
                if (dataItems.length > scrollLimitHelperLimit) {
                    setHelperContainerObj({
                        minHeight: `${scrollLimit * itemHeight + CBUtils.getHiddenHeight(helperTextRef.current)}px`,
                        paddingBottom: `${CBUtils.getHiddenHeight(helperTextRef.current)}px`,
                    });
                    setShowHelper(true);
                }
                else {
                    setHelperContainerObj({
                        minHeight: `${scrollLimit * itemHeight}px`,
                        paddingBottom: 0,
                    });
                    setShowHelper(false);
                }
            }
            else {
                const height = dataItems.length ? dataItems.length : 0;
                setHelperContainerObj({
                    minHeight: `${height * itemHeight + 8}px`,
                    paddingBottom: 0,
                });
                setShowHelper(false);
            }
        }
        else {
            removeScrollLimitHelper();
        }
    };
    useEffect(() => {
        const container = (mainInputRef === null || mainInputRef === void 0 ? void 0 : mainInputRef.current) || null;
        container === null || container === void 0 ? void 0 : container.addEventListener('focusin', eventFocusIn);
        if (closeOnClickOutside)
            container === null || container === void 0 ? void 0 : container.addEventListener('focusout', eventFocusOut);
        if (escClose)
            document.addEventListener('keydown', eventEscape);
        if (scrollLimit > 0)
            setScrollLimitObj({ maxHeight: `${scrollLimit * itemHeight}px` });
        return () => {
            container === null || container === void 0 ? void 0 : container.removeEventListener('focusin', eventFocusIn);
            if (closeOnClickOutside)
                container === null || container === void 0 ? void 0 : container.removeEventListener('focusout', eventFocusOut);
            if (escClose)
                document.removeEventListener('keydown', eventEscape);
        };
    }, []);
    useEffect(() => {
        if (didMount.current) {
            if (!dataItems.length) {
                if (showNoItemsText) {
                    setDropDownDataItem([{ label: noItemsText, value: '', noItemText: true }]);
                    setIsOpen(true);
                }
                else {
                    setDropDownDataItem([]);
                    setIsOpen(false);
                }
                if (CBUtils.isBlank(inputValue)) {
                    setSrMessage('');
                }
                else {
                    setSrMessage(noItemsText);
                }
                if (scrollLimitHelper)
                    resizeEventHandler();
            }
            else {
                setDropDownDataItem(dataItems);
                let msg = '';
                msg = dataItems.length === 1 ? srMessageText[0] : srMessageText[1];
                setSrMessage(`${dataItems.length} ${msg}`);
                if (scrollLimitHelper) {
                    if (dataItems.length > scrollLimit)
                        setSrMessage(`${dataItems.length} ${msg}. ${scrollLimitHelperText}`);
                    resizeEventHandler();
                }
                if (!clickEvent)
                    setIsOpen(true);
                setClickEvent(false);
            }
        }
        else {
            if (dropdownOverlay && scrollLimit > 0 && scrollLimitHelper && defaultItems.length > 0) {
                const height = defaultItems.length < scrollLimit ? defaultItems.length : scrollLimit;
                setHelperContainerObj({
                    minHeight: `${height * itemHeight + 8}px`,
                });
            }
            // open onRender
            if (openOnLoad && !!defaultItems.length)
                setIsOpen(true);
            didMount.current = true;
        }
    }, [dataItems]);
    // handle reset
    useEffect(() => {
        if (reset) {
            setInputValue('');
            setDropDownDataItem([]);
            setSrMessage('');
            setClickEvent(false);
            resizeEventHandler();
            if (onReset)
                onReset();
        }
    }, [reset]);
    // handle height adjustment
    useEffect(() => {
        if (scrollLimitHelper) {
            if (isOpen && dataItems.length > 0 && !!inputValue) {
                resizeEventHandler();
            }
            else {
                removeScrollLimitHelper();
            }
        }
        // call onOpen/onClose callbacks
        if (typeof onOpen === 'function' && isOpen) {
            onOpen(inputValue, dropDownDataItem);
        }
        else if (typeof onClose === 'function' && !isOpen) {
            onClose(inputValue, dropDownDataItem);
        }
    }, [isOpen]);
    useEffect(() => {
        if (scrollLimitHelper) {
            const debouncedResizeEventHandler = debounce(resizeEventHandler, 300);
            window.addEventListener('resize', debouncedResizeEventHandler);
            // remove the event on dismount
            return () => {
                window.removeEventListener('resize', debouncedResizeEventHandler);
            };
        }
        return () => {
            window.removeEventListener('resize', resizeEventHandler);
        };
    }, [dataItems, scrollLimitHelper]);
    if (shadowRoot) {
        return (React__default.createElement("div", { "data-cb-input-m": "keyboard" },
            React__default.createElement(Input, Object.assign({}, props, { ref: inputRef, mainRef: mainInputRef, label: label, id: inputId, mainClassName: cn('cb-input-dropdown', clearable && inputValue && hasFocus && 'cb-value-ci cb-focus-elem-ci', !!dropdownOverlay && 'cb-input-dropdown-overlay', !label || (!floating && 'cb-input-no-label'), !disabled && isOpen && 'cb-open', scrollLimit > 0 && 'cb-input-dropdown-scroll', mainClassName), className: className, labelClassName: cn(labelClassName, floating && inputValue && 'cb-value-fl'), name: name, "aria-label": ariaLabel, placeholder: placeholder, "aria-controls": dropdownId, autoComplete: "off", ariaDescribedby: ariaDescribedby, "aria-haspopup": "listbox", iconRight: iconRight, required: required, icon: icon, type: type, disabled: disabled, condensed: condensed, floating: floating, floatingBg: floatingBg, clearable: clearable, divAttributes: divAttributes, onClear: event => {
                    // clear is a change to the value of the input
                    const changeEvent = new Event('change');
                    if (inputRef.current)
                        inputRef.current.dispatchEvent(changeEvent);
                    if (onClear)
                        onClear(event);
                }, value: inputValue, "data-cb-value": inputValue, "cb-value-ci": clearable ? inputValue : undefined, onKeyDown: inputKeydownEvents, onChange: (e, value) => {
                    // if it's a dropdown item
                    if (value) {
                        handleOnChange(e, value);
                    }
                    else {
                        handleOnChange(e, e.target.value);
                    }
                }, validationMessageId: validationMessageId, validationMsgRole: validationMsgRole, inputWithDropdown: true }),
                React__default.createElement("div", { className: "cb-dropdown-input" },
                    React__default.createElement("span", { role: "status", className: "cb-dropdown-input-items sr-only" }, srMessage),
                    React__default.createElement("div", { className: cn('cb-dropdown-container', 
                        // only if we want to show scroll helper text
                        dropdownOverlay &&
                            scrollLimit > 0 &&
                            scrollLimitHelper &&
                            'cb-has-dropdown-overlay'), style: helperContainerObj },
                        React__default.createElement("div", { className: cn('cb-dropdown-menu', showHelper && 'cb-no-box-shadow'), ref: inputDropdownItemContainerRef, style: scrollLimitObj },
                            React__default.createElement("ul", { id: dropdownId, role: "listbox", "aria-labelledby": label ? labelId : undefined }, !disabled &&
                                dropDownDataItem &&
                                dropDownDataItem.map((item, index) => {
                                    return item.noItemText ? (React__default.createElement("li", { className: "cb-dropdown-header" },
                                        React__default.createElement("p", { className: "cb-no-suggestion" }, item.label))) : (React__default.createElement(DropdownItem$1, Object.assign({ key: `menuitem__${index}`, icon: dropDownItemIcon || item.icon, dropDownItemIconLeft: dropDownItemIconLeft, searchValue: inputValue === null || inputValue === void 0 ? void 0 : inputValue.toString(), highlightInputValue: highlightInputValue, onClickDropdownItem: clickDropdownItem, dropDownItemClassNameFunc: dropDownItemClassNameFunc, dropDownItemAnchorAttributes: dropDownItemAnchorAttributes, dropDownItemFunc: dropDownItemFunc }, item)));
                                }))),
                        scrollLimitHelper && (React__default.createElement("p", { className: cn('cb-dropdown-helper', showHelper && 'cb-show', scrollLimitHelperClass), "aria-hidden": "true", ref: helperTextRef }, scrollLimitHelperText)))))));
    }
    return (React__default.createElement(Input, Object.assign({}, props, { ref: inputRef, mainRef: mainInputRef, label: label, id: inputId, mainClassName: cn('cb-input-dropdown', clearable && inputValue && hasFocus && 'cb-value-ci cb-focus-elem-ci', !!dropdownOverlay && 'cb-input-dropdown-overlay', !label || (!floating && 'cb-input-no-label'), !disabled && isOpen && 'cb-open', scrollLimit > 0 && 'cb-input-dropdown-scroll', mainClassName), className: className, labelClassName: cn(labelClassName, floating && inputValue && 'cb-value-fl'), name: name, "aria-label": ariaLabel, placeholder: placeholder, "aria-controls": dropdownId, autoComplete: "off", ariaDescribedby: ariaDescribedby, "aria-haspopup": "listbox", iconRight: iconRight, required: required, icon: icon, type: type, disabled: disabled, condensed: condensed, floating: floating, floatingBg: floatingBg, clearable: clearable, divAttributes: divAttributes, onClear: event => {
            // clear is a change to the value of the input
            const changeEvent = new Event('change');
            if (inputRef.current)
                inputRef.current.dispatchEvent(changeEvent);
            if (onClear)
                onClear(event);
        }, value: inputValue, "data-cb-value": inputValue, "cb-value-ci": clearable ? inputValue : undefined, onKeyDown: inputKeydownEvents, onChange: (e, value) => {
            // if it's a dropdown item
            if (value) {
                handleOnChange(e, value);
            }
            else {
                handleOnChange(e, e.target.value);
            }
        }, validationMessageId: validationMessageId, validationMsgRole: validationMsgRole, inputWithDropdown: true }),
        React__default.createElement("div", { className: "cb-dropdown-input" },
            React__default.createElement("span", { role: "status", className: "cb-dropdown-input-items sr-only" }, srMessage),
            React__default.createElement("div", { className: cn('cb-dropdown-container', 
                // only if we want to show scroll helper text
                dropdownOverlay && scrollLimit > 0 && scrollLimitHelper && 'cb-has-dropdown-overlay'), style: helperContainerObj },
                React__default.createElement("div", { className: cn('cb-dropdown-menu', showHelper && 'cb-no-box-shadow'), ref: inputDropdownItemContainerRef, style: scrollLimitObj },
                    React__default.createElement("ul", { id: dropdownId, role: "listbox", "aria-labelledby": label ? labelId : undefined }, !disabled &&
                        dropDownDataItem &&
                        dropDownDataItem.map((item, index) => {
                            return item.noItemText ? (React__default.createElement("li", { className: "cb-dropdown-header" },
                                React__default.createElement("p", { className: "cb-no-suggestion" }, item.label))) : (React__default.createElement(DropdownItem$1, Object.assign({ key: `menuitem__${index}`, icon: dropDownItemIcon || item.icon, dropDownItemIconLeft: dropDownItemIconLeft, searchValue: inputValue === null || inputValue === void 0 ? void 0 : inputValue.toString(), highlightInputValue: highlightInputValue, onClickDropdownItem: clickDropdownItem, dropDownItemClassNameFunc: dropDownItemClassNameFunc, dropDownItemAnchorAttributes: dropDownItemAnchorAttributes, dropDownItemFunc: dropDownItemFunc }, item)));
                        }))),
                scrollLimitHelper && (React__default.createElement("p", { className: cn('cb-dropdown-helper', showHelper && 'cb-show', scrollLimitHelperClass), "aria-hidden": "true", ref: helperTextRef }, scrollLimitHelperText))))));
});

/* eslint-disable max-lines */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-lines-per-function */
const DatePicker = forwardRef(({ buttonId, datePickerId, shadowRoot = false, palette, placement = 'bottom-start', flipVariations = 'top-start', offset = [0, 8], closeOnClickOutside = true, buttonClassName, datePickerClassName, dateFormat = 'MM/DD/YY', inputDateFormat = 'MM/DD/YYYY', singleDate = false, singleLabel = 'Date', startLabel = 'Start Date', endLabel = 'End Date', firstDay = 7, disableButton = false, disableDates, disableWeekends = false, startDate, endDate, setPreviousDates = false, startInput = null, endInput = null, minDate, maxDate, minDays, maxDays, minPanel = true, lang = 'auto', resetLabel = 'Reset', updateLabel = 'Update', updateClose = true, condensedButtons, interactiveInput = false, inputColClassStart = 'col-sm-4', inputColClassEnd = 'col-sm-4 cb-margin-xs-top-24', floating = true, condensed = false, required = false, defaultValueStartInput, valueStartInput, defaultValueEndInput, valueEndInput, disabledStartInput = false, disabledEndInput = false, startInputID, endInputID, startInputBtnID, endInputBtnID, ariaLabel, ariaLabelledby, ariaLabelPrefix, ariaDescribedby, ariaDescribedbyStartInput, ariaDescribedbyEndInput, validationStartInput, validationMsgStartInput, validationEndInput, validationMsgEndInput, ariaLive, inputMask = true, inputDateMask = '##/##/####', inputValidation = false, helperText = ['Please enter a valid date.', 'The start date must be earlier than the end date.'], footer = true, onSelect, onSelectStart, onSelectEnd, onUpdate, onReset, onShow, onHide, startInputOnChange, endInputOnChange, startInputOnKeyUp, endInputOnKeyUp, inputStartOnChange, inputEndOnChange, cbRef, }, ref) => {
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    buttonId = buttonId || `apricot_datePicker_trigger_${uid}`;
    // eslint-disable-next-line no-param-reassign
    datePickerId = datePickerId || `apricot_datePicker_${uid}`;
    const datePickerRef = useRef(null);
    const buttonRef = useRef(null);
    const idStartInput = startInputID || `apricot_datePicker_start_${uid}`;
    const idEndInput = endInputID || `apricot_datePicker_end_${uid}`;
    const idStartInputBtn = startInputBtnID || `${idStartInput}_btn`;
    const idEndInputBtn = endInputBtnID || `${idEndInput}_btn`;
    useImperativeHandle(ref, () => buttonRef.current);
    useEffect(() => {
        const datePickerPlugin = CBDatePicker({
            elem: buttonRef.current,
            popoverNode: datePickerRef.current,
            shadowRoot,
            placement,
            flipVariations,
            offset,
            closeOnClickOutside,
            dateFormat,
            inputDateFormat,
            singleDate,
            singleLabel,
            startLabel,
            endLabel,
            firstDay,
            disableDates,
            disableWeekends,
            startDate,
            endDate,
            setPreviousDates,
            ariaLabel,
            ariaLabelPrefix,
            hasAriaLabelledby: !!ariaLabelledby,
            startInput: interactiveInput ? idStartInput : startInput,
            endInput: interactiveInput ? idEndInput : endInput,
            startInputHelper: interactiveInput ? `${idStartInput}Msg` : null,
            endInputHelper: interactiveInput ? `${idEndInput}Msg` : null,
            interactiveInput,
            inputMask,
            inputDateMask,
            inputValidation,
            helperText,
            minDate,
            maxDate,
            minDays,
            maxDays,
            minPanel,
            lang,
            onSelect,
            onSelectStart,
            onSelectEnd,
            onUpdate,
            onReset,
            onShow,
            onHide,
            startInputOnChange,
            endInputOnChange,
        });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = datePickerPlugin;
        }
        return () => {
            if (datePickerPlugin) {
                datePickerPlugin.destroy();
            }
        };
    }, []);
    return (React__default.createElement("div", { className: cn(interactiveInput && 'cb-input-date-picker'), "data-cbtrack-comp": "apricot-react:date-picker" },
        interactiveInput && (React__default.createElement("div", { className: "row" },
            React__default.createElement("div", { className: inputColClassStart },
                React__default.createElement(Input, { defaultValue: defaultValueStartInput, value: valueStartInput, id: idStartInput, buttonId: idStartInputBtn, mainClassName: "cb-input-date cb-input-date-start", label: startLabel && startLabel.toLocaleLowerCase() !== 'start date'
                        ? startLabel
                        : singleDate
                            ? 'Date'
                            : 'Start', floating: floating, disabled: disabledStartInput, buttonDisabled: disabledStartInput, condensed: condensed, button: "cal-empty", buttonOffScreen: "Start Date", placeholder: "mm/dd/yyyy", buttonClassName: "cb-date-btn", required: required, clearable: false, "aria-describedby": ariaDescribedbyStartInput || undefined, "data-cb-describedby": ariaDescribedbyStartInput || undefined, ariaLive: ariaLive, validation: validationStartInput, validationMsg: validationMsgStartInput, validationMessageId: `${startInputID}Msg`, onKeyUp: startInputOnKeyUp, onChange: inputStartOnChange }),
                inputValidation && React__default.createElement("p", { className: "cb-input-helper cb-hidden", id: `${startInputID}Msg` })),
            !singleDate && (React__default.createElement("div", { className: inputColClassEnd },
                React__default.createElement(Input, { defaultValue: defaultValueEndInput, value: valueEndInput, id: idEndInput, buttonId: idEndInputBtn, mainClassName: "cb-input-date cb-input-date-end", label: endLabel && endLabel.toLocaleLowerCase() !== 'end date' ? endLabel : 'End', floating: floating, disabled: disabledEndInput, buttonDisabled: disabledEndInput, condensed: condensed, button: "cal-empty", buttonOffScreen: "End Date", placeholder: "mm/dd/yyyy", buttonClassName: "cb-date-btn", required: required, clearable: false, "aria-describedby": ariaDescribedbyEndInput || undefined, "data-cb-describedby": ariaDescribedbyEndInput || undefined, ariaLive: ariaLive, validation: validationEndInput, validationMsg: validationMsgEndInput, validationMessageId: `${endInputID}Msg`, onKeyUp: endInputOnKeyUp, onChange: inputEndOnChange }),
                inputValidation && React__default.createElement("p", { className: "cb-input-helper cb-hidden", id: `${endInputID}Msg` }))))),
        !interactiveInput && (React__default.createElement("a", { ref: buttonRef, href: "/#", role: "button", className: cn('cb-btn', 'cb-btn-filter', 'cb-btn-date-picker', disableButton && 'cb-disabled', buttonClassName, palette && `cb-palette-${palette}`, condensedButtons && 'cb-btn-sm'), id: buttonId, "aria-disabled": disableButton ? 'true' : 'false', "aria-expanded": "false", "aria-describedby": ariaDescribedby || undefined, "aria-labelledby": ariaLabelledby || undefined },
            React__default.createElement("span", { className: "cb-icon cb-cal-full", "aria-hidden": true }),
            singleDate ? (React__default.createElement("span", { className: "cb-date-picker-start" }, singleLabel)) : (React__default.createElement(React__default.Fragment, null,
                React__default.createElement("span", { className: "cb-date-picker-start" }, startLabel),
                React__default.createElement("span", { className: "cb-date-picker-end" }, endLabel))))),
        React__default.createElement("div", { className: cn('cb-popover', 'cb-date-picker', datePickerClassName), role: "region", id: datePickerId, "aria-hidden": "true", ref: datePickerRef },
            React__default.createElement("div", { className: "cb-popover-inner" },
                React__default.createElement("div", { className: "cb-popover-header" },
                    React__default.createElement("p", { className: "sr-only", role: "status" }, singleDate ? 'Select Date' : 'Select Start Date and End Date'),
                    React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-square cb-btn-sm cb-prev-month" },
                        React__default.createElement("span", { className: "cb-icon cb-left", "aria-hidden": "true" }),
                        React__default.createElement("span", { className: "sr-only" }, "Previous Month")),
                    React__default.createElement("h3", { className: "cb-date-picker-month" },
                        React__default.createElement("span", { role: "status" },
                            React__default.createElement("span", { className: "cb-month-label" }),
                            React__default.createElement("span", { className: "cb-year-label" }))),
                    !singleDate && (React__default.createElement("h3", { className: "cb-date-picker-month" },
                        React__default.createElement("span", { role: "status" },
                            React__default.createElement("span", { className: "cb-month-label" }),
                            React__default.createElement("span", { className: "cb-year-label" })))),
                    React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-square cb-btn-sm cb-next-month" },
                        React__default.createElement("span", { className: "cb-icon cb-right", "aria-hidden": "true" }),
                        React__default.createElement("span", { className: "sr-only" }, "Next Month"))),
                React__default.createElement("div", { className: "cb-popover-content" },
                    React__default.createElement("div", { className: "cb-month-container" })),
                footer && (React__default.createElement("div", { className: "cb-popover-footer" },
                    React__default.createElement(NakedButton, { className: cn('cb-filter-reset', condensedButtons && 'cb-btn-sm'), noPadding: true, onClick: onReset }, resetLabel),
                    React__default.createElement(PrimaryButton, { className: cn('cb-filter-update', condensedButtons && 'cb-btn-sm'), onClick: onUpdate, "data-cb-popover-close": updateClose || null, disabled: true }, updateLabel)))))));
});

const Dropdown = ({ dropdownId, dropdownMenuId, selectListLabelId, disabled, caret = true, title = 'Menu List', titleIcon, closeOnClick = false, closeOnClickOutside = true, className, classNameMenu, classNameToggle, iconLeft = false, selectList = false, ariaLabel, condensed = false, callBack, cbRef, children, }) => {
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    dropdownId = dropdownId || `apricot_dropdown_${uid}`;
    // eslint-disable-next-line no-param-reassign
    dropdownMenuId = dropdownMenuId || `apricot_dropdown_menu_${uid}`;
    const dropdownSelectedValueId = `apricot_dropdown_select_${uid}`;
    const dropdownRef = useRef(null);
    useEffect(() => {
        const dropdownPlugin = CBDropdown({
            elem: dropdownRef.current,
            closeOnClick,
            closeOnClickOutside,
            selectList,
            callBack,
        });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = dropdownPlugin;
        }
        return () => {
            if (dropdownPlugin) {
                dropdownPlugin.destroy();
            }
        };
    }, [children]);
    return (React__default.createElement("div", { className: cn('cb-dropdown', disabled && 'cb-disabled', selectList && 'cb-dropdown-select', condensed && 'cb-dropdown-condensed', className), "data-cbtrack-comp": "apricot-react:dropdown", ref: dropdownRef },
        React__default.createElement("a", { className: cn('cb-dropdown-toggle', classNameToggle), href: "/#", id: dropdownId, role: "button", "aria-expanded": "false", "aria-haspopup": "true", "aria-controls": dropdownMenuId, "aria-disabled": disabled ? true : undefined, tabIndex: disabled ? -1 : undefined, "aria-labelledby": selectList && selectListLabelId ? `${dropdownSelectedValueId} ${selectListLabelId}` : undefined, "aria-label": ariaLabel },
            titleIcon && React__default.createElement(Icon, { decorative: true, name: titleIcon }),
            !selectListLabelId && React__default.createElement("span", { className: "sr-only" }),
            React__default.createElement("span", { id: dropdownSelectedValueId }, title),
            caret && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement("i", { className: "cb-icon cb-down", "aria-hidden": "true" }),
                React__default.createElement("i", { className: "cb-icon cb-up cb-hidden", "aria-hidden": "true" })))),
        React__default.createElement("div", { className: cn('cb-dropdown-menu', 'cb-box-shadow', classNameMenu) },
            React__default.createElement("ul", { id: dropdownMenuId, role: "menu", "aria-labelledby": selectList ? dropdownSelectedValueId : dropdownId, className: cn(iconLeft && 'cb-dropdown-left-icon') }, children))));
};

/**
 * miscellaneous Divider element
 */
const Divider = () => React__default.createElement("li", { role: "separator", className: "cb-divider" });
const isIcon = (x) => iconNames.includes(x);
/**
 * Dropdown Item - supposed to be inside the Dropdown element
 */
const DropdownItem = (_a) => {
    var { dropdownItemId, disabled, onClick, iconLeft, icon = null, label, divider, selected } = _a, props = __rest(_a, ["dropdownItemId", "disabled", "onClick", "iconLeft", "icon", "label", "divider", "selected"]);
    /**
     * Default Item on click event handler - check the prop and call the callback if given
     */
    const onItemClick = e => {
        e.preventDefault();
        if (onClick)
            onClick();
    };
    const IconComponent = isIcon(icon) && React__default.createElement(Icon, { decorative: true, name: icon });
    return divider ? (React__default.createElement(Divider, null)) : (React__default.createElement("li", { role: "presentation", className: cn(disabled && 'cb-disabled') },
        React__default.createElement("a", Object.assign({ id: dropdownItemId, role: "menuitem", href: "/#", "aria-disabled": disabled ? true : undefined, tabIndex: disabled ? -1 : undefined, onClick: onItemClick, className: cn(selected && 'cb-selected') }, props),
            iconLeft && icon && (typeof icon === 'string' ? IconComponent : icon),
            label,
            !iconLeft && icon && (typeof icon === 'string' ? IconComponent : icon))));
};

/**
 * Dropdown Group Item - supposed to be rendered inside the Dropdown control
 */
const DropdownGroup = ({ label, children }) => {
    const seed = useUIDSeed();
    const ID = `apricot_${seed('dropdown')}`;
    return (React__default.createElement("li", { role: "none" },
        React__default.createElement("ul", { role: "group", "aria-labelledby": ID },
            React__default.createElement("li", { "aria-hidden": "true", role: "presentation", className: "cb-dropdown-header", tabIndex: -1 },
                React__default.createElement("p", { id: ID }, label)),
            children)));
};

const layout = ['horizontal', 'vertical'];

const EventTimeline = ({ layout: controlLayout, ariaLabel, children }) => {
    const eventRef = useRef(null);
    useEffect(() => {
        const eventTimeline = CBEventTimeline({
            elem: eventRef.current,
        });
        return () => {
            if (eventTimeline) {
                eventTimeline.destroy();
            }
        };
    }, []);
    return (React__default.createElement("div", { className: cn('cb-event-timeline', `cb-event-cards-${controlLayout}`), role: "region", "aria-label": ariaLabel, "data-cbtrack-comp": "apricot-react:event-timeline", ref: eventRef },
        React__default.createElement("ol", { className: "cb-gutterh-24" }, children)));
};

const popoverFilterPlacement = ['bottom-start', 'top-start'];
const filterPalette = ['blue5', 'blue2', 'purple1'];

const FilterFooter = ({ clearButtonId, updateButtonId, clearLabel = 'Clear', updateLabel = 'Save', disabledClear, disabledUpdate, updateClose = true, onClear, onUpdate, }) => {
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(NakedButton, { id: clearButtonId, className: "cb-filter-reset", noPadding: true, onClick: onClear, disabled: disabledClear }, clearLabel),
        React__default.createElement(PrimaryButton, { id: updateButtonId, className: "cb-filter-update", onClick: onUpdate, disabled: disabledUpdate, "data-cb-popover-close": updateClose || null }, updateLabel)));
};
const Filter = forwardRef((_a, ref) => {
    var { buttonId, filterId, filterHeaderId, shadowRoot = false, buttonLabel = 'Filter', palette, popoverTitle = null, closeButton = true, filterFooter, activeFilters, closeButtonLabel = 'Close Popover', placement = 'bottom-start', closeOnClickOutside = true, disableHeightAdjustment = false, disableHeightAdjustmentAria = true, buttonClassName, filterClassName, onShow, onHide, cbRef, children } = _a, props = __rest(_a, ["buttonId", "filterId", "filterHeaderId", "shadowRoot", "buttonLabel", "palette", "popoverTitle", "closeButton", "filterFooter", "activeFilters", "closeButtonLabel", "placement", "closeOnClickOutside", "disableHeightAdjustment", "disableHeightAdjustmentAria", "buttonClassName", "filterClassName", "onShow", "onHide", "cbRef", "children"]);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    buttonId = buttonId || `apricot_filter_trigger_${uid}`;
    // eslint-disable-next-line no-param-reassign
    filterId = filterId || `apricot_filter_${uid}`;
    // eslint-disable-next-line no-param-reassign
    filterHeaderId = filterHeaderId || `apricot_popover_header_${uid}`;
    const filterRef = useRef(null);
    const buttonRef = useRef(null);
    const [btnLabel, setLabel] = useState(buttonLabel);
    useImperativeHandle(ref, () => buttonRef.current);
    useEffect(() => {
        const filterPlugin = CBFilter({
            elem: buttonRef.current,
            popoverNode: filterRef.current,
            closeOnClickOutside,
            disableHeightAdjustment,
            disableHeightAdjustmentAria,
            placement,
            shadowRoot,
            onShow,
            onHide,
        });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = filterPlugin;
        }
        return () => {
            if (filterPlugin) {
                filterPlugin.destroy();
            }
        };
    }, []);
    useEffect(() => {
        if (activeFilters && activeFilters > 0) {
            setLabel(`${buttonLabel} (${activeFilters})`);
        }
        else {
            setLabel(buttonLabel);
        }
    }, [activeFilters]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("button", Object.assign({ id: buttonId, type: "button", className: cn('cb-btn', 'cb-btn-filter', activeFilters && activeFilters > 0 && 'cb-filter-active', buttonClassName, palette && `cb-palette-${palette}`), "data-cbtrack-comp": "apricot-react:filter" }, props, { ref: buttonRef }), btnLabel),
        React__default.createElement("div", { className: cn('cb-popover', 'cb-filter', filterClassName), role: "region", id: filterId, "aria-hidden": "true", ref: filterRef },
            React__default.createElement("div", { className: "cb-popover-inner" },
                popoverTitle && (React__default.createElement("div", { className: cn('cb-popover-header', closeButton && 'cb-popover-close') },
                    React__default.createElement("h3", { className: "cb-popover-title", id: filterHeaderId }, popoverTitle),
                    closeButton && (React__default.createElement(CloseButton, { "aria-describedby": filterHeaderId, label: closeButtonLabel, "data-cb-popover-close": true })))),
                React__default.createElement("div", { className: "cb-popover-content" }, children),
                filterFooter && React__default.createElement("div", { className: "cb-popover-footer" }, filterFooter)))));
});

const carouselVariants = ['light', 'dark', 'menu', 'menu-condensed'];

const HorizontalCarousel = ({ carouselId, children, ariaLabel, variant, bgClass, arrowClass, scrollDistance = 150, selectedClass, contentClass, itemClass, className, }) => {
    const carouselClassName = cn(`cb-horizontal-carousel`, variant && `cb-horizontal-carousel-${variant}`, className);
    const ref = useRef(null);
    useEffect(() => {
        CBHorizontalCarousel({
            elem: ref.current,
            markup: false,
            scrollDistance,
            selectedClass,
            itemClass,
        });
    }, []);
    return (React__default.createElement("div", { id: carouselId, role: "region", "aria-label": ariaLabel, className: carouselClassName, "data-cbtrack-comp": "apricot-react:horizontal-carousel", ref: ref },
        React__default.createElement("div", { className: "cb-horizontal-nav cb-horizontal-nav-left" },
            React__default.createElement("a", { "aria-label": "scroll content to left", role: "button", href: "/#", tabIndex: -1, "aria-disabled": "true", className: cn(bgClass && bgClass, arrowClass && arrowClass) },
                React__default.createElement("span", { className: cn(`cb-icon`, !variant && `cb-left`, variant && `cb-carousel-backward`), "aria-hidden": "true" }))),
        React__default.createElement("div", { className: "cb-horizontal-carousel-nav" },
            React__default.createElement("div", { className: cn('cb-horizontal-carousel-content', contentClass) }, children)),
        React__default.createElement("div", { className: "cb-horizontal-nav cb-horizontal-nav-right" },
            React__default.createElement("a", { "aria-label": "scroll content to right", href: "/#", role: "button", tabIndex: -1, "aria-disabled": "true", className: cn(bgClass, arrowClass) },
                React__default.createElement("span", { className: cn(`cb-icon`, !variant && `cb-right`, variant && `cb-carousel-forward`), "aria-hidden": "true" })))));
};

const hLevel = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
const skipEnrollOpt = ['Y', ''];
const identityCardPalette = ['blue5', 'blue2', 'purple1'];

const useLoginStatus = () => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const getIsLoggedIn = () => {
        const { isLoggedIn: authLoggedIn } = window.cb.core.iam.getAuthSession();
        setIsLoggedIn(authLoggedIn);
    };
    useEffect(() => {
        window.cb.core.iam.getEventBus().on('cb:core:iam:events:Login', () => {
            getIsLoggedIn();
        });
        window.cb.core.iam.getEventBus().on('cb:core:iam:events:Logout', () => {
            setIsLoggedIn(false);
        });
    }, []);
    useEffect(() => {
        getIsLoggedIn();
    }, [window.cb.core.iam]);
    return {
        isLoggedIn,
    };
};

const IdentityCardHeading = (_a) => {
    var { headingLevel, children } = _a, props = __rest(_a, ["headingLevel", "children"]);
    const Heading = headingLevel;
    return React__default.createElement(Heading, Object.assign({}, props), children);
};
const IdentityCard = (_a) => {
    var { loginUrl = 'https://account.collegeboard.org/login/login', createAccountUrl = 'https://account.collegeboard.org/login/signUp', appId, destinationUrl, isStudent, skipEnroll, loggedInComponent = null, className, topIcon = true, headingLevel = 'h2', headingTitle = 'Sign In', topDesc = 'Welcome to College Board', primaryBtnLabel = 'Continue', bottomIcon = true, secondaryBtnLabel = 'Create Account', bottomDesc = React__default.createElement(React__default.Fragment, null, "Don't have an account?"), palette } = _a, props = __rest(_a, ["loginUrl", "createAccountUrl", "appId", "destinationUrl", "isStudent", "skipEnroll", "loggedInComponent", "className", "topIcon", "headingLevel", "headingTitle", "topDesc", "primaryBtnLabel", "bottomIcon", "secondaryBtnLabel", "bottomDesc", "palette"]);
    let oktaUrl = `${loginUrl}?appId=${appId}`;
    if (destinationUrl) {
        oktaUrl += `&DURL=${encodeURI(destinationUrl)}`;
    }
    if (skipEnroll) {
        oktaUrl += `&skipEnroll=${skipEnroll}`;
    }
    oktaUrl += `${isStudent !== undefined ? `&idp=${isStudent ? 'ECL' : 'EPL'}` : ''}`;
    const { isLoggedIn } = useLoginStatus();
    const anchorRef = useRef(null);
    const btnRef = useRef(null);
    const cardClass = cn('cb-card', 'cb-identity-card', 'cb-align-center', className);
    const a11YKeydown = (e, node) => {
        var _a;
        const k = e.code;
        // space or enter
        if (k === ' ' || k === 'Enter') {
            e.preventDefault();
            if (node)
                (_a = node.current) === null || _a === void 0 ? void 0 : _a.click();
        }
    };
    return (React__default.createElement(React__default.Fragment, null, isLoggedIn ? (loggedInComponent) : (React__default.createElement("div", Object.assign({ className: cardClass, "data-cbtrack-comp": "apricot-react:card" }, props),
        React__default.createElement("div", { className: "cb-identity-card-container" },
            topIcon && (React__default.createElement("span", { className: cn('cb-icon', 'cb-user', 'cb-icon-64', 'cb-icon-circular', 'cb-margin-bottom-24', `cb-${palette || 'blue5'}-color`), "aria-hidden": "true" })),
            React__default.createElement(IdentityCardHeading, { headingLevel: headingLevel, className: "cb-h3 cb-margin-bottom-24" }, headingTitle),
            React__default.createElement("p", { className: " cb-margin-bottom-24 cb-paragraph1-st cb-align-center" }, topDesc),
            React__default.createElement("a", { ref: btnRef, href: oktaUrl, role: "button", className: "cb-btn cb-btn-primary", onKeyDown: e => a11YKeydown(e, btnRef) }, primaryBtnLabel),
            createAccountUrl && (React__default.createElement("div", { className: "cb-margin-top-32 cb-padding-top-32 cb-border-top-1 cb-border-solid cb-gray8-border" },
                bottomIcon && (React__default.createElement("span", { className: cn('cb-icon', 'cb-user-add', 'cb-icon-64', 'cb-icon-circular', 'cb-margin-bottom-24', `cb-${palette || 'blue5'}-color`), "aria-hidden": "true" })),
                React__default.createElement("p", { className: "cb-h6 cb-align-center cb-margin-bottom-24" }, bottomDesc),
                React__default.createElement("a", { ref: anchorRef, href: createAccountUrl, className: "cb-btn cb-btn-naked", role: "button", onKeyDown: e => a11YKeydown(e, anchorRef) }, secondaryBtnLabel))))))));
};

const LogoutButton = (_a) => {
    var { logoutUrl = 'https://account.collegeboard.org/login/logout', isStudent, returnUrl, appId, label = 'Sign Out', className } = _a, props = __rest(_a, ["logoutUrl", "isStudent", "returnUrl", "appId", "label", "className"]);
    const onButtonClick = () => {
        window.location.href = `${logoutUrl}?appId=${appId}${returnUrl ? `&DURL=${encodeURI(returnUrl)}` : ''}${isStudent !== undefined ? `&idp=${isStudent ? 'ECL' : 'EPL'}` : ''}`;
    };
    return (React__default.createElement("button", Object.assign({ type: "button", onClick: onButtonClick, className: `cb-btn ${className}` }, props), label));
};

const markerTypes = ['dash', 'checkmark', 'checkbox', 'numeric', 'numeric-icon', 'reset'];

const List = ({ children, marker, flush, feature, listId, className }) => {
    const Tag = marker && marker.startsWith('numeric') ? 'ol' : 'ul';
    const textListClass = (marker &&
        {
            dash: 'cb-text-list-dash',
            checkmark: 'cb-text-list-checkmark',
            checkbox: 'cb-text-list-checkbox',
            'numeric-icon': 'cb-text-list-order',
            reset: 'cb-no-text-list',
        }[marker]) ||
        'cb-text-list';
    return (React__default.createElement(Tag, { id: listId, className: cn(textListClass, flush && 'cb-text-list-no-indent', (marker === 'checkmark' || marker === undefined) && feature && 'cb-text-list-feature', className), "data-cbtrack-comp": "apricot-react:list" }, children));
};

const CSSTransition = ({ children, shouldMount = true, timeout = 500, className = 'cb-css-transition' }) => (React__default.createElement(CSSTransition$2, { in: shouldMount, unmountOnExit: true, timeout: timeout, classNames: className }, children));
const BandLoader = ({ loaderId, show = true, ariaLabel = 'Loading content...', background = '', light = false, timeout = 500, transitionClassName = 'cb-css-transition', }) => {
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    loaderId = loaderId || `apricot_loader_${uid}`;
    const wrapperClassName = cn(`cb-loader-shimmer`, `cb-loader-band`, light && `cb-loader-light`, `cb-${background}-bg`);
    return (React__default.createElement(CSSTransition, { shouldMount: show, timeout: timeout, className: transitionClassName },
        React__default.createElement("div", { role: "status", "aria-labelledby": loaderId, "data-cbtrack-comp": "apricot-react:loader" },
            React__default.createElement("div", { className: wrapperClassName },
                React__default.createElement("div", { className: "container cb-padding-top-72 cb-padding-bottom-72" },
                    React__default.createElement("div", { className: "row" },
                        React__default.createElement("div", { className: "col-lg-10 offset-lg-1 col-xl-8 offset-xl-2", id: loaderId, role: "img", "aria-label": ariaLabel },
                            React__default.createElement("div", { className: "cb-line-24 cb-line-xl-up-32" }),
                            React__default.createElement("div", { className: "cb-margin-top-16 cb-line-48 cb-line-xl-up-72 cb-line-xs-32" }),
                            React__default.createElement("div", { className: "cb-margin-top-32 cb-margin-md-top-48 cb-line-16" }),
                            React__default.createElement("div", { className: "cb-margin-top-16 cb-no-margin-xs-bottom cb-margin-sm-bottom-16 cb-margin-bottom-8 cb-line-16" }))))))));
};
const HeroCenterLoader = ({ loaderId, show = true, ariaLabel = 'Loading content...', background = '', light = false, timeout = 500, transitionClassName = 'cb-css-transition', }) => {
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    loaderId = loaderId || `apricot_loader_${uid}`;
    const wrapperClassName = cn(`cb-loader-shimmer`, `cb-loader-hero-band`, light && `cb-loader-light`, `cb-${background}-bg`);
    return (React__default.createElement(CSSTransition, { shouldMount: show, timeout: timeout, className: transitionClassName },
        React__default.createElement("div", { role: "status", "aria-labelledby": loaderId, "data-cbtrack-comp": "apricot-react:loader" },
            React__default.createElement("div", { className: wrapperClassName },
                React__default.createElement("div", { className: "container cb-padding-top-72 cb-padding-bottom-72" },
                    React__default.createElement("div", { className: "row" },
                        React__default.createElement("div", { className: "col-xs-12 col-sm-8 offset-sm-2 col-xl-6 offset-xl-3" },
                            React__default.createElement("div", { className: "display-flex justify-content-center flex-column", id: loaderId, role: "img", "aria-label": ariaLabel },
                                React__default.createElement("div", { className: "cb-line-24 cb-line-xl-up-32" }),
                                React__default.createElement("div", { className: "cb-margin-top-16 cb-line-48 cb-line-xl-up-72 cb-line-xs-32" }),
                                React__default.createElement("div", { className: "cb-margin-top-32 cb-margin-md-top-48 cb-line-16" }),
                                React__default.createElement("div", { className: "cb-margin-top-16 cb-no-margin-xs-bottom cb-margin-sm-bottom-16 cb-margin-bottom-8 cb-line-16" })))))))));
};
const HeroFocusLoader = ({ loaderId, show = true, ariaLabel = 'Loading content...', background = '', light = false, timeout = 500, transitionClassName = 'cb-css-transition', }) => {
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    loaderId = loaderId || `apricot_loader_${uid}`;
    const wrapperClassName = cn(`cb-loader-shimmer`, `cb-loader-hero-band`, light && `cb-loader-light`, `cb-${background}-bg`);
    return (React__default.createElement(CSSTransition, { shouldMount: show, timeout: timeout, className: transitionClassName },
        React__default.createElement("div", { role: "status", "aria-labelledby": loaderId, "data-cbtrack-comp": "apricot-react:loader" },
            React__default.createElement("div", { className: wrapperClassName },
                React__default.createElement("div", { className: "container cb-padding-top-72 cb-padding-bottom-72" },
                    React__default.createElement("div", { className: "row" },
                        React__default.createElement("div", { className: "col-xs-12 offset-sm-5 col-sm-7 offset-md-6 col-md-6", id: loaderId, role: "img", "aria-label": ariaLabel },
                            React__default.createElement("div", { className: "cb-line-24 cb-line-xl-up-32" }),
                            React__default.createElement("div", { className: "cb-margin-top-16 cb-line-48 cb-line-xl-up-72 cb-line-xs-32" }),
                            React__default.createElement("div", { className: "cb-margin-top-32 cb-margin-md-top-48 cb-line-16" }),
                            React__default.createElement("div", { className: "cb-margin-top-16 cb-no-margin-xs-bottom cb-margin-sm-bottom-16 cb-margin-bottom-8 cb-line-16" }))))))));
};
const SectionLoader = ({ loaderId, show = true, ariaLabel = 'Loading content...', background = '', light = false, timeout = 500, transitionClassName = 'cb-css-transition', }) => {
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    loaderId = loaderId || `apricot_loader_${uid}`;
    const wrapperClassName = cn(`cb-loader-shimmer`, `cb-loader-section`, light && `cb-loader-light`, `cb-${background}-bg`);
    return (React__default.createElement(CSSTransition, { shouldMount: show, timeout: timeout, className: transitionClassName },
        React__default.createElement("div", { role: "status", "aria-labelledby": loaderId, "data-cbtrack-comp": "apricot-react:loader" },
            React__default.createElement("div", { className: wrapperClassName },
                React__default.createElement("div", { className: "container cb-padding-top-72 cb-padding-bottom-72" },
                    React__default.createElement("div", { className: "row" },
                        React__default.createElement("div", { className: "col-xs-12", id: loaderId, role: "img", "aria-label": ariaLabel },
                            React__default.createElement("div", { className: "cb-line-32" }),
                            React__default.createElement("div", { className: "cb-margin-top-24 cb-line-16" }),
                            React__default.createElement("div", { className: "cb-margin-top-16 cb-line-16" }),
                            React__default.createElement("div", { className: "cb-margin-top-16 cb-line-16" }),
                            React__default.createElement("div", { className: "cb-margin-top-16 cb-line-16" }))))))));
};

const btnType = ['pager', 'size'];

const PageButton = (_a) => {
    var { type = 'pager', buttonId, children, srDescription, active = false, ariaLabel, disabled = false, className, onClick } = _a, props = __rest(_a, ["type", "buttonId", "children", "srDescription", "active", "ariaLabel", "disabled", "className", "onClick"]);
    /**
     * Check if the button is disabled first before passing the event up
     */
    const onClickWithCheck = e => {
        if (disabled)
            e.preventDefault();
        else if (onClick) {
            onClick(e);
        }
    };
    return (React__default.createElement("li", null,
        React__default.createElement("a", Object.assign({ id: buttonId, href: "/#", onClick: onClickWithCheck, className: cn(type === 'pager' && `cb-btn cb-btn-square cb-btn-greyscale`, disabled && 'cb-disabled', active && `cb-active`, className), "aria-current": active || undefined, "aria-live": active ? 'polite' : undefined, "aria-disabled": disabled || undefined, "aria-label": ariaLabel || undefined, tabIndex: disabled ? -1 : undefined }, props),
            type === 'pager' && React__default.createElement("span", { className: "sr-only" }, active ? 'page ' : srDescription),
            type === 'size' && React__default.createElement("span", { className: "sr-only" }, "Show "),
            children,
            type === 'size' && React__default.createElement("span", { className: "sr-only" }, srDescription || 'records per page'))));
};

// prev & next buttons
const previousIcon = React__default.createElement("span", { "aria-hidden": "true", className: "cb-icon cb-left" });
const nextIcon = React__default.createElement("span", { "aria-hidden": "true", className: "cb-icon cb-right" });
const paginationBtnCount = (node) => {
    // base on apricots button size
    const btnWidth = 48;
    const maxWidth = node ? node.offsetWidth : 0;
    // we always have next and prev
    return Math.floor(maxWidth / btnWidth) - 2;
};
const getPaginationRange = (current, m, delta) => {
    const last = m;
    const left = current - delta;
    const right = current + delta + 1;
    const range = [];
    const rangeWithDots = [];
    let l;
    // eslint-disable-next-line no-plusplus
    for (let i = 1; i <= last; i++) {
        if (i === 1 || i === last || (i >= left && i < right)) {
            range.push(i);
        }
    }
    range.forEach(i => {
        if (l) {
            if (i - l === 2) {
                rangeWithDots.push(l + 1);
            }
            else if (i - l !== 1) {
                rangeWithDots.push('...');
            }
        }
        rangeWithDots.push(i);
        l = i;
    });
    return rangeWithDots;
};
const pagination = (current, m, delta, responsive, node) => {
    const rangeWithDots = getPaginationRange(current, m, delta);
    const count = rangeWithDots.length;
    const maxBtnNum = paginationBtnCount(node);
    if (responsive) {
        if (count > maxBtnNum && delta >= 0) {
            return pagination(current, m, delta - 1, responsive, node);
        }
        return rangeWithDots;
    }
    return rangeWithDots;
};
const Pager = (_a) => {
    var { pagerId, buttonId, responsive = true, current, max, delta = 3, disabled = [], ariaLabel = 'pagination', onPageChange } = _a, props = __rest(_a, ["pagerId", "buttonId", "responsive", "current", "max", "delta", "disabled", "ariaLabel", "onPageChange"]);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    pagerId = pagerId || `apricot_pager_${uid}`;
    const pagerRef = useRef(null);
    const [pageData, setPageData] = useState([]);
    const resizeEvent = (node) => {
        const event = new CustomEvent(`apricot_pagerChange_${pagerId}`);
        window.addEventListener('resize', () => {
            const currentBtnNum = node.querySelectorAll('li').length;
            const maxBtnNum = paginationBtnCount(node);
            if (maxBtnNum !== currentBtnNum) {
                document.dispatchEvent(event);
            }
        });
    };
    const setPaginationData = () => {
        const node = pagerRef.current;
        setPageData(pagination(current, max, delta, responsive, node));
    };
    const onClick = (page, e) => {
        e.preventDefault();
        if (typeof page === 'string')
            return;
        if (onPageChange)
            onPageChange(page);
    };
    useEffect(() => {
        setPaginationData();
        if (responsive) {
            if (pagerRef.current)
                resizeEvent(pagerRef.current);
            document.addEventListener(`apricot_pagerChange_${pagerId}`, setPaginationData);
        }
        // kill custom event
        return () => {
            if (responsive) {
                document.removeEventListener(`apricot_pagerChange_${pagerId}`, setPaginationData);
            }
        };
    }, [delta, current, max, responsive]);
    return (React__default.createElement("nav", Object.assign({ "aria-label": ariaLabel, "data-cbtrack-comp": "apricot-react:pager", ref: pagerRef, id: pagerId }, props),
        React__default.createElement("ul", { className: "cb-pagination" },
            React__default.createElement(PageButton, { buttonId: `${buttonId}_prev`, key: "prev", disabled: current === 1, srDescription: "Go to previous page", onClick: e => onClick(current - 1, e) }, previousIcon),
            pageData.map((page, index) => page === '...' ? (React__default.createElement(PageButton, { buttonId: `${buttonId}_${index}`, key: `more-${index}`, srDescription: "More Pages", disabled: true }, "...")) : (React__default.createElement(PageButton, { buttonId: `${buttonId}_${index}`, key: `page-${page}`, disabled: (typeof page === 'string' && disabled.includes(parseInt(page, 10))) ||
                    (typeof page === 'number' && disabled.includes(page)), active: page === current, srDescription: "Go to page ", onClick: e => onClick(page, e) }, page))),
            React__default.createElement(PageButton, { buttonId: `${buttonId}_next`, key: "next", disabled: current === max, srDescription: "Go to next page", onClick: e => onClick(current + 1, e) }, nextIcon))));
};

// single pagination button component
const PageSize = ({ select = false, pagesizeId, buttonId, label, labelId, maxRecords, recordCountLabel = `Total ${maxRecords} records`, recordCountLabelPositionLeft = true, pageSizeOptions, current, disabled = [], ariaLabel = 'page size', describedbyValue, truncate = false, className, onSizeChange, }) => {
    const uid = useUID();
    const sizeId = pagesizeId || `page_size_${uid}`;
    const btnId = buttonId || `page_size_btn_${uid}`;
    const finalLabel = select ? label : label || 'Show';
    const convertToLabeledValues = (options) => {
        return options.map(option => ({
            label: option.toString(),
            value: option,
            disabled: option === disabled.find(size => size === option),
        }));
    };
    const values = select ? convertToLabeledValues(pageSizeOptions) : [];
    const onClick = (size, e) => {
        e.preventDefault();
        if (onSizeChange)
            onSizeChange(size);
    };
    return (React__default.createElement(React__default.Fragment, null,
        !select && (React__default.createElement("div", { className: cn('cb-page-size', className), id: sizeId, "data-cbtrack-comp": "apricot-react:page-size" },
            recordCountLabel && recordCountLabelPositionLeft && React__default.createElement("span", null, recordCountLabel),
            finalLabel && React__default.createElement("span", null, finalLabel),
            React__default.createElement("nav", { "aria-label": ariaLabel },
                React__default.createElement("ul", null, pageSizeOptions.map((size, index) => (React__default.createElement(PageButton, { buttonId: `${btnId}_${index}`, key: `page-size-${index}`, disabled: disabled.includes(size), active: size === current, srDescription: " rows per page", onClick: e => onClick(size, e), type: "size" }, size))))),
            recordCountLabel && !recordCountLabelPositionLeft && React__default.createElement("span", null, recordCountLabel))),
        select && (React__default.createElement("div", { className: cn('cb-page-size', className), id: sizeId, "data-cbtrack-comp": "apricot-react:page-size" },
            React__default.createElement(Select, { selectId: pagesizeId, label: finalLabel, labelId: labelId, value: current.toString(), truncate: truncate, values: values, "aria-label": ariaLabel !== 'page size' ? ariaLabel : undefined, "aria-describedby": describedbyValue || undefined, onChange: value => {
                    if (onSizeChange)
                        onSizeChange(parseInt(value, 10));
                } })))));
};

const PaginationInfo = (_a) => {
    var { currentPage, pageSize, totalRecords, className, role = 'status' } = _a, props = __rest(_a, ["currentPage", "pageSize", "totalRecords", "className", "role"]);
    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    return (React__default.createElement("p", Object.assign({ role: role, className: cn('cb-pagination-info', className) }, props),
        "Showing ",
        React__default.createElement("strong", null, startRecord),
        "-",
        React__default.createElement("strong", null, endRecord),
        " of ",
        totalRecords));
};

/* eslint-disable max-lines-per-function */
const GalleryContext = React__default.createContext({ gridClasses: undefined });
const Thumbnail = ({ src, tSrc, alt, title, desc, children }) => {
    const { gridClasses } = useContext(GalleryContext);
    return (React__default.createElement("li", { className: gridClasses },
        React__default.createElement("a", { href: "/#", "aria-haspopup": "dialog" },
            React__default.createElement("div", { className: "cb-photo-thumb", "data-cb-img": src },
                React__default.createElement("img", { src: tSrc || src, alt: alt || '' })),
            React__default.createElement("div", { className: "cb-photo-thumb-info" },
                title && React__default.createElement("h3", null, title),
                desc && desc.map((d, key) => React__default.createElement("p", { key: key }, d)))),
        React__default.createElement("div", { className: "cb-photo-details-info" }, children)));
};
let galleryPlugin;
const PhotoGallery = ({ photoGalleryId, children, gridClasses, withAutoPlayBtn = true, autoPlayTime = 2000, withFullScreenBtn = true, altTxt, modalOnly = false, modalOnlyOpenWithFirst = false, modalTrigger, titleTag = 'h2', modalData, cbRef, }) => {
    const galleryRef = useRef(null);
    const modalRef = useRef(null);
    const flatChildren = flattenChildren(children);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    photoGalleryId = photoGalleryId || `apricot_gallery_${uid}`;
    useEffect(() => {
        if (!modalOnly && galleryRef.current) {
            galleryPlugin = CBPhotoGallery({
                elem: galleryRef.current,
                modal: modalRef.current,
                autoPlayTime,
                altTxt,
            });
            // eslint-disable-next-line no-param-reassign
            if (cbRef)
                cbRef.current = galleryPlugin;
        }
        if (modalOnly && modalTrigger) {
            galleryPlugin = CBPhotoGallery({
                elem: modalTrigger,
                modal: modalRef.current,
                autoPlayTime,
                altTxt,
                modalOnly,
                modalOnlyOpenWithFirst,
                titleTag,
                modalData,
            });
            // eslint-disable-next-line no-param-reassign
            if (cbRef)
                cbRef.current = galleryPlugin;
        }
        return () => {
            if (galleryPlugin) {
                galleryPlugin.destroy();
            }
        };
    }, [children, modalData]);
    const context = useMemo(() => ({ gridClasses }), [gridClasses]);
    return (React__default.createElement(React__default.Fragment, null,
        !modalOnly && (React__default.createElement("div", { className: "cb-photo-gallery", "data-cbtrack-comp": "apricot-react:photo-gallery", ref: galleryRef },
            React__default.createElement(GalleryContext.Provider, { value: context },
                React__default.createElement("ul", { className: cn(gridClasses && 'cb-photo-gallery-grid') }, flatChildren)))),
        React__default.createElement("div", { className: "cb-modal cb-photo-gallery-modal", id: photoGalleryId, "aria-hidden": "true", ref: modalRef },
            React__default.createElement("div", { className: "cb-modal-overlay", tabIndex: -1, "data-cb-modal-close": true },
                React__default.createElement("div", { className: "cb-modal-container", role: "dialog", "aria-modal": "true" },
                    React__default.createElement("div", { className: "cb-modal-header cb-gallery-header" },
                        React__default.createElement("div", { className: "cb-gallery-counter", role: "region", "aria-live": "polite", "aria-atomic": "true" },
                            React__default.createElement("p", { className: "cb-count-info", role: "heading", "aria-level": 3 },
                                React__default.createElement("span", { className: "cb-current-num cb-gallery-counter" }),
                                React__default.createElement("span", { "aria-hidden": "true" }, "/"),
                                React__default.createElement("span", { className: "sr-only" }, "of"),
                                React__default.createElement("span", { className: "cb-count-num cb-gallery-counter" }))),
                        React__default.createElement("div", { className: "cb-gallery-controls" },
                            withFullScreenBtn && (React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-square cb-btn-light cb-btn-fullscreen" },
                                React__default.createElement("span", { className: "cb-icon cb-full-screen", "aria-hidden": "true" }),
                                React__default.createElement("span", { className: "sr-only" }, "Display Fullscreen"))),
                            withAutoPlayBtn && (React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-square cb-btn-light cb-btn-play", "aria-pressed": "false" },
                                React__default.createElement("span", { className: "cb-icon cb-play-circle", "aria-hidden": "true" }),
                                React__default.createElement("span", { className: "sr-only" }, "Auto Play"))),
                            React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-square cb-btn-light cb-btn-close", "data-cb-modal-close": true },
                                React__default.createElement("span", { className: "cb-icon cb-x-mark", "aria-hidden": "true" }),
                                React__default.createElement("span", { className: "sr-only" }, "Close Gallery Dialog")))),
                    React__default.createElement("div", { className: "cb-modal-content" },
                        React__default.createElement("div", { className: "cb-gallery-container" },
                            React__default.createElement("div", { className: "cb-gallery-navigation cb-prev-img", tabIndex: 0, role: "link", "aria-label": "previous gallery image" },
                                React__default.createElement("span", { className: "cb-icon cb-gallery-backward" })),
                            React__default.createElement("div", { className: "cb-gallery-content" },
                                React__default.createElement("div", { className: "cb-gallery-image" },
                                    React__default.createElement("div", { className: "cb-gallery-image-container" }),
                                    React__default.createElement("div", { className: "cb-gallery-hit" })),
                                React__default.createElement("div", { className: "cb-info-container" },
                                    React__default.createElement("div", { className: "cb-gallery-des", 
                                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                                        tabIndex: 0, role: "region", "aria-label": "Gallery Image Description" }))),
                            React__default.createElement("div", { className: "cb-gallery-navigation cb-next-img", tabIndex: 0, role: "link", "aria-label": "next gallery image" },
                                React__default.createElement("span", { className: "cb-icon cb-gallery-forward" })))))))));
};

const popoverPlacements = ['top', 'right', 'bottom', 'left'];

const Popover = ({ trigger, shadowRoot = false, popoverId, headerId, popoverTitle, children, triggerEvent = 'click', placement = 'top', closeButton, closeButtonLabel = 'Close Popover', closeOnClickOutside = true, className, popoverClassName, onShow = null, onHide = null, cbRef, }) => {
    const popoverRef = useRef(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    headerId = headerId || `apricot_popover_header_${uid}`;
    useEffect(() => {
        const popover = CBPopover({
            elem: typeof trigger === 'string' ? document.getElementById(trigger) : trigger,
            trigger: triggerEvent,
            popoverNode: popoverRef.current,
            placement,
            shadowRoot,
            closeOnClickOutside,
            style: popoverClassName,
            onShow,
            onHide,
        });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = popover;
        }
        return () => {
            if (popover) {
                popover.destroy();
            }
        };
    }, []);
    return (React__default.createElement("div", { id: popoverId, className: cn('cb-popover', className), "aria-hidden": "true", role: "region", "data-cbtrack-comp": "apricot-react:popover", ref: popoverRef },
        React__default.createElement("div", { className: "cb-popover-inner" },
            popoverTitle && (React__default.createElement("div", { className: cn('cb-popover-header', closeButton && 'cb-popover-close') },
                React__default.createElement("h3", { id: headerId, className: "cb-popover-title" }, popoverTitle),
                closeButton && (React__default.createElement(CloseButton, { "aria-describedby": headerId, label: closeButtonLabel, "data-cb-popover-close": true })))),
            React__default.createElement("div", { className: "cb-popover-content" }, children))));
};

const behaviourTypes = ['tap', 'snap'];
const pipsMode = ['range', 'steps', 'positions', 'count', 'values'];
const pipsType = [-1, 0, 1];

/* eslint-disable max-lines-per-function */
let sliderIns = null;
const Slider = forwardRef(({ input1 = null, input2 = null, input1Label = 'Minimum', input2Label = 'Maximum', input1AriaLabel, input2AriaLabel, input1Style, input2Style, input1LabelId, input2LabelId, inputContainerClass, inputContainerStyle, start, range = { min: 0, max: 0 }, stacked = false, ariaControls, handleInputChange = true, label, animation = true, behaviour, step = 1, keyboardDefaultStep = 1, keyboardMultiplier = 5, black, className, reset, style, format, ariaFormat, margin, limit, padding, tooltips, pips, disabled, disabledHandel, onStart, onUpdate, onSlide, onDrag, onSet, onChange, onEnd, }, ref) => {
    const refSlider = useRef(null);
    const refInput1 = useRef(null);
    const refInput2 = useRef(null);
    const sliderID = `apricot_slider_${useUID()}`;
    const inputID1 = `apricot_slider_input_${useUID()}`;
    const inputID2 = `apricot_slider_input_${useUID()}`;
    const sliderClass = cn('cb-slider', black && 'cb-slider-black', className);
    const sliderRange = Array.isArray(start) && start[1];
    if (input1 && (!input1Label || !input1AriaLabel)) {
        console.warn('Apricot Warning- Slider, Make sure to add an label or aria-label value for your input1');
    }
    if (input2 && (!input2Label || !input2AriaLabel)) {
        console.warn('Apricot Warning- Slider, Make sure to add an label or aria-label value for your input2 ');
    }
    useImperativeHandle(ref, () => refSlider.current);
    useEffect(() => {
        if (refSlider.current) {
            sliderIns = CBSlider.init({
                elem: refSlider.current,
                input1: input1 && refInput1.current,
                input2: input2 && refInput2.current,
                handleInputChange,
                ariaControls,
                label,
                start,
                animation,
                range,
                step,
                keyboardDefaultStep,
                keyboardMultiplier,
                pips,
                format,
                tooltips,
                margin,
                limit,
                padding,
                ariaFormat,
                behaviour,
            });
            // Events
            if (sliderIns) {
                sliderIns.on('apricot_start', (values, handle, unencoded, tap, positions) => {
                    if (onStart)
                        onStart(values, handle, unencoded, tap, positions);
                });
                sliderIns.on('apricot_update', (values, handle, unencoded, tap, positions) => {
                    if (onUpdate)
                        onUpdate(values, handle, unencoded, tap, positions);
                });
                sliderIns.on('apricot_change', (values, handle, unencoded, tap, positions) => {
                    if (onChange)
                        onChange(values, handle, unencoded, tap, positions);
                });
                sliderIns.on('apricot_slide', (values, handle, unencoded, tap, positions) => {
                    if (onSlide)
                        onSlide(values, handle, unencoded, tap, positions);
                });
                sliderIns.on('apricot_set', (values, handle, unencoded, tap, positions) => {
                    if (onSet)
                        onSet(values, handle, unencoded, tap, positions);
                });
                sliderIns.on('apricot_drag', (values, handle, unencoded, tap, positions) => {
                    if (onDrag)
                        onDrag(values, handle, unencoded, tap, positions);
                });
                sliderIns.on('apricot_end', (values, handle, unencoded, tap, positions) => {
                    if (onEnd)
                        onEnd(values, handle, unencoded, tap, positions);
                });
            }
        }
        return () => {
            if (sliderIns) {
                sliderIns.destroy();
            }
        };
    }, []);
    useEffect(() => {
        if (reset) {
            if (sliderIns)
                sliderIns.reset();
        }
    }, [reset]);
    const arraysEqual = (a1, a2) => {
        return JSON.stringify(a1) === JSON.stringify(a2);
    };
    const startRef = useRef(start);
    if (!arraysEqual(startRef.current, start)) {
        startRef.current = start;
    }
    useEffect(() => {
        if (sliderIns)
            sliderIns.set(start);
    }, [startRef.current]);
    useEffect(() => {
        if (sliderIns)
            sliderIns.disableSlider(!!disabled);
    }, [disabled]);
    useEffect(() => {
        if (sliderIns && disabledHandel) {
            if (typeof disabledHandel[0] !== 'undefined')
                sliderIns.disableHandle(0, disabledHandel[0]);
            if (typeof disabledHandel[1] !== 'undefined')
                sliderIns.disableHandle(1, disabledHandel[1]);
        }
    }, [disabledHandel]);
    return (React__default.createElement("div", { className: cn('cb-slider-container', stacked && 'cb-slider-stacked', sliderRange ? 'cb-slider-range' : 'cb-slider-single'), "data-cbtrack-comp": "apricot-react:slider" },
        React__default.createElement("div", { ref: refSlider, id: sliderID, "aria-disabled": disabled, className: sliderClass, style: style }),
        (input1 || input2) && (React__default.createElement("div", { style: inputContainerStyle, className: cn('cb-input-container', !stacked && 'cb-margin-sm-up-left-32', !pips && 'cb-margin-xs-top-16', pips && 'cb-margin-xs-top-48', stacked && pips && 'cb-margin-top-48', stacked && !pips && 'cb-margin-top-16', inputContainerClass) },
            input1 && (React__default.createElement("div", { className: cn('cb-input', 'cb-input-slider') },
                React__default.createElement("label", { htmlFor: inputID1, id: input1LabelId }, input1Label),
                React__default.createElement("input", { type: "number", "aria-label": input1AriaLabel, step: step, id: inputID1, ref: refInput1, style: input1Style }))),
            input2 && (React__default.createElement("div", { className: cn('cb-input', 'cb-input-slider') },
                React__default.createElement("label", { htmlFor: inputID2, id: input2LabelId }, input2Label),
                React__default.createElement("input", { type: "number", "aria-label": input2AriaLabel, step: step, id: inputID2, ref: refInput2, style: input2Style })))))));
});

const stepperStates = ['active', 'complete', 'skip', 'disabled', 'error'];

const pastTense = (state) => ({ complete: 'completed', skip: 'skipped' })[state] || state;
const simpleIcon = (state) => {
    const icon = (state &&
        {
            complete: 'check-fill',
            skip: 'disc',
            error: 'disc',
            disabled: 'disc-alt',
            active: 'disc',
        }[state]) ||
        'circle';
    return icon;
};
const StepperContext = React__default.createContext({ simple: false, navigational: false });
const StepWrapper = ({ navigational, state, href, handleClick, children }) => {
    return navigational ? (React__default.createElement("a", { href: state !== 'disabled' ? href : null, onClick: handleClick, "aria-disabled": state === 'disabled' || undefined }, children)) : (children);
};
const IconStep = ({ state, icon, iconDecorative = true, label, subLabel, href = '/#', onClick = null, }) => {
    const ref = useRef(null);
    const [stepNumber, setStepNumber] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    useEffect(() => {
        const stepElements = Array.from(ref.current.parentElement.children);
        setStepNumber(1 + stepElements.indexOf(ref.current));
        setTotalSteps(stepElements.length);
    });
    const handleClick = e => {
        if (state === 'disabled' || onClick)
            e.preventDefault();
        if (onClick)
            onClick();
    };
    const { simple, navigational } = useContext(StepperContext);
    return (React__default.createElement("li", { className: cn(state && `cb-stepper-${state}`), ref: ref },
        React__default.createElement(StepWrapper, { navigational: navigational, state: state, href: href, handleClick: handleClick },
            simple && React__default.createElement(Icon, { name: simpleIcon(state), decorative: iconDecorative }),
            !simple && icon && state !== 'complete' && (React__default.createElement(Icon, { name: icon, circular: !simple, decorative: iconDecorative })),
            !simple && icon && state === 'complete' && (React__default.createElement(Icon, { circular: true, name: "check-fill", decorative: iconDecorative })),
            !simple && !icon && state === 'complete' && (React__default.createElement(Icon, { circular: true, name: "check-fill", decorative: iconDecorative })),
            !simple && !icon && state !== 'complete' && (React__default.createElement(NumericIcon, { decorative: iconDecorative }, stepNumber)),
            label && typeof label === 'string' ? React__default.createElement("p", null, label) : React__default.createElement("div", { className: "cb-stepper-label" }, label),
            subLabel && typeof subLabel === 'string' ? (React__default.createElement("p", null, subLabel)) : (React__default.createElement("div", { className: "cb-stepper-sub-label" }, subLabel)),
            React__default.createElement("span", { className: "sr-only cb-stepper-step" }, `step ${stepNumber} of ${totalSteps} ${state ? pastTense(state) : 'pending'}`))));
};
const NumericStep = ({ state, label, iconDecorative = true, subLabel, href = '/#', onClick = null, }) => {
    const ref = useRef(null);
    const [stepNumber, setStepNumber] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    // Mitra: we need a list of dependencies here
    useEffect(() => {
        const stepElements = Array.from(ref.current.parentElement.children);
        setStepNumber(1 + stepElements.indexOf(ref.current));
        setTotalSteps(stepElements.length);
    });
    const handleClick = e => {
        if (state === 'disabled' || onClick)
            e.preventDefault();
        if (onClick)
            onClick();
    };
    const { navigational } = useContext(StepperContext);
    return (React__default.createElement("li", { className: cn(state && `cb-stepper-${state}`), ref: ref },
        React__default.createElement(StepWrapper, { navigational: navigational, state: state, href: href, handleClick: handleClick },
            state === 'complete' ? (React__default.createElement(Icon, { circular: true, name: "check-fill", decorative: iconDecorative })) : (React__default.createElement(NumericIcon, { decorative: iconDecorative }, stepNumber)),
            label && typeof label === 'string' ? React__default.createElement("p", null, label) : React__default.createElement("div", { className: "cb-stepper-label" }, label),
            subLabel && typeof subLabel === 'string' ? (React__default.createElement("p", null, subLabel)) : (React__default.createElement("div", { className: "cb-stepper-sub-label" }, subLabel)),
            React__default.createElement("span", { className: "sr-only cb-stepper-step" }, `step ${stepNumber} of ${totalSteps} ${state ? pastTense(state) : 'pending'}`))));
};
const SimpleStep = ({ state, iconDecorative = true, href = '/#', onClick = null, }) => {
    const ref = useRef(null);
    const [stepNumber, setStepNumber] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    // Mitra: we need a list of dependencies here
    useEffect(() => {
        const stepElements = Array.from(ref.current.parentElement.children);
        setStepNumber(1 + stepElements.indexOf(ref.current));
        setTotalSteps(stepElements.length);
    });
    const handleClick = e => {
        if (state === 'disabled' || onClick)
            e.preventDefault();
        if (onClick)
            onClick();
    };
    const { navigational } = useContext(StepperContext);
    return (React__default.createElement("li", { className: cn(state && `cb-stepper-${state}`), ref: ref },
        React__default.createElement(StepWrapper, { navigational: navigational, state: state, href: href, handleClick: handleClick },
            React__default.createElement(Icon, { name: simpleIcon(state), decorative: iconDecorative }),
            React__default.createElement("span", { className: "sr-only cb-stepper-step" }, `step ${stepNumber} of ${totalSteps} ${state ? pastTense(state) : 'pending'}`))));
};
const Stepper = ({ stepperId, children, vertical, ariaLabel, navigational, simple, horizontalFix, role = navigational ? 'navigation' : 'region', className, }) => {
    const context = useMemo(() => ({ simple: !!simple, navigational: !!navigational }), [simple, navigational]);
    return (React__default.createElement("div", { id: stepperId, className: cn('cb-stepper', vertical && 'cb-stepper-vertical', simple && 'cb-stepper-simple', horizontalFix && 'cb-stepper-horizontal-fix', className), "aria-label": ariaLabel, "data-cbtrack-comp": "apricot-react:stepper", role: role },
        React__default.createElement(StepperContext.Provider, { value: context },
            React__default.createElement("ol", null, children))));
};

const offsetType = ['16', '24', '32', '48', '72'];

const Sticky = forwardRef((_a, ref) => {
    var { active = true, bottom, dropShadow, offsetTop = 0, offsetBottom = 0, intuitive = false, parent = false, localNavId = null, callBack, className, style, zIndex, children } = _a, props = __rest(_a, ["active", "bottom", "dropShadow", "offsetTop", "offsetBottom", "intuitive", "parent", "localNavId", "callBack", "className", "style", "zIndex", "children"]);
    const refSticky = useRef(null);
    const stickyClass = cn('cb-sticky', active && 'cb-sticky-active', !bottom && 'cb-sticky-top', bottom && 'cb-sticky-bottom', dropShadow && !callBack && `cb-box-shadow-${bottom ? 'top' : 'bottom'}`, className);
    useImperativeHandle(ref, () => refSticky.current);
    // add inline style
    const zIndexStyle = zIndex && { zIndex: `${zIndex}` };
    const topStyle = !bottom && offsetTop && { top: `${offsetTop}px` };
    const bottomStyle = bottom && offsetBottom && { bottom: `${offsetBottom}px` };
    // only use CBSticky if we have a callback
    useEffect(() => {
        let sticky = null;
        if (intuitive && !bottom && refSticky.current) {
            sticky = CBSticky({
                elem: refSticky.current,
                localNavElem: localNavId && document.querySelector(`#${localNavId}`),
                parent,
                offsetTop,
                dropShadow,
                callBack,
            });
        }
        return () => {
            if (sticky) {
                sticky.destroy();
            }
        };
    }, []);
    return (React__default.createElement("div", Object.assign({ ref: refSticky, className: stickyClass, style: Object.assign(Object.assign(Object.assign(Object.assign({}, zIndexStyle), topStyle), bottomStyle), style), "data-cbtrack-comp": "apricot-react:sticky" }, props), children));
});

// Sort variants:
const SORT_DEFAULT = 'default'; // separate row
const SORT_INLINE = 'inline'; // inline with the header
const SORT_NONE = 'none'; // no sorting - ignore sortable column props
const sortVariants = [SORT_NONE, SORT_INLINE, SORT_DEFAULT];
const detachedComponentsVariants = ['top', 'bottom', 'none'];
/**
 * Expander position variants
 */
const EXPANDER_RIGHT = 'right'; // most right position
const EXPANDER_LEFT = 'left'; // most left position
const EXPANDER_NONE = 'none'; // no expander
const expanderVariants = [EXPANDER_RIGHT, EXPANDER_LEFT, EXPANDER_NONE];
const captionAlignment = ['left', 'center'];
const captionFontWeight = ['regular', 'bold'];
const captionFontSize = ['regular', 'large'];
const ASCENDING_TEXT = 'ascending';
const DESCENDING_TEXT = 'descending';
const attributesFromTupleArray = (attributes) => {
    return attributes.reduce((attrib, [aName, aValue]) => (Object.assign(Object.assign({}, attrib), { [aName]: aValue })), {});
};
const a11yHighlightTypes = ['append', 'wrap', 'none'];

const LoadingWrapper = ({ children, loading, ariaLabel, light, overlayBackground = 'rgba(255, 255, 255, 0.3)', spinnerClassName, overflowX = true, positionRelative = true, maxHeight, }) => {
    return (React__default.createElement("div", { className: cn(positionRelative && 'position-relative'), style: { overflowX: overflowX ? 'hidden' : undefined, maxHeight: maxHeight ? `${maxHeight}px` : undefined } },
        loading && (React__default.createElement("div", { className: "cb-loader-wrapper", style: { background: overlayBackground } },
            React__default.createElement(Spinner, { light: light, ariaLabel: ariaLabel, className: spinnerClassName }))),
        children));
};

const SortCell = React__default.forwardRef((_a, ref) => {
    var { children, active, ascending, className, describedBy, empty, onClick, joined = false, focused, unFocusable, onFocus, onBlur } = _a, props = __rest(_a, ["children", "active", "ascending", "className", "describedBy", "empty", "onClick", "joined", "focused", "unFocusable", "onFocus", "onBlur"]);
    const cellContent = !empty && (React__default.createElement("a", { key: `a${props.id}`, href: "/#", onClick: e => e.preventDefault(), "aria-describedby": !joined ? describedBy : undefined, role: "button", tabIndex: unFocusable ? -1 : 0, onFocus: onFocus, onBlur: onBlur, className: cn(focused && 'focused') },
        children ? React__default.createElement("span", null, children) : null,
        React__default.createElement("span", { className: cn('cb-icon', active ? `cb-sort-${ascending ? 'asc' : 'desc'}` : 'cb-sort') }, !joined && (React__default.createElement("span", { className: "sr-only" },
            active ? React__default.createElement(React__default.Fragment, null,
                startCase(ascending ? ASCENDING_TEXT : DESCENDING_TEXT),
                " - ") : null,
            "Click to sort in ",
            active && ascending ? DESCENDING_TEXT : ASCENDING_TEXT,
            " order")))));
    return React__default.createElement(joined ? 'th' : 'td', Object.assign(Object.assign({}, props), { ref, className: cn(`cb-table-sort${joined ? '-joined' : ''}`, className), onClick: (e) => {
            e.stopPropagation();
            if (!empty)
                onClick();
        }, onKeyPress: e => {
            if (e.which === 32 && !empty) {
                e.preventDefault();
                onClick();
            }
        } }), [cellContent]);
});

function computeLayout(titles, separator) {
    const colHeaders = titles.map(title => title.split(separator));
    const headRowsCount = max(map(colHeaders, 'length'));
    let data = colHeaders.map(col => col.map((name, i) => {
        const isLast = i === col.length - 1;
        return {
            name,
            rowSpan: isLast ? headRowsCount - col.length + 1 : undefined,
            colSpan: 1,
            isLast,
        };
    }));
    data = unzip(data);
    data.forEach(row => {
        let spanningCell = null;
        each(row, (col, i) => {
            set(row[i], 'colIndex', i);
            if (col && spanningCell && col.name === spanningCell.name) {
                /* eslint-disable no-param-reassign,@typescript-eslint/ban-ts-comment */
                // @ts-ignore
                row[i] = undefined;
                if (spanningCell)
                    spanningCell.colSpan += 1;
            }
            else
                spanningCell = col;
        });
    });
    return data.map(compact);
}

const useIsSticky = (ref, stuckClass = 'cb-sticky-active', unstuckClass = null) => {
    const [isSticky, setIsStuck] = useState(false);
    const stickyClassName = isSticky ? stuckClass : unstuckClass;
    useEffect(() => {
        const cachedRef = ref.current;
        const observer = new window.IntersectionObserver(([e]) => {
            setIsStuck(e.intersectionRatio < 1);
        }, { threshold: [1] });
        observer.observe(cachedRef);
        return () => observer.unobserve(cachedRef);
    }, [ref]);
    return { isSticky, stickyClassName };
};

const TableHeaderContext = React.createContext({});

/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable jsx-a11y/control-has-associated-label */
const TableHeader = ({ columns, onSortClick, sortState, columnTitleSeparator = '|', sortType, expanderPosition, stickyHeaderParent, stickyColumn, focusedFieldDispatch, }) => {
    const cellRefs = useRef([]); // one ref per cell
    const [cellWidths, setCellWidths] = useState([]);
    const { stickyMode } = useContext(TableHeaderContext);
    const [focusedField, setFocusedField] = focusedFieldDispatch;
    const hasSortRow = sortType === SORT_DEFAULT && columns.some(c => c.props.sortable);
    const titles = columns.map(c => c.props.title || startCase(c.props.field));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const layoutInfo = computeLayout(titles, columnTitleSeparator);
    const uid = useUID();
    const onFocus = (field) => () => {
        setFocusedField(field);
    };
    const onBlur = () => {
        setFocusedField(null);
    };
    const buildAriaSort = (field) => {
        if (!sortState || field !== sortState.field) {
            return 'none';
        }
        return sortState.ascending ? ASCENDING_TEXT : DESCENDING_TEXT;
    };
    // Build single header cell using CustomHeadComponent or the title
    const buildHeaderCell = ({ props: { className, field, headerComponent: CustomHeadComponent, headerId } }, colIndex, rowIndex, colSpan, rowSpan, name, isLast, cellRefsParam, cellWidths) => {
        const cellRefsLocal = cellRefsParam;
        // if custom component - then render it otherwise simply dump the title
        const cellContent = CustomHeadComponent ? (React__default.createElement(CustomHeadComponent, { field: field, title: name, rowIndex: rowIndex, stickyMode: stickyMode })) : (name);
        const column = columns[colIndex];
        const withColScope = isNil(column.props.withColScope) ? true : column.props.withColScope;
        const isSort = isLast && sortType === SORT_INLINE && column.props.sortable;
        const cellId = headerId || `t${uid}${field}${colIndex}`;
        const id = `${cellId}${isLast ? '' : rowIndex + (rowSpan || 1) - 1}`;
        const cellProps = Object.assign(Object.assign({ className: cn(stickyColumn && column.props.sticky && 'cb-sticky-column', className) }, (withColScope ? { scope: colSpan > 1 ? 'colgroup' : 'col' } : {})), { colSpan: colSpan > 1 ? colSpan : undefined, rowSpan: rowSpan && rowSpan > 1 ? rowSpan : undefined, id, 'aria-sort': isLast ? buildAriaSort(field) : undefined, headers: (column.props.headHeaders && column.props.headHeaders(rowIndex)) || undefined });
        const cellKey = `${isSort ? 'sort' : 'col'}-${field}-${colIndex}-${rowIndex}`;
        // if sort enabled for the cell - render a joined sorting cell otherwise a regular cell
        return isSort ? (React__default.createElement(SortCell, Object.assign({ key: cellKey, ref: el => {
                cellRefsLocal.current[colIndex] = el;
            }, style: column.props.sticky
                ? {
                    left: colIndex > 0 ? cellWidths.slice(0, colIndex).reduce((a, b) => a + b, 0) : undefined,
                }
                : undefined }, cellProps, { active: column.props.field === (sortState === null || sortState === void 0 ? void 0 : sortState.field), focused: column.props.field === focusedField, ascending: (sortState === null || sortState === void 0 ? void 0 : sortState.ascending) || false, onClick: () => {
                onSortClick(column.props.field);
            }, describedBy: `${cellId}`, joined: true, unFocusable: stickyMode, onFocus: onFocus(column.props.field), onBlur: onBlur }), cellContent)) : (React__default.createElement("th", Object.assign({ key: cellKey, ref: el => {
                cellRefsLocal.current[colIndex] = el;
            }, style: stickyColumn && column.props.sticky
                ? {
                    left: colIndex > 0 ? cellWidths.slice(0, colIndex).reduce((a, b) => a + b, 0) : undefined,
                }
                : undefined }, cellProps), cellContent));
    };
    const expanderHeadCell = () => React__default.createElement("td", { className: "cb-table-expandable-trigger" });
    const expanderSortCell = () => React__default.createElement("td", { className: "cb-table-sort cb-table-expandable-trigger" });
    const headerRef = useRef(null);
    const { stickyClassName } = useIsSticky(headerRef);
    const updateWidths = () => {
        setCellWidths(cellRefs.current.map(ref => (ref === null || ref === void 0 ? void 0 : ref.getBoundingClientRect().width) || 0));
    };
    useEffect(() => {
        if (stickyColumn) {
            // Wait for next frame to ensure DOM is painted
            requestAnimationFrame(updateWidths);
            window.addEventListener('resize', updateWidths);
            return () => window.removeEventListener('resize', updateWidths);
        }
        return undefined;
    }, []);
    return (React__default.createElement("thead", { className: cn(stickyHeaderParent && 'cb-sticky-header-parent', stickyClassName), ref: headerRef },
        layoutInfo.map((row, rowIndex) => (React__default.createElement("tr", { key: `header-row-${rowIndex}` },
            expanderPosition === EXPANDER_LEFT && expanderHeadCell(),
            row.map(({ name, rowSpan, colSpan, colIndex, isLast }) => buildHeaderCell(columns[colIndex], colIndex, rowIndex, colSpan, rowSpan, name, isLast, cellRefs, cellWidths)),
            expanderPosition === EXPANDER_RIGHT && expanderHeadCell()))),
        hasSortRow && (React__default.createElement("tr", { className: "cb-table-sort-row header-row" },
            expanderPosition === EXPANDER_LEFT && expanderSortCell(),
            columns.map((column, colIndex) => {
                const cellId = column.props.headerId || `t${uid}${column.props.field}${colIndex}`;
                return (React__default.createElement(SortCell, { key: `sort-${column.props.field}`, active: column.props.field === (sortState === null || sortState === void 0 ? void 0 : sortState.field), focused: column.props.field === focusedField, ascending: (sortState === null || sortState === void 0 ? void 0 : sortState.ascending) || false, className: cn(column.props.className, stickyColumn && column.props.sticky && 'cb-sticky-column'), empty: !column.props.sortable, onClick: () => {
                        onSortClick(column.props.field);
                    }, describedBy: `${cellId}`, unFocusable: stickyMode, onFocus: onFocus(column.props.field), onBlur: onBlur, ref: el => {
                        if (stickyColumn && column.props.sticky) {
                            cellRefs.current[colIndex] = el;
                        }
                    }, style: stickyColumn && column.props.sticky
                        ? {
                            left: colIndex > 0
                                ? cellWidths.slice(0, colIndex).reduce((a, b) => a + b, 0)
                                : undefined,
                        }
                        : undefined }));
            }),
            expanderPosition === EXPANDER_RIGHT && expanderSortCell()))));
};

/* eslint-disable no-nested-ternary */
const TableCell = React__default.forwardRef(({ field, highlight, a11yHighlightType, a11yHighlightText, a11ySelectedText, isRowHighlighted, isRowSelected, selected, component: CustomComponent, className, classNameFunc, attributes, attributesFunc, rowIndex, row, rowSpan, colSpan, rowHeader, sticky, onValueFormat, id, headers, style, scopeFunc, }, ref) => {
    const isHighlighted = highlight && highlight(row[field]);
    const isSelected = selected && selected(row[field]);
    const value = onValueFormat ? onValueFormat(row[field], row, rowIndex) : row[field];
    const cellClassName = cn(isHighlighted && 'cb-table-highlight', isSelected && 'cb-table-selected', sticky && 'cb-sticky-column', isRowSelected && 'cb-table-selected', isRowHighlighted && 'cb-table-highlight', className, classNameFunc && classNameFunc(value, row, rowIndex));
    const cellContent = CustomComponent ? (React__default.createElement(CustomComponent, { value: value, field: field, rowIndex: rowIndex, row: row })) : (value);
    let a11yLabel;
    if (isHighlighted || isRowHighlighted) {
        a11yLabel = a11yHighlightText;
    }
    else if (isSelected || isRowSelected) {
        a11yLabel = a11ySelectedText;
    }
    return React__default.createElement(rowHeader ? 'th' : 'td', Object.assign(Object.assign(Object.assign(Object.assign({ ref, className: cellClassName, rowSpan, colSpan: colSpan !== 1 ? colSpan : undefined, headers,
        id }, (rowHeader
        ? {
            scope: scopeFunc
                ? scopeFunc(value, row, rowIndex)
                : rowSpan && rowSpan > 1
                    ? 'rowgroup'
                    : colSpan && colSpan > 1
                        ? 'colgroup'
                        : 'row',
        }
        : {})), (attributes && attributesFromTupleArray(attributes))), (attributesFunc && attributesFromTupleArray(attributesFunc(value, row, rowIndex)))), { style }), React__default.createElement(React__default.Fragment, null,
        a11yLabel && a11yHighlightType === 'append' && React__default.createElement("span", { className: "sr-only" }, a11yLabel),
        a11yLabel && a11yHighlightType === 'wrap' ? (React__default.createElement("span", { role: "region", "aria-label": a11yLabel }, cellContent)) : (cellContent)));
});

const ExpanderRow = (_a) => {
    var { colSpan, isExpanded, expandableRowComponent: CustomComponent, expanderPosition, id, className } = _a, props = __rest(_a, ["colSpan", "isExpanded", "expandableRowComponent", "expanderPosition", "id", "className"]);
    return (React__default.createElement("tr", { className: cn('cb-table-expandable-row', !isExpanded && 'hidden', className), "aria-hidden": !isExpanded, id: id },
        React__default.createElement("td", { className: cn('cb-table-expandable-container', expanderPosition === EXPANDER_LEFT && 'cb-table-expandable-trigger-left', expanderPosition === EXPANDER_RIGHT && 'cb-table-expandable-trigger-right'), colSpan: colSpan }, isExpanded && CustomComponent && React__default.createElement(CustomComponent, Object.assign({}, props)))));
};

const ExpanderCell = ({ expandableIconOpen, expandableIconClosed, expanderButtonLabel, isExpanded, isDisabled, onClick, ariaControls, }) => (React__default.createElement("td", { className: cn('cb-table-expandable-trigger', isDisabled && 'trigger-disabled') }, !isDisabled && (React__default.createElement(SquareButton, { icon: isExpanded ? expandableIconOpen : expandableIconClosed, label: expanderButtonLabel(), onClick: onClick, "aria-controls": ariaControls, "aria-expanded": isExpanded, className: "cb-btn-black" }))));

/* eslint-disable max-lines-per-function */
const TableRow = ({ row, rowIndex, rowSelected = () => false, rowHighlighted = () => false, isRowStriped, columns, stickyColumn, rowSpans, expandableRows, expanderPosition, expandableIconClosed = 'down', expandableIconOpen = 'up', expandableRowExpanded = 'isExpanded', expandableRowDisabled = 'isExpandDisabled', expandableRowComponent, expanderButtonLabel = 'Expand the row for more information', onRowExpandToggled, a11yHighlightText, a11ySelectedText, a11yHighlightType, rowAttributes, rowAttributesFunc, rowClassNameFunc, }) => {
    const cellRefs = useRef([]); // one ref per cell
    const [cellWidths, setCellWidths] = useState([]);
    // update the expander position based on the global enable flag
    const expander = expandableRows ? expanderPosition : EXPANDER_NONE;
    const isRowHighlighted = rowHighlighted(row);
    const isRowSelected = rowSelected(row);
    const isDisabled = useMemo(() => {
        return typeof expandableRowDisabled === 'string' ? row[expandableRowDisabled] : expandableRowDisabled(row);
    }, [expandableRowDisabled, row]);
    const [isExpanded, setIsExpanded] = useState(typeof expandableRowExpanded === 'string' ? row[expandableRowExpanded] : expandableRowExpanded(row));
    const rowId = `row_${useUID()}`;
    /**
     * Toggle the expanded prop on click
     */
    const expandOnClick = () => {
        if (onRowExpandToggled)
            onRowExpandToggled(row, !isExpanded);
        setIsExpanded(value => !value);
    };
    const getExpanderLabel = () => {
        if (typeof expanderButtonLabel === 'string') {
            return expanderButtonLabel;
        }
        return expanderButtonLabel ? expanderButtonLabel(row, isExpanded) : '';
    };
    /**
     * Render the expander cell code
     */
    const renderExpander = () => {
        return (React__default.createElement(ExpanderCell, { ariaControls: rowId, expandableIconOpen: expandableIconOpen, expandableIconClosed: expandableIconClosed, isDisabled: isDisabled, isExpanded: isExpanded, expanderButtonLabel: getExpanderLabel, onClick: expandOnClick }));
    };
    const rowClassName = cn(isRowSelected && 'cb-table-selected', isRowHighlighted && 'cb-table-highlight', isRowStriped === true && 'cb-stripe', isRowStriped === false && 'cb-no-stripe', rowClassNameFunc && rowClassNameFunc(row));
    const rowAttributesProps = Object.assign(Object.assign({}, (rowAttributes && attributesFromTupleArray(rowAttributes))), (rowAttributesFunc && attributesFromTupleArray(rowAttributesFunc(row))));
    let spanCount = 1;
    const updateWidths = () => {
        setCellWidths(cellRefs.current.map(ref => (ref === null || ref === void 0 ? void 0 : ref.getBoundingClientRect().width) || 0));
    };
    useEffect(() => {
        if (stickyColumn) {
            // Wait for next frame to ensure DOM is painted
            requestAnimationFrame(updateWidths);
            window.addEventListener('resize', updateWidths);
            return () => window.removeEventListener('resize', updateWidths);
        }
        return undefined;
    }, []);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("tr", Object.assign({ "data-tr": rowIndex, key: `row-${rowIndex}`, className: rowClassName }, rowAttributesProps),
            expander === EXPANDER_LEFT && renderExpander(),
            columns.map(({ props }, colIndex) => {
                if (spanCount > 1) {
                    spanCount -= 1;
                    return null;
                }
                spanCount = props.onColSpan ? props.onColSpan(row, rowIndex) : 1;
                if (props && props.groupValues && !rowSpans[props.field][rowIndex])
                    return null;
                return (React__default.createElement(TableCell, Object.assign({ key: `row-${rowIndex}-col-${props.field}-${colIndex}`, ref: el => {
                        if (stickyColumn && props.sticky) {
                            cellRefs.current[colIndex] = el;
                        }
                    }, style: stickyColumn && props.sticky
                        ? {
                            left: colIndex > 0
                                ? cellWidths.slice(0, colIndex).reduce((a, b) => a + b, 0)
                                : undefined,
                        }
                        : undefined, row: row, rowIndex: rowIndex, rowSpan: props.groupValues ? rowSpans[props.field][rowIndex] : undefined, colSpan: spanCount, a11yHighlightType: a11yHighlightType, a11yHighlightText: a11yHighlightText, a11ySelectedText: a11ySelectedText, isRowHighlighted: isRowHighlighted, isRowSelected: isRowSelected, id: (props.cellId && props.cellId(row, rowIndex)) || undefined, headers: (props.cellHeaders && props.cellHeaders(row, rowIndex)) || undefined }, props, { sticky: stickyColumn && props.sticky })));
            }),
            expander === EXPANDER_RIGHT && renderExpander()),
        expandableRows && !isDisabled && (React__default.createElement(ExpanderRow, { id: rowId, colSpan: columns.length + 1, row: row, rowIndex: rowIndex, isExpanded: isExpanded, expandableRowComponent: expandableRowComponent, expanderPosition: expander, className: rowClassName }))));
};

/**
 * TODO: this should go to the hooks package as a separate hook
 */
const useOnScreen = (ref, rootMargin = '0px') => {
    // State and setter for storing whether element is visible
    const [isVisible, setIsVisible] = useState(false);
    const [isTopAbove, setIsTopAbove] = useState(false);
    const [isAbove, setIsAbove] = useState(false);
    const [isBelow, setIsBelow] = useState(false);
    const isScrolling = useRef(false);
    const [isMounted, setIsMounted] = useState(true);
    useEffect(() => {
        let observer;
        let interval;
        const scrollEventHandler = () => {
            isScrolling.current = true;
        };
        if (isMounted) {
            observer = new window.IntersectionObserver(([entry]) => {
                setIsTopAbove(entry.boundingClientRect.top < 0);
                setIsVisible(entry.isIntersecting);
                setIsAbove(!entry.isIntersecting && entry.boundingClientRect.top > 0);
                setIsBelow(!entry.isIntersecting && entry.boundingClientRect.top <= 0);
            }, {
                root: null,
                threshold: 0,
                rootMargin,
            });
            interval = setInterval(() => {
                if (isScrolling.current && ref.current) {
                    isScrolling.current = false;
                    const offset = ref.current.getBoundingClientRect();
                    setIsTopAbove(offset.top < 0);
                }
            }, 300);
            if (ref.current)
                observer.observe(ref.current);
            window.addEventListener('scroll', scrollEventHandler);
        }
        return () => {
            setIsMounted(false);
            if (ref.current)
                observer.unobserve(ref.current);
            window.removeEventListener('scroll', scrollEventHandler);
            clearInterval(interval);
        };
    }, []); // Empty array ensures that effect is only run on mount and unmount
    return { isVisible, isTopAbove, isAbove, isBelow };
};

const Table = (_a) => {
    var { tableId, children, data, condensed, borderless, borderlessColumn, striped, headless, light, initialSort, onSort, sortFn, sortOrder, isExternalSort = false, noStretch, responsive, tabIndex, topScroll, forceResize = false, className, caption, captionAlignment = 'left', captionFontWeight = 'regular', captionFontSize = 'regular', classNameCaption, columnTitleSeparator, a11yHighlightType = 'append', a11yHighlightText = 'highlighted cell', a11ySelectedText = 'selected cell', summary, sortType = SORT_DEFAULT, expandableRows, 
    // todo: why there is no handler that uses expandOnRowClick?
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    expandOnRowClick, expanderPosition = EXPANDER_RIGHT, stickyHeader, stickyHeaderParent, stickyColumn, detachedHeaderComponents = 'none', detachedColumnComponents = 'none', overflowX, positionRelative, maxHeight, rowClassNameFunc, rowKeyFunc, rowAttributes, rowAttributesFunc, tableAttributesFunc, forceColumnsUpdate } = _a, rowProps = __rest(_a, ["tableId", "children", "data", "condensed", "borderless", "borderlessColumn", "striped", "headless", "light", "initialSort", "onSort", "sortFn", "sortOrder", "isExternalSort", "noStretch", "responsive", "tabIndex", "topScroll", "forceResize", "className", "caption", "captionAlignment", "captionFontWeight", "captionFontSize", "classNameCaption", "columnTitleSeparator", "a11yHighlightType", "a11yHighlightText", "a11ySelectedText", "summary", "sortType", "expandableRows", "expandOnRowClick", "expanderPosition", "stickyHeader", "stickyHeaderParent", "stickyColumn", "detachedHeaderComponents", "detachedColumnComponents", "overflowX", "positionRelative", "maxHeight", "rowClassNameFunc", "rowKeyFunc", "rowAttributes", "rowAttributesFunc", "tableAttributesFunc", "forceColumnsUpdate"]);
    const [sortState, setSortState] = useState(initialSort || {});
    const [loading, setLoading] = useState(false);
    const [scrollIsOn, setScrollIsOn] = useState(false);
    const [tableVisibleWidth, setTableVisibleWidth] = useState(0);
    const [tableWidth, setTableWidth] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [focusedField, setFocusedField] = useState(null);
    const tableRef = useRef(null);
    const wrapperRef = useRef(null);
    const topScrollRef = useRef(null);
    const headerTableRef = useRef(null);
    const headerWrapperRef = useRef(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    tableId = tableId || `apricot_table_${uid}`;
    const tblKey = `key_tbl_${tableId}`;
    const wrapperKey = `key_wrapper_${tableId}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [sortedData, setSortedData] = useState([]);
    /** Build a list of columns from children */
    const getColumns = () => flattenChildren(children).filter(child => !!child);
    /** Hold the updated list of flattened children whenever they are updated or forced to be updated */
    const [flatColumns, setFlatColumns] = useState(getColumns());
    /** If children change and forceColumnsUpdate is not set - skip the update */
    useEffect(() => {
        if (!forceColumnsUpdate) {
            setFlatColumns(getColumns());
        }
    }, [children]);
    /** if forceChickenUpdate changes, and it evaluates as JS `true` - force update the columns */
    useEffect(() => {
        if (forceColumnsUpdate) {
            setFlatColumns(getColumns());
        }
    }, [forceColumnsUpdate]);
    /**
     * Get sorted data if sort state is defined
     */
    const getSortedData = () => {
        return sortFn
            ? sortFn(data, sortState)
            : orderBy(data, [sortState.field], [sortState.ascending ? 'asc' : 'desc']);
    };
    useEffect(() => {
        if (sortOrder === null || sortOrder === void 0 ? void 0 : sortOrder.field) {
            setSortState(sortOrder);
        }
    }, [sortOrder]);
    useEffect(() => {
        setSortedData(sortState && !isExternalSort ? getSortedData() : data);
    }, [sortState, data]);
    const resizeColumnWidths = () => {
        if (headerTableRef.current) {
            const mainTableCells = tableRef.current.querySelectorAll('tr th');
            headerTableRef.current.querySelectorAll('tr th').forEach((el, index) => {
                // eslint-disable-next-line no-param-reassign
                el.style.width = `${mainTableCells[index].getBoundingClientRect().width}px`;
            });
            setHeaderHeight(headerTableRef.current.getBoundingClientRect().height);
        }
    };
    const { ref: refResizeDetector } = useResizeDetector({ onResize: resizeColumnWidths });
    /** recalculate columns on major table change */
    useEffect(() => {
        if (stickyHeader) {
            setTimeout(() => {
                resizeColumnWidths();
            }, 300);
        }
    }, [sortedData, stickyHeader, expandableRows]);
    // store rowSpans for grouped fields in an array of numbers
    const rowSpans = {};
    // store the previous row indexes for grouped fields as numbers
    const prevRowIndexes = {};
    // loop through all the rows and calculate rowSpans
    if (sortedData) {
        sortedData.forEach((row, rowIndex) => {
            // pick only columns with groupValues === true
            flatColumns.forEach(({ props: { groupValues, field } }) => {
                if (groupValues) {
                    if (!rowSpans[field]) {
                        rowSpans[field] = new Array(sortedData.length);
                    }
                    const prevRowIndex = prevRowIndexes[field];
                    // if the current cell value is the same - increment the prev rowSpan and set the current to 0
                    if (!isNil(prevRowIndex) && sortedData[prevRowIndex][field] === row[field]) {
                        rowSpans[field][rowIndex] = 0;
                        rowSpans[field][prevRowIndex] += 1;
                    }
                    else {
                        // if this cell does not match the previous - save it as the previous reference
                        rowSpans[field][rowIndex] = 1;
                        prevRowIndexes[field] = rowIndex;
                    }
                }
            });
        });
    }
    const tableClassName = cn('cb-table', 'cb-table-react', light && 'cb-table-light', borderlessColumn && 'cb-no-border-left-right', condensed && 'cb-table-condensed', borderless && 'cb-no-border', striped && 'cb-table-striped-reverse', expandableRows && 'cb-table-expandable', stickyColumn && 'cb-sticky-table-column', noStretch && 'cb-table-no-stretch', className);
    /**
     * Top Scroll if available - sync the table scroll
     */
    const topScrollEventHandler = () => {
        if (topScroll && wrapperRef.current && topScrollRef.current) {
            wrapperRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        }
    };
    /**
     * Wrapper Scroll event handler - sync top scroll
     */
    const wrapperScrollEventListener = () => {
        if (wrapperRef.current) {
            if (topScrollRef.current) {
                topScrollRef.current.scrollLeft = wrapperRef.current.scrollLeft;
            }
            headerWrapperRef.current.scrollLeft = wrapperRef.current.scrollLeft;
        }
    };
    /**
     * Window Resize event handler - save the current widths
     */
    const resizeEventHandler = () => {
        if (tableRef.current)
            setTableWidth(tableRef.current.offsetWidth);
        if (wrapperRef.current) {
            setTableVisibleWidth(wrapperRef.current.parentElement.offsetWidth);
        }
    };
    /**
     * Control Mount - add window resize event listeners
     */
    useEffect(() => {
        resizeEventHandler();
        window.addEventListener('resize', resizeEventHandler);
        // remove the event on dismount
        return () => {
            window.removeEventListener('resize', resizeEventHandler);
        };
    }, []);
    /**
     * Recalculate the width of the table and parent
     */
    useEffect(() => {
        if (typeof forceResize === 'boolean') {
            if (forceResize)
                resizeEventHandler();
        }
        else {
            resizeEventHandler();
        }
    }, [forceResize]);
    /**
     * Children change event - handle as a regular resize with a timeout
     */
    useEffect(() => {
        setTimeout(() => {
            resizeEventHandler();
        }, 100);
    }, [flatColumns]);
    useEffect(() => {
        if (wrapperRef.current) {
            wrapperRef.current.addEventListener('scroll', wrapperScrollEventListener);
            wrapperScrollEventListener();
        }
    }, [topScroll, wrapperRef.current]);
    /**
     * TopScroll is shown - add event handlers
     */
    useEffect(() => {
        let scrollRef = null;
        if (topScrollRef.current) {
            topScrollRef.current.addEventListener('scroll', topScrollEventHandler);
            scrollRef = topScrollRef.current;
        }
        return () => {
            if (scrollRef) {
                scrollRef.removeEventListener('scroll', topScrollEventHandler);
            }
        };
    }, [topScroll, scrollIsOn]);
    /**
     * Widths are changed - decide if the scroll is shown
     */
    useEffect(() => {
        setScrollIsOn(tableVisibleWidth < tableWidth);
        if (headerTableRef.current)
            headerTableRef.current.style.width = `${tableRef.current.getBoundingClientRect().width}px`;
        if (headerWrapperRef.current)
            headerWrapperRef.current.style.width = `${wrapperRef.current.getBoundingClientRect().width}px`;
    }, [tableVisibleWidth, tableWidth]);
    const { isTopAbove, isVisible } = useOnScreen(tableRef, `-${headerHeight}px` /* "-200px" */);
    const onSortClick = (field) => __awaiter(void 0, void 0, void 0, function* () {
        if (onSort) {
            setLoading(true);
            yield onSort(field, field !== sortState.field || !sortState.ascending);
            setLoading(false);
            if (!sortOrder) {
                setSortState({
                    field,
                    ascending: field !== sortState.field || !sortState.ascending,
                });
            }
        }
        else {
            setSortState({
                field,
                ascending: field !== sortState.field || !sortState.ascending,
            });
        }
    });
    // Context for the stickyMode header
    const stickyModeContext = useMemo(() => ({ stickyMode: true }), []);
    const headerTable = stickyHeader && !headless && (React__default.createElement("table", Object.assign({ key: `${tblKey}_h`, id: `${tableId}_hdr`, className: tableClassName, summary: summary, ref: headerTableRef, "aria-hidden": "true" }, (tableAttributesFunc && attributesFromTupleArray(tableAttributesFunc()))),
        caption && (React__default.createElement("caption", { className: cn(captionAlignment === 'center' && `cb-caption-center`, captionFontWeight === 'bold' && `cb-caption-bold`, captionFontSize === 'large' && `cb-caption-large`, isVisible && isTopAbove ? 'display-none' : '', classNameCaption) }, caption)),
        React__default.createElement(TableHeaderContext.Provider, { value: stickyModeContext },
            React__default.createElement(TableHeader, { expanderPosition: expandableRows ? expanderPosition : EXPANDER_NONE, columns: flatColumns, columnTitleSeparator: columnTitleSeparator, onSortClick: onSortClick, sortState: sortState, sortType: sortType, focusedFieldDispatch: [focusedField, setFocusedField] }))));
    const table = (React__default.createElement("div", { ref: refResizeDetector },
        React__default.createElement("table", Object.assign({ key: tblKey, id: tableId, className: tableClassName, summary: summary, tabIndex: tabIndex, "data-cbtrack-comp": "apricot-react:table", ref: tableRef }, (tableAttributesFunc && attributesFromTupleArray(tableAttributesFunc()))),
            caption && (React__default.createElement("caption", { className: cn(captionAlignment === 'center' && `cb-caption-center`, captionFontWeight === 'bold' && `cb-caption-bold`, captionFontSize === 'large' && `cb-caption-large`, classNameCaption) }, caption)),
            !headless && (React__default.createElement(TableHeader, { columns: flatColumns, columnTitleSeparator: columnTitleSeparator, onSortClick: onSortClick, sortState: sortState, sortType: sortType, expanderPosition: expandableRows ? expanderPosition : EXPANDER_NONE, focusedFieldDispatch: [focusedField, setFocusedField], stickyHeaderParent: stickyHeaderParent, stickyColumn: stickyColumn })),
            React__default.createElement("tbody", null, sortedData.map((row, rowIndex) => {
                const isRowStriped = striped ? rowIndex % 2 === 1 : undefined;
                return (React__default.createElement(TableRow, Object.assign({ key: rowKeyFunc ? rowKeyFunc(row, rowIndex) : rowIndex, row: row, rowIndex: rowIndex, expanderPosition: expanderPosition, expandableRows: expandableRows, columns: flatColumns, rowSpans: rowSpans, a11yHighlightType: a11yHighlightType, a11yHighlightText: a11yHighlightText, a11ySelectedText: a11ySelectedText, isRowStriped: isRowStriped, rowAttributes: rowAttributes, rowClassNameFunc: rowClassNameFunc, rowAttributesFunc: rowAttributesFunc, stickyColumn: stickyColumn }, rowProps)));
            })))));
    const renderDetachedHeaderComponents = (columns) => {
        return columns.map((column, columnIndex) => {
            const DetachedHeaderComponent = column.props.detachedHeaderComponent;
            if (DetachedHeaderComponent) {
                const { field, title } = column.props;
                return (React__default.createElement(DetachedHeaderComponent, { key: `detached-header-component-${columnIndex}`, field: field, title: title, rowIndex: columnIndex }));
            }
            return null;
        });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderDetachedColumnComponents = (columns, cData) => {
        return columns.map((column, columnIndex) => {
            const DetachedColumnComponent = column.props.detachedColumnComponent;
            if (DetachedColumnComponent) {
                const { field } = column.props;
                return cData.map((row, rowIndex) => (React__default.createElement(DetachedColumnComponent, { key: `portal-component-${columnIndex}-${rowIndex}`, field: field, rowIndex: rowIndex, value: row[field], row: row })));
            }
            return null;
        });
    };
    return (React__default.createElement(React__default.Fragment, null,
        detachedHeaderComponents === 'top' && (React__default.createElement("div", { className: "cb-top-detached-header-components" }, renderDetachedHeaderComponents(flatColumns))),
        detachedColumnComponents === 'top' && (React__default.createElement("div", { className: "cb-top-detached-column-components" }, renderDetachedColumnComponents(flatColumns, sortedData))),
        React__default.createElement(LoadingWrapper, { loading: loading, positionRelative: positionRelative, overflowX: overflowX, maxHeight: maxHeight },
            React__default.createElement(React__default.Fragment, null,
                React__default.createElement("div", { className: cn('cb-table-react', 'cb-table-header', isVisible && isTopAbove ? 'cb-sticky-table' : ''), style: { width: topScroll ? tableVisibleWidth : undefined } },
                    topScroll && scrollIsOn && (React__default.createElement("div", { className: "cb-table-top-scroll-holder", style: { width: tableVisibleWidth }, ref: topScrollRef },
                        React__default.createElement("div", { className: "cb-table-top-scroll", style: { width: `${tableWidth}px` } }))),
                    React__default.createElement("div", { className: cn('cb-table-wrapper', responsive && 'cb-table-responsive'), ref: headerWrapperRef }, headerTable)),
                topScroll && scrollIsOn && React__default.createElement("div", { className: "cb-table-top-scroll-holder" }),
                React__default.createElement("div", { key: wrapperKey, className: cn('cb-table-wrapper', responsive && 'cb-table-responsive', topScroll && 'with-top-scroll'), 
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                    tabIndex: scrollIsOn && responsive ? 0 : undefined, "aria-label": scrollIsOn && responsive ? 'scrolling table' : undefined, role: scrollIsOn && responsive ? 'region' : undefined, ref: wrapperRef, style: { width: topScroll ? tableVisibleWidth : undefined } }, table))),
        detachedHeaderComponents === 'bottom' && (React__default.createElement("div", { className: "cb-bottom-detached-header-components" }, renderDetachedHeaderComponents(flatColumns))),
        detachedColumnComponents === 'bottom' && (React__default.createElement("div", { className: "cb-bottom-detached-column-components" }, renderDetachedColumnComponents(flatColumns, sortedData)))));
};
const Column = () => null;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typedTableComponents = (data = []) => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Table: Table,
    Column: Column,
});

const TabContext = React__default.createContext({ hasIcon: false, iconOnly: false });
const Tab = ({ tabId, panelId, label, icon, selected = false, disabled = false, floatRight = false, labelClassName, }) => {
    const { hasIcon, iconOnly } = useContext(TabContext);
    return (React__default.createElement("li", { role: "presentation", className: cn(floatRight && 'cb-tab-right') },
        React__default.createElement("a", { id: tabId, href: `#${panelId}`, className: cn('cb-menu-link', disabled && 'cb-disabled', selected && 'cb-selected', labelClassName), role: "tab", "aria-disabled": disabled || undefined },
            hasIcon && icon && React__default.createElement(Icon, { name: icon, decorative: true }),
            React__default.createElement("span", { className: cn(iconOnly && 'sr-only') }, label))));
};
const Tabs = (_a) => {
    var { carouselId, children, hasIcon, hasFloatRight, iconOnly, condensed, hasCarousel = false, ariaLabelCarousel, isControlled = false, onTabChange, className, cbRef } = _a, props = __rest(_a, ["carouselId", "children", "hasIcon", "hasFloatRight", "iconOnly", "condensed", "hasCarousel", "ariaLabelCarousel", "isControlled", "onTabChange", "className", "cbRef"]);
    const tabRef = useRef(null);
    const panelRef = useRef(null);
    const flatChildren = flattenChildren(children);
    const tabsPlugin = useRef(null);
    useEffect(() => {
        tabsPlugin.current = CBTab({
            elem: tabRef.current,
            react: isControlled,
        });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = tabsPlugin.current;
        }
        if (tabRef.current) {
            tabRef.current.addEventListener('apricot_tabChanged', (e) => {
                const event = e;
                const panelId = event && event.data ? event.data.id : null;
                // call onTabChange
                if (onTabChange && panelId)
                    onTabChange(panelId);
            });
        }
        return () => {
            if (tabsPlugin.current) {
                tabsPlugin.current.destroy();
            }
        };
    }, []);
    const context = useMemo(() => ({ hasIcon: !!hasIcon, iconOnly: !!iconOnly }), [hasIcon, iconOnly]);
    const tabsMenu = (React__default.createElement("div", { className: cn('cb-tabs-menu', hasIcon && 'cb-tabs-menu-icon', condensed && 'cb-condensed', iconOnly && hasIcon && 'cb-tabs-menu-icon-only'), "data-cbtrack-comp": "apricot-react:tabs", ref: tabRef },
        React__default.createElement(TabContext.Provider, { value: context },
            React__default.createElement("ul", { role: "tablist", className: cn(hasFloatRight && 'cb-tabs-float-right') }, flatChildren.map(tab => {
                return tab;
            })))));
    return (React__default.createElement(React__default.Fragment, null,
        hasCarousel ? (React__default.createElement(HorizontalCarousel, { carouselId: carouselId, ariaLabel: ariaLabelCarousel, variant: condensed ? 'menu-condensed' : 'menu', bgClass: "cb-white-bg", arrowClass: "cb-black1-color" }, tabsMenu)) : (tabsMenu),
        React__default.createElement("div", Object.assign({ className: cn('cb-tabs-container', className), ref: panelRef }, props), flatChildren.map((tab, index) => {
            // labelClassName is not used here but needs to be removed from props
            // eslint-disable-next-line @typescript-eslint/no-shadow,@typescript-eslint/no-unused-vars
            const _a = tab.props, { panelId, selected, children, className, tabId, labelClassName } = _a, props = __rest(_a, ["panelId", "selected", "children", "className", "tabId", "labelClassName"]);
            return (React__default.createElement("div", Object.assign({ key: tabId || `tab_${index}`, id: `${panelId}`, role: "tabpanel", className: cn(selected && `cb-selected`, 'cb-tabs-content', className) }, props), children));
        }))));
};

// TBD: Add story with useTransition
const TabR = React__default.forwardRef((_a, ref) => {
    var { id, controlsID, selected = false, focus = false, disabled = false, className, anchorClassName, floatRight, children } = _a, props = __rest(_a, ["id", "controlsID", "selected", "focus", "disabled", "className", "anchorClassName", "floatRight", "children"]);
    const tabRef = useRef(null) || ref;
    const hasMounted = useRef(false);
    useImperativeHandle(ref, () => tabRef.current);
    useEffect(() => {
        var _a;
        if (hasMounted.current) {
            if (focus) {
                (_a = tabRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
        else {
            hasMounted.current = true;
        }
    }, [focus]);
    return (React__default.createElement("li", { role: "presentation", className: cn(floatRight && 'cb-tab-right', className), "data-cb-type": "tabr" },
        React__default.createElement("a", Object.assign({ className: cn('cb-menu-link', disabled && 'cb-disabled', selected && 'cb-selected', anchorClassName), ref: tabRef, role: "tab", id: id, "aria-selected": selected ? 'true' : 'false', "aria-disabled": disabled ? 'true' : 'false', tabIndex: selected ? 0 : -1, "aria-controls": controlsID, href: "/#" }, props), children)));
});
TabR.displayName = 'TabR';

// ------- Helper Functions for Type Check
const makeTypeChecker = (tabsRole) => {
    return element => {
        if (element && element.type) {
            const displayName = element.type.displayName || element.type.name || 'Unknown';
            return displayName === tabsRole;
        }
        return false;
    };
};
const isTab = makeTypeChecker('TabR');
const isTabList = makeTypeChecker('TabListR');
const isTabPanel = makeTypeChecker('TabPanelR');
const isTabPanelList = makeTypeChecker('TabPanelListR');
const isTabChild = (child) => {
    return isTab(child) || isTabList(child) || isTabPanel(child);
};
const deepMap = (children, callback) => {
    return Children.map(children, (child) => {
        if (child === null)
            return null;
        if (isTabChild(child)) {
            return callback(child);
        }
        if (child.props && child.props.children && typeof child.props.children === 'object') {
            // Clone the child that has children and map them too
            return cloneElement(child, Object.assign(Object.assign({}, child.props), { children: deepMap(child.props.children, callback) }));
        }
        return child;
    });
};
const deepForEach = (children, callback) => {
    return Children.forEach(children, (child) => {
        var _a;
        if (child === null)
            return;
        if (isTab(child) || isTabPanel(child)) {
            callback(child);
        }
        else if (child.props && ((_a = child === null || child === void 0 ? void 0 : child.props) === null || _a === void 0 ? void 0 : _a.children) && typeof child.props.children === 'object') {
            if (isTabList(child))
                callback(child);
            if (isTabPanelList(child))
                callback(child);
            deepForEach(child.props.children, callback);
        }
    });
};
const getTabsCount = (children) => {
    let tabCount = 0;
    deepForEach(children, child => {
        if (isTab(child))
            tabCount += 1;
    });
    return tabCount;
};
const isNode = node => {
    return node && 'getAttribute' in node;
};
const isTabNode = node => {
    return isNode(node) && node.getAttribute('data-cb-type') === 'tabr';
};
const isTabDisabled = node => {
    let tag = node;
    if (!isNode(tag))
        return false;
    if (isTabNode(tag)) {
        tag = tag.querySelector('a');
    }
    if (tag.getAttribute('aria-disabled') === 'true')
        return true;
    const parent = tag.closest('a');
    return isNode(parent) && parent.getAttribute('aria-disabled') === 'true';
};

const TabsR = React.forwardRef((_a, ref) => {
    var { id, defaultTab, selectedTab = null, className, renderTabPanel, onSelected, onSelectTab, children } = _a, props = __rest(_a, ["id", "defaultTab", "selectedTab", "className", "renderTabPanel", "onSelected", "onSelectTab", "children"]);
    const tabsRef = useRef(null);
    useImperativeHandle(ref, () => tabsRef.current);
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    const tabNodes = useRef([]);
    const tabIds = useRef([]);
    const panelIds = useRef([]);
    const [controlled] = useState(!!selectedTab);
    const [selectedIndex, setSelectedIndex] = useState(selectedTab || defaultTab || 0);
    const [focusIndex, setFocusIndex] = useState(selectedTab || defaultTab || 0);
    const getTabsCount$1 = () => {
        return getTabsCount(children);
    };
    const getTab = index => {
        return tabNodes.current[`tabs-${index}`];
    };
    const setSelected = (index, event) => {
        // Check index boundary
        if (index < 0 || index >= getTabsCount$1())
            return;
        // Call change event handler
        if (!controlled) {
            setFocusIndex(index);
            setSelectedIndex(index);
        }
        /* eslint-disable-next-line @typescript-eslint/no-unused-expressions */
        onSelected && onSelected(index, selectedIndex, event);
    };
    const getNextTab = index => {
        const count = getTabsCount$1();
        // Look for non-disabled tab from index to the last tab on the right
        for (let i = index + 1; i < count; i += 1) {
            if (!isTabDisabled(getTab(i))) {
                return i;
            }
        }
        // If no tab found, continue searching from first on left to index
        for (let i = 0; i < index; i += 1) {
            if (!isTabDisabled(getTab(i))) {
                return i;
            }
        }
        // All tabs are disabled, return index
        /* istanbul ignore next */
        return index;
    };
    const getPrevTab = index => {
        let i = index;
        // Look for non-disabled tab from index to first tab on the left
        i -= 1;
        while (i >= 0) {
            if (!isTabDisabled(getTab(i))) {
                return i;
            }
            i -= 1;
        }
        // If no tab found, continue searching from last tab on right to index
        i = getTabsCount$1();
        i -= 1;
        while (i > index) {
            if (!isTabDisabled(getTab(i))) {
                return i;
            }
            i -= 1;
        }
        // All tabs are disabled, return index
        /* istanbul ignore next */
        return index;
    };
    const GetChildren = () => {
        let index = 0;
        tabIds.current = tabIds.current || [];
        panelIds.current = panelIds.current || [];
        // let diff = tabIds.current.length - getTabsCount();
        // Add ids if new tabs have been added
        // Don't bother removing ids, just keep them in case they are added again
        // This is more efficient, and keeps the uuid counter under control
        const newID = useUID();
        const tId = id || newID;
        React.Children.map(children, child => {
            if (React.isValidElement(child)) {
                if (isTabList(child)) {
                    React.Children.map(child.props.children, tChild => {
                        var _a, _b, _c;
                        if (isTab(tChild)) {
                            if (tChild.props.id) {
                                (_a = tabIds.current) === null || _a === void 0 ? void 0 : _a.push(tChild.props.id);
                            }
                            else {
                                (_b = tabIds.current) === null || _b === void 0 ? void 0 : _b.push(`tab_${tId}${(_c = tabIds.current) === null || _c === void 0 ? void 0 : _c.length}`);
                            }
                        }
                    });
                }
                else if (isTabPanelList(child)) {
                    React.Children.map(child.props.children, pChild => {
                        var _a, _b, _c;
                        if (isTabPanel(pChild)) {
                            if (pChild.props.id) {
                                (_a = panelIds.current) === null || _a === void 0 ? void 0 : _a.push(pChild.props.id);
                            }
                            else {
                                (_b = panelIds.current) === null || _b === void 0 ? void 0 : _b.push(`panel_${tId}${(_c = panelIds.current) === null || _c === void 0 ? void 0 : _c.length}`);
                            }
                        }
                    });
                }
            }
        });
        // Map children to dynamically setup refs
        return deepMap(children, child => {
            let result = child;
            // Clone TabList and Tab components to have refs
            if (isTabList(child)) {
                let listIndex = 0;
                result = cloneElement(child, {
                    children: deepMap(child.props.children, tab => {
                        const key = `tabs-${listIndex}`;
                        const selected = selectedIndex === listIndex;
                        const focused = focusIndex === listIndex;
                        const childProps = {
                            ref: node => {
                                tabNodes.current[key] = node;
                            },
                            id: tabIds.current[listIndex],
                            controlsID: panelIds.current[listIndex],
                            selected,
                            focus: focused,
                        };
                        listIndex += 1;
                        return cloneElement(tab, childProps);
                    }),
                });
            }
            else if (isTabPanel(child)) {
                const childProps = {
                    id: panelIds.current[index],
                    labelledbyID: tabIds.current[index],
                    selected: selectedIndex === index,
                    forceRender: renderTabPanel,
                };
                index += 1;
                result = cloneElement(child, childProps);
            }
            return result;
        });
    };
    //  Determine if a node from event.target is a Tab element for the current Tabs container.
    //  If the clicked element is not a Tab, it returns false.
    //  If it finds another Tabs container between the Tab and the correct one, it returns false.
    const isTabFromContainer = node => {
        // return immediately if the clicked element is not a Tab.
        if (!isTabNode(node)) {
            return false;
        }
        // Check if the first occurrence of a Tabs container is this tab.
        let nodeAncestor = node.parentElement;
        do {
            if (nodeAncestor === tabsRef.current) {
                return true;
            }
            if (nodeAncestor.getAttribute('data-cb-type') === 'tabsr') {
                break;
            }
            nodeAncestor = nodeAncestor.parentElement;
        } while (nodeAncestor);
        return false;
    };
    const handleClick = e => {
        var _a;
        let node = e.target;
        // mak sure node is the LI tag
        node = (_a = node.closest('a')) === null || _a === void 0 ? void 0 : _a.parentNode;
        do {
            if (isTabFromContainer(node)) {
                e.preventDefault();
                if (isTabDisabled(node)) {
                    return;
                }
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                const tmp = [].slice.call(node.parentNode.children).filter(isTabNode);
                const index = tmp.indexOf(node);
                // Call onSelectTab if provided
                if (onSelectTab) {
                    const shouldProceed = onSelectTab(index, e);
                    if (!shouldProceed) {
                        return; // Stop default behavior
                    }
                }
                setSelected(index, e);
                return;
            }
            node = node === null || node === void 0 ? void 0 : node.parentNode;
        } while (node != null);
    };
    const handleKeyDown = e => {
        var _a;
        let node = e.target;
        // mak sure node is the LI tag
        node = (_a = node.closest('a')) === null || _a === void 0 ? void 0 : _a.parentNode;
        if (isTabFromContainer(node)) {
            let index = focusIndex;
            let useSelectedIndex = false;
            let preventDefault = false;
            const k = e.which || e.keyCode;
            // space or enter
            if (k === 32 || k === 13) {
                preventDefault = true;
                handleClick(e);
            }
            /* arrow left */
            if (k === 37) {
                index = getPrevTab(index);
                preventDefault = true;
                useSelectedIndex = true;
            }
            else if (k === 39) {
                /* arrow right */
                index = getNextTab(index);
                preventDefault = true;
                useSelectedIndex = true;
            }
            // This prevents scrollbars from moving around
            if (preventDefault) {
                e.preventDefault();
            }
            // Only use the selected index in the state if we're not using the tabbed index
            if (useSelectedIndex) {
                // setSelected(index, e);
                setFocusIndex(index);
            }
        }
    };
    const tabsCount = getTabsCount$1();
    useEffect(() => {
        if (selectedIndex != null && !controlled) {
            const maxTabIndex = Math.max(0, tabsCount - 1);
            setSelectedIndex(Math.min(selectedIndex, maxTabIndex));
        }
    }, [tabsCount]);
    useEffect(() => {
        /* eslint-disable-next-line @typescript-eslint/no-unused-expressions */
        selectedTab !== null && setSelectedIndex(selectedTab);
    }, [selectedTab]);
    return (React.createElement("div", Object.assign({}, props, { className: cn('cb-tabs-react', className), onClick: handleClick, onKeyDown: handleKeyDown, ref: node => {
            tabsRef.current = node;
        }, "data-cbtrack-comp": "apricot-react:tabs", "data-cb-type": "tabsr" }), GetChildren()));
});
TabsR.displayName = 'TabsR';

const TabListR = (_a) => {
    var { className, condensed = false, hasIcon = false, iconOnly = false, carouselId, hasCarousel = false, hasFloatRight = false, ariaLabelCarousel, children } = _a, props = __rest(_a, ["className", "condensed", "hasIcon", "iconOnly", "carouselId", "hasCarousel", "hasFloatRight", "ariaLabelCarousel", "children"]);
    const tabMenu = (React__default.createElement("div", Object.assign({ className: cn('cb-tabs-menu', condensed && 'cb-condensed', hasIcon && 'cb-tabs-menu-icon', iconOnly && hasIcon && ['cb-tabs-menu-icon', 'cb-tabs-menu-icon-only'], className) }, props),
        React__default.createElement("ul", { role: "tablist", className: cn(hasFloatRight && 'cb-tabs-float-right') }, children)));
    return (React__default.createElement(React__default.Fragment, null, hasCarousel ? (React__default.createElement(HorizontalCarousel, { carouselId: carouselId, ariaLabel: ariaLabelCarousel, variant: condensed ? 'menu-condensed' : 'menu', bgClass: "cb-white-bg", arrowClass: "cb-black1-color", contentClass: hasFloatRight ? 'cb-width-100' : undefined }, tabMenu)) : (tabMenu)));
};
TabListR.displayName = 'TabListR';

const TabPanelR = (_a) => {
    var { id, labelledbyID, selected = false, forceRender = false, className, children } = _a, props = __rest(_a, ["id", "labelledbyID", "selected", "forceRender", "className", "children"]);
    return (React__default.createElement("div", Object.assign({ id: id, role: "tabpanel", className: cn('cb-tabs-content', selected && `cb-selected`, className), "aria-labelledby": labelledbyID, tabIndex: selected ? 0 : -1 }, props), forceRender || selected ? children : null));
};
TabPanelR.displayName = 'TabPanelR';

const TabPanelListR = (_a) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    return (React__default.createElement("div", Object.assign({ className: cn('cb-tabs-container', className) }, props), children));
};
TabPanelListR.displayName = 'TabPanelListR';

const toastType = ['alternative', 'success', 'caution', 'warning', 'info'];
const iconType = ['check-fill', 'caution-fill', 'exclamation-fill', 'info-circle'];

// ---------------------------------- Toasts
const Toasts = ({ children, bottom = false, offsetTop = 0, offsetBottom = 16 }) => {
    const [childrenList, setChildren] = useState([]);
    useEffect(() => {
        // Reverse the order for toast effect
        setChildren(React__default.Children.toArray(children).reverse());
    }, [children]);
    return (React__default.createElement("div", { className: cn('cb-toast-container', bottom && 'cb-toast-container-bottom'), style: {
            top: !bottom ? offsetTop : 0,
            bottom: bottom ? offsetBottom : 'auto',
        } }, childrenList));
};
// ---------------------------------- Toast
const Toast = forwardRef(({ toastId, controlled = false, show = false, timeout = 5000, type, icon, className, message, ctaLabel, ctaAriaLabel, ctaCallback = null, closeBtn = true, closeAriaLabel = 'Close alert', onDismiss = null, autoClose = true, escClose = true, }, ref) => {
    const iconMsg = type === 'success' || type === 'caution' || type === 'warning' || type === 'info' ? type : null;
    const toastClass = cn('cb-toast', type && `cb-toast-${type}`, className);
    const refToast = useRef(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    toastId = toastId || `apricot_toast_${uid}`;
    const messageId = `apricot_msgToast_${uid}`;
    const [showToast, setDisplayState] = useState(true);
    useImperativeHandle(ref, () => refToast.current);
    // toast instance
    const toast = useRef(null);
    const removeToast = (hideEvent) => {
        var _a;
        const elem = hideEvent ? (_a = hideEvent.data) === null || _a === void 0 ? void 0 : _a.elem : null;
        if (elem && elem === refToast.current) {
            if (controlled) {
                // eslint-disable-next-line no-param-reassign
                show = false;
            }
            else {
                setDisplayState(false);
            }
        }
    };
    // general treatment
    useEffect(() => {
        if (!controlled) {
            toast.current = CBToast({
                elem: refToast.current,
                timeout,
                ctaCallback,
                onDismiss,
                escClose,
                autoClose,
                react: true,
            });
        }
    }, []);
    // controlled
    useEffect(() => {
        if (controlled) {
            if (show) {
                toast.current = CBToast({
                    elem: refToast.current,
                    timeout,
                    ctaCallback,
                    onDismiss,
                    escClose: false,
                    autoClose: false,
                    react: true,
                    controlled,
                });
            }
            else if (toast.current) {
                // clean up
                toast.current.hide();
            }
        }
    }, [show]);
    // remove Instance
    const isCalledRef = useRef(false);
    useEffect(() => {
        document.addEventListener('apricot_toast_hide', removeToast);
        return () => {
            // We just want this to run once
            if (toast.current && isCalledRef.current) {
                toast.current.destroy();
            }
            else if (toast.current) {
                isCalledRef.current = true;
            }
        };
    }, []);
    return (!controlled && showToast) || (controlled && show) ? (React__default.createElement("div", { role: ctaLabel ? 'alertdialog' : 'alert', className: toastClass, ref: refToast, "aria-hidden": true, id: toastId, "aria-describedby": ctaLabel ? messageId : undefined, "data-cbtrack-comp": "apricot-react:toast" },
        icon && (React__default.createElement("span", { className: `cb-icon cb-${icon} cb-margin-right-8`, "aria-hidden": !iconMsg ? true : undefined },
            React__default.createElement("span", { className: "sr-only" }, iconMsg))),
        typeof message === 'string' ? (React__default.createElement("p", { className: "cb-toast-msg", id: messageId }, message)) : (React__default.createElement("div", { className: "cb-toast-msg", id: messageId }, message)),
        ctaLabel && (React__default.createElement("a", { href: "/#", className: "cb-toast-action cb-margin-left-16", "aria-label": ctaAriaLabel }, ctaLabel)),
        closeBtn && (React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-square cb-btn-greyscale cb-btn-close cb-margin-left-8" },
            React__default.createElement("span", { className: "cb-icon cb-x-mark", "aria-hidden": "true" }),
            React__default.createElement("span", { className: "sr-only" }, closeAriaLabel))))) : null;
});

const tooltipPlacements = ['top', 'right', 'bottom', 'left'];

const Tooltip = ({ tooltipId, children, trigger, shadowRoot = false, triggerEvent = 'hover focus', light, repetitiveLabel = false, placement = 'top', className, onShow = null, onHide = null, cbRef, }) => {
    const ref = useRef(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    tooltipId = tooltipId || `apricot_tooltip_${uid}`;
    useEffect(() => {
        var _a, _b;
        const reference = typeof trigger === 'string' ? document.getElementById(trigger) : trigger;
        if (reference) {
            reference.setAttribute('aria-controls', tooltipId);
        }
        const tooltip = CBTooltip({
            elem: reference,
            trigger: triggerEvent,
            markup: false,
            html: true,
            placement,
            shadowRoot,
            title: ((_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.firstElementChild) === null || _b === void 0 ? void 0 : _b.innerHTML) || '',
            style: light ? 'cb-tooltip-light' : className,
            repetitiveLabel,
            onShow,
            onHide,
        });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = tooltip;
        }
        return () => {
            if (tooltip) {
                tooltip.destroy();
            }
        };
    }, []);
    return (React__default.createElement("div", { key: tooltipId, id: tooltipId, className: "cb-tooltip hidden", role: "tooltip", "data-cbtrack-comp": "apricot-react:tooltip", ref: ref },
        React__default.createElement("div", { className: "cb-tooltip-inner" }, children)));
};

// Define TreeIconSize as a subset of IconSize
const treeIconSizes = ['12', '14', '16'];

/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable max-lines-per-function */
const TreeView = ({ items, openIcon = 'down', ariaLabel = 'Main navigation', ariaLabelledbyID, ariaCurrent = 'page', closeIcon = 'right', iconSize = '12', activeTrail = [], reset = false, multiSelect = false, toggleSelectOnClick = false, title, controls = false, loader = true, spinnerProps, LabelComponent, clickStopPropagation = true, clickPreventDefault = true, onItemClick, onSelectedItemsChange, onReset, }) => {
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [selectedNodes, setSelectedNodes] = useState(new Set()); // Updated to support multiple selections
    const [dataArray, setDataArray] = useState(null);
    const treeRef = useRef(null);
    const handleToggle = (id) => {
        setExpandedNodes(prevExpandedNodes => {
            const newExpandedNodes = new Set(prevExpandedNodes);
            if (newExpandedNodes.has(id)) {
                newExpandedNodes.delete(id);
            }
            else {
                newExpandedNodes.add(id);
            }
            return newExpandedNodes;
        });
    };
    const updateNodesWithTabIndexZero = () => {
        var _a, _b;
        const focusableNodes = (_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('[role="treeitem"][tabIndex="0"]');
        focusableNodes === null || focusableNodes === void 0 ? void 0 : focusableNodes.forEach(node => {
            if (node instanceof HTMLElement) {
                // Set tabIndex to 0 for the current node
                node.setAttribute('tabIndex', '-1');
            }
        });
        // Set tabIndex to 0 for the first node
        const firstNode = (_b = treeRef.current) === null || _b === void 0 ? void 0 : _b.querySelector('[role="treeitem"]');
        if (firstNode instanceof HTMLElement) {
            firstNode.setAttribute('tabIndex', '0');
        }
    };
    const handleExpandAll = () => {
        const expandAllNodes = (nodes, expanded) => {
            nodes.forEach(node => {
                if (node.id && !node.disabled) {
                    expanded.add(node.id);
                }
                if (node.children && node.children.length > 0) {
                    expandAllNodes(node.children, expanded);
                }
            });
        };
        const allExpandedNodes = new Set();
        if (dataArray) {
            expandAllNodes(dataArray, allExpandedNodes);
        }
        setExpandedNodes(allExpandedNodes);
        // remove tabIndex from all nodes
        updateNodesWithTabIndexZero();
    };
    const handleCollapseAll = () => {
        var _a;
        setExpandedNodes(new Set());
        // Set tabIndex to 0 for the first node
        const firstNode = (_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('[role="treeitem"]');
        if (firstNode instanceof HTMLElement) {
            firstNode.setAttribute('tabIndex', '0');
        }
    };
    const handleSelect = (e, id, source = 'user') => {
        setSelectedNodes(prevSelectedNodes => {
            const newSelectedNodes = new Set(prevSelectedNodes);
            if (source === 'activeTrail') {
                // For activeTrail, always add the node to the selection
                newSelectedNodes.add(id);
            }
            else if (multiSelect) {
                if (e && (e.ctrlKey || e.metaKey)) {
                    // If Ctrl (Windows/Linux) or Cmd (macOS) is pressed, toggle selection
                    // Toggle selection for multi-select
                    if (newSelectedNodes.has(id)) {
                        newSelectedNodes.delete(id);
                    }
                    else {
                        newSelectedNodes.add(id);
                    }
                }
                else if (!e && multiSelect) {
                    newSelectedNodes.add(id);
                }
                else {
                    // If Ctrl/Cmd is not pressed, clear previous selections and select the current item
                    newSelectedNodes.clear();
                    newSelectedNodes.add(id);
                }
            }
            else if (toggleSelectOnClick) {
                // Single-select with toggle on click
                if (newSelectedNodes.has(id)) {
                    newSelectedNodes.delete(id);
                }
                else {
                    newSelectedNodes.clear();
                    newSelectedNodes.add(id);
                }
            }
            else {
                // Single-select: clear previous selection
                newSelectedNodes.clear();
                newSelectedNodes.add(id);
            }
            return newSelectedNodes;
        });
    };
    const handleClick = (e, node) => {
        if (clickStopPropagation) {
            e.stopPropagation();
        }
        if (clickPreventDefault) {
            e.preventDefault();
        }
        if (node.disabled) {
            return;
        }
        if (node.children && node.id) {
            handleToggle(node.id);
        }
        if (node.id) {
            handleSelect(e, node.id);
        }
        if (onItemClick) {
            onItemClick(node);
        }
    };
    const handleKeyDown = (e, node) => {
        var _a, _b;
        e.stopPropagation();
        if (!node.id)
            return;
        const isNodeExpanded = expandedNodes.has(node.id);
        const getParentNode = currentNode => {
            var _a;
            return (_a = currentNode.closest('ul')) === null || _a === void 0 ? void 0 : _a.closest('li');
        };
        const setTabIndex = (currentNode, newNode) => {
            // Set tabIndex to -1 for the current node and 0 for the new node
            currentNode.setAttribute('tabIndex', '-1');
            newNode.setAttribute('tabIndex', '0');
        };
        const focusNextNode = (currentNode) => {
            const isNodeExpanded = expandedNodes.has(currentNode.getAttribute('id') || '');
            let tmpNode = currentNode;
            if (isNodeExpanded) {
                const firstChild = currentNode.querySelector(':scope > ul > li');
                if (firstChild) {
                    setTabIndex(currentNode, firstChild);
                    firstChild.focus();
                    return;
                }
            }
            let nextNode = currentNode.nextElementSibling || null;
            // If there is no next node, we need to go up the tree
            // and find the next node
            // This is done by checking the parent node and its next sibling
            while (!nextNode) {
                const parentNode = getParentNode(tmpNode);
                if (parentNode) {
                    // we have a loop here
                    nextNode = parentNode.nextElementSibling || null;
                    tmpNode = parentNode;
                }
                else {
                    break;
                }
            }
            if (nextNode) {
                // Set tabIndex to -1 for the current node
                // before moving to the next node through the parent
                // This is to ensure that the current node is not focusable
                currentNode.setAttribute('tabIndex', '-1');
                setTabIndex(tmpNode, nextNode);
                nextNode.focus();
            }
        };
        const focusPreviousNode = currentNode => {
            const previousNode = currentNode.previousElementSibling;
            if (previousNode) {
                const isNodeExpanded = expandedNodes.has(previousNode.getAttribute('id'));
                if (isNodeExpanded) {
                    let lastChild = previousNode.querySelector(':scope > ul > li:last-child');
                    while (lastChild && expandedNodes.has(lastChild.getAttribute('id'))) {
                        const nestedLastChild = lastChild.querySelector(':scope > ul > li:last-child');
                        if (nestedLastChild) {
                            lastChild = nestedLastChild;
                        }
                        else {
                            break;
                        }
                    }
                    if (lastChild) {
                        setTabIndex(currentNode, lastChild);
                        lastChild.focus();
                        return;
                    }
                }
                setTabIndex(currentNode, previousNode);
                previousNode.focus();
            }
            else {
                const parentNode = getParentNode(currentNode);
                if (parentNode) {
                    setTabIndex(currentNode, parentNode);
                    parentNode.focus();
                }
            }
        };
        const focusFirstChild = currentNode => {
            const firstChild = currentNode.querySelector('ul > li');
            if (firstChild) {
                setTabIndex(currentNode, firstChild);
                firstChild.focus();
            }
        };
        const focusParentNode = currentNode => {
            const parentNode = getParentNode(currentNode);
            if (parentNode) {
                setTabIndex(currentNode, parentNode);
                parentNode.focus();
            }
        };
        const focusFirstNode = () => {
            var _a;
            const firstNode = (_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('[role="treeitem"]');
            if (firstNode) {
                setTabIndex(e.currentTarget, firstNode);
                firstNode.focus();
            }
        };
        const focusLastNode = () => {
            var _a;
            const treeItems = (_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('[role="treeitem"]');
            if (treeItems && treeItems.length > 0) {
                const lastNode = treeItems[treeItems.length - 1];
                setTabIndex(e.currentTarget, lastNode);
                lastNode.focus();
            }
        };
        if (node.disabled) {
            if (e.key === 'Enter' || e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                return;
            }
        }
        // Home: Fn + Left Arrow
        // End: Fn + Right Arrow
        switch (e.key) {
            case 'Enter':
                handleSelect(e, node.id);
                if (onItemClick) {
                    onItemClick(node);
                }
                break;
            case 'ArrowRight':
                if (node.children && ((_a = node.children) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                    if (!isNodeExpanded) {
                        e.preventDefault();
                        handleToggle(node.id);
                    }
                    else {
                        e.preventDefault();
                        focusFirstChild(e.currentTarget);
                    }
                }
                break;
            case 'ArrowLeft':
                if (node.children && ((_b = node.children) === null || _b === void 0 ? void 0 : _b.length) > 0) {
                    if (isNodeExpanded) {
                        e.preventDefault();
                        handleToggle(node.id);
                    }
                    else {
                        focusParentNode(e.currentTarget);
                    }
                }
                else {
                    focusParentNode(e.currentTarget);
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                focusNextNode(e.currentTarget);
                break;
            case 'ArrowUp':
                e.preventDefault();
                focusPreviousNode(e.currentTarget);
                break;
            case 'Home':
                e.preventDefault();
                focusFirstNode();
                break;
            case 'End':
                e.preventDefault();
                focusLastNode();
                break;
        }
    };
    const renderTree = (nodes, parentId, isMainList = true) => {
        return (React__default.createElement("ul", { className: "product-tree", role: isMainList ? 'tree' : 'group', "aria-label": isMainList ? ariaLabel : undefined, "aria-labelledby": !isMainList ? `${parentId}_label` : ariaLabelledbyID }, nodes === null || nodes === void 0 ? void 0 : nodes.map((node, index) => (React__default.createElement("li", Object.assign({ 
            // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
            role: "treeitem", key: node.id, id: node.id, className: "cb-tree-node", "aria-labelledby": `${node.id}_label`, "aria-current": selectedNodes.has(node.id) ? ariaCurrent : undefined, "aria-disabled": node.disabled || undefined, tabIndex: isMainList && index === 0 ? 0 : -1, onKeyDown: e => handleKeyDown(e, node), onClick: e => handleClick(e, node) }, (node.children &&
            node.children.length > 0 &&
            node.id && {
            'aria-expanded': expandedNodes.has(node.id),
        })),
            React__default.createElement("div", { className: cn('cb-tree-node-container', {
                    'cb-tree-node-parent': node.children,
                    'cb-disabled': node.disabled,
                }) },
                node.children &&
                    node.children.length > 0 &&
                    node.id &&
                    (expandedNodes.has(node.id) ? (React__default.createElement(Icon, { size: iconSize, name: openIcon, decorative: true })) : (React__default.createElement(Icon, { size: iconSize, name: closeIcon, decorative: true }))),
                node.icon && (React__default.createElement(Icon, { className: "cb-tree-node-icon", size: iconSize, name: node.icon, decorative: true })),
                LabelComponent ? (React__default.createElement("div", { id: `${node.id}_label`, className: "cb-tree-node-label" },
                    React__default.createElement(LabelComponent, { node: node, isExpanded: expandedNodes.has(node.id), isSelected: selectedNodes.has(node.id) }))) : (React__default.createElement("span", { id: `${node.id}_label`, className: "cb-tree-node-label" }, node.label))),
            node.children &&
                node.id &&
                expandedNodes.has(node.id) &&
                renderTree(node.children, node.id, false))))));
    };
    const findCategoryWithChildren = (data, targetCategoryId) => {
        let result = null;
        const traverse = categories => {
            return categories.some((category) => {
                if (category.id === targetCategoryId) {
                    result = Object.assign(Object.assign({}, category), { children: [] });
                    return true;
                }
                if (category.children && category.children.length > 0) {
                    const childFound = traverse(category.children);
                    if (childFound && result) {
                        result = Object.assign(Object.assign({}, category), { children: [result] });
                        return true;
                    }
                }
                return false;
            });
        };
        if (data) {
            traverse(data);
        }
        return result ? [result] : [];
    };
    const markSelectedNode = (categories) => {
        categories.forEach(category => {
            // Expand the current node
            setExpandedNodes(prevExpandedNodes => {
                const newExpandedNodes = new Set(prevExpandedNodes);
                if (category.id) {
                    newExpandedNodes.add(category.id);
                }
                return newExpandedNodes;
            });
            if (category.children && category.children.length > 0) {
                markSelectedNode(category.children);
            }
        });
    };
    const updateParentIds = (nodes, parentId = null) => {
        if (nodes) {
            return nodes.map(node => {
                // Update the parentId of the current node
                const updatedNode = Object.assign(Object.assign({}, node), { parentId: parentId || node.parentId || '' });
                // Recursively update the children
                if (updatedNode.children && updatedNode.children.length > 0) {
                    updatedNode.children = updateParentIds(updatedNode.children, updatedNode.id);
                }
                return updatedNode;
            });
        }
        return [];
    };
    const updateNodeIds = (nodes) => {
        return nodes.map(node => {
            // If the node doesn't have an ID, assign a unique ID using useUID
            const updatedNode = Object.assign(Object.assign({}, node), { id: node.id || uid(node) });
            // Recursively update the children
            if (updatedNode.children && updatedNode.children.length > 0) {
                updatedNode.children = updateNodeIds(updatedNode.children);
            }
            return updatedNode;
        });
    };
    useEffect(() => {
        if (items) {
            const updatedItems = updateNodeIds(items);
            setDataArray(updateParentIds(updatedItems));
        }
    }, [items]);
    useEffect(() => {
        if (onSelectedItemsChange) {
            onSelectedItemsChange(Array.from(selectedNodes).filter((item) => typeof item === 'string'));
        }
    }, [selectedNodes]);
    // check if we have pre-selected items
    useEffect(() => {
        if (activeTrail.length > 0) {
            const data = JSON.parse(JSON.stringify(dataArray)); // Deep copy of the data array
            // Iterate over each ID in activeTrail
            if (multiSelect) {
                activeTrail.forEach(nodeId => {
                    const selectedData = findCategoryWithChildren(data, nodeId);
                    // Expand the node and its parents
                    if (selectedData.length > 0) {
                        markSelectedNode(selectedData);
                    }
                    // Select the node
                    handleSelect(null, nodeId, 'activeTrail');
                });
            }
            else {
                // if user passed multiple ID's we take the last one
                const selectedNodeId = activeTrail[activeTrail.length - 1];
                const selectedData = findCategoryWithChildren(data, selectedNodeId);
                // Expand the node and its parents
                if (selectedData.length > 0) {
                    markSelectedNode(selectedData);
                }
                // Select the node
                handleSelect(null, selectedNodeId, 'activeTrail');
            }
        }
    }, [activeTrail, dataArray]);
    useEffect(() => {
        if (reset === true) {
            setExpandedNodes(new Set());
            setSelectedNodes(new Set());
            if (onReset) {
                onReset();
            }
        }
    }, [reset]);
    return (React__default.createElement("div", { className: "cb-tree-view-container" },
        dataArray && (React__default.createElement(React__default.Fragment, null,
            controls && (React__default.createElement("div", { className: "cb-tree-view-controls" },
                title && (React__default.createElement("div", { className: "cb-tree-view-title" },
                    React__default.createElement("h3", null, title))),
                React__default.createElement("div", { className: "cb-tree-view-buttons" },
                    React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-greyscale cb-tree-view-expand", onClick: handleExpandAll }, "Expand All"),
                    React__default.createElement("button", { type: "button", className: "cb-btn cb-btn-greyscale cb-tree-view-collapse", onClick: handleCollapseAll }, "Collapse All")))),
            React__default.createElement("div", { className: "cb-tree-view", ref: treeRef }, renderTree(dataArray)))),
        !dataArray && loader && React__default.createElement(Spinner, Object.assign({}, spinnerProps))));
};

export { ASCENDING_TEXT, AccordionControls, AccordionHeader, AccordionItem, AccordionList, AccordionPanel, AnchorMenu, ArticleBand, AvatarIcon, BackToTop, BandLoader, BareModal, BaseButton, BlackButton, Breadcrumbs, Button, ButtonRow, CTACard, CardBand, CardWrapper, CautionNotification, CenterTextBand, Checkbox, CheckboxGroup, CircularButton, ClearButton, CloseButton, Column, Crumb, DESCENDING_TEXT, DatePicker, Dropdown, DropdownGroup, DropdownItem, EXPANDER_LEFT, EXPANDER_NONE, EXPANDER_RIGHT, ErrorNotification, EventCard, EventTimeline, Expandable, FileUpload, Filter, FilterFooter, FocusedBand, HeroCenterLoader, HeroFocusLoader, HighlightedString, HorizontalCarousel, ICON_LEFT, ICON_NONE, ICON_RIGHT, Icon, IconStep, IdentityCard, IdentityCardHeading, Illustration, InfoNotification, Input, InputDropdown, InputDropdownR, InputNotification, InputPhoneInt, InputStepper, LeftTextBand, LinkTagButton, List, LoadingWrapper, LogoutButton, MediaCard, Modal, ModalDialog, ModalPromo, ModalSpinner, ModalVideo, MultiSelect, NakedButton, Notification, NumericIcon, NumericStep, PageSize, Pager, PaginationInfo, Password, PhotoGallery, Popover, PrimaryButton, PromoCard, RadioButton, RadioButtonGroup, ResourceCard, SORT_DEFAULT, SORT_INLINE, SORT_NONE, SecondaryButton, SectionLoader, Select, SelectDropdown, SelectDropdownGroup, SelectDropdownItem, SimpleAccordion, SimpleAccordionContext, SimpleAccordionHeader, SimpleAccordionIcon, SimpleAccordionItem, SimpleAccordionItemContext, SimpleAccordionPanel, SimpleAccordionTrigger, SimpleCard, SimpleStep, Slider, Spacer, SpacerH, SpacerV, Spinner, SquareButton, Stepper, Sticky, SuccessNotification, Tab, TabListR, TabPanelListR, TabPanelR, TabR, Table, TableHeaderContext, Tabs, TabsR, Tag, TagSet, TextArea, Thumbnail, Toast, Toasts, ToggleButton, ToggleButtonGroup, ToggleSwitch, Tooltip, TreeView, YellowButton, a11yHighlightTypes, ariaLive, attributesFromTupleArray, autocomplete, behaviourTypes, btnType, buttonVariants, captionAlignment, captionFontSize, captionFontWeight, carouselVariants, circularIconSizes, datePickerPalette, detachedComponentsVariants, expanderVariants, fdbkPosition, fileButtonSize, fileButtonVariants, filterPalette, floatingBg, hLevel, horizontalSpacings, iconColors, iconNames, iconSizes, iconType, iconVariants, identityCardPalette, illustrationName, illustrationSize, labelPosition, layout, linkPosition, markerTypes, msLabelPosition, msTagPosition, noticeHLevel, numericIconSizes, offsetType, pipsMode, pipsType, popoverDatePickerPlacement, popoverFilterPlacement, popoverPlacements, skipEnrollOpt, sortVariants, spinnerPalette, spinnerSize, stepperStates, toastType, tooltipPlacements, treeIconSizes, typedTableComponents, useAccordion, useAccordionItem, useLoginStatus, validationState, verticalSpacings };
//# sourceMappingURL=index.js.map
